// VE API wrapper functions
/* global ve Blob location */

import API from './api.js'
import vecfg from './vecfg.js'
import {
  addToLog,
  doLog,
  ensureLongISO,
  getVeEnvLoc,
  getVeEnvLocMsApiUrl,
  getVeEnvLocNames,
  getUserProfile,
  setUserProfile,
  hasProp,
  hasKeys,
  isNumeric,
  isUndefined,
  getDatalakeBucketName,
  getTimezoneFromMicrogridId,
  getIntervalTimeStamp,
  getUnixTimeStamp,
  dateAddShortISO,
  dateAddShortISOLocal,
  getMicrogridTimeStamp,
  shortISOAddSeconds,
  convertJsonToCsv,
  shortISOToUnixTimestamp,
  sleep,
  splitArray,
  useGMC as vebUseGMC,
  getDataSource as vebGetDataSource,
  getLongISOTimeStamp,
  longISOAddSeconds,
  getDateRange,
  getUniqueArray,
  getAccessProfile,
  roundToTwo
} from './veb.js'

const logRouteParams = (fn, route, params, dbg = false) => {
  if (!dbg) return
  doLog(`${fn}:route`, route, 'params', params, true) // dbg)
}

const logRes = (fn, res, dbg = false) => {
  if (!dbg) return
  doLog(`${fn}:res`, res, true) // dbg)
}

const routeVersion = 'v2' // old: v1, new: v2
// routes.<dataClass>.<subClass>
const routes = {
  assets: {
    'devices/provision': { v2: 'devices/provision' },
    'devices/status': { v2: 'devices/status' },
    'devices/deviceserialnumber': { v2: 'devices/deviceserialnumber' },
    'devices/online': { v2: 'devices/online' },
    'devices/reboot-and-ota': { v2: 'devices/reboot-and-ota' },
    'devices/reboot': { v2: 'devices/reboot' },
    'devices/statistics': { v2: 'devices/statistics' },
    'endpoint/aggregateby': { v2: '' },
    'endpoints/devicediscovery': { v2: 'endpoints/devicediscovery' },
    'endpoints/devicediscovery/results': { v2: 'endpoints/devicediscovery/results' },
    'endpoints/inverterdiscovery': { v2: 'endpoints/inverterdiscovery' },
    'endpoints/inverterdiscovery/results': { v2: 'endpoints/inverterdiscovery/results' },
    'endpoints/inverter/nameplate': { v2: 'endpoints/inverter/nameplate' },
    'endpoints/online': { v2: 'endpoints/online' },
    'endpoints/control-logs': { v2: 'endpoints/control-logs' },
    'endpoints/ocpp': { v2: 'endpoints/ocpp' },
    'endpoints/status': { v2: 'endpoints/status' },
    'endpoints/profile': { v2: 'endpoints/profile' },
    'endpoints/config': { v2: 'endpoints/config' },
    'endpoints/contract': { v2: 'endpoints/contract' },
    'endpoints/configcontract': { v2: 'endpoints/configcontract' },
    'security-contexts': { v2: 'context-selector/assets' },
    'service-entity': { v2: 'service-entity' },
    'service-entity/coordinates': { v2: 'service-entity' },
    'service-entity/delegated-access': { v2: 'service-entity/delegated-access' },
    'service-entities': { v2: 'service-entities' },
    'service-entities/access': { v2: 'service-entities/access' },
    'service-entities/delegated-access': { v2: 'service-entities/delegated-access' },
    'service-entities/owner-access': { v2: 'service-entities/owner-access' },
    'supported-devices': { v2: 'supported-devices' },
    devices: { v2: 'devices' },
    endpoints: { v2: 'endpoints' },
    'metrics/list': { v2: 'metrics/list' }
  },
  contracts: {
    'contracts/growth': { v2: 'contracts/growth' },
    'contracts/metrics': { v2: 'contracts/metrics' },
    'definition/defaultappui': { v2: 'definitions' },
    'definition/endpointtype': { v2: 'definitions' },
    'definition/offer': { v2: 'definitions' },
    'definition/status': { v2: 'definitions' },
    'definition/versions': { v2: 'definitions' },
    'definitions/info': { v2: 'definitions/info' },
    'definitions/pricing': { v2: 'definitions/pricing' },
    'definitions/pricing/calculate': { v2: 'definitions/pricing/calculate' },
    'events/metrics': { v2: 'events/metrics' },
    'events/executions': { v2: 'events/executions' },
    'orchestration/events': { v2: 'orchestration/events' },
    'orchestration/events/chart-data': { v2: 'orchestration/events/chart-data' },
    'orchestration/events/progress': { v2: 'orchestration/events/progress' },
    'security-contexts': { v2: 'context-selector/contracts' },
    'transactions/metrics': { v2: 'transactions/metrics' },
    'transactions/chart-data': { v2: 'transactions/chart-data' },
    'transactions/status': { v2: 'transactions/status' },
    aggregateby: { v2: 'aggregateby' },
    'aggregateby/endpoint': { v2: 'aggregateby/endpoint' },
    algorithms: { v2: 'algorithms' },
    changelog: { v2: 'changelog' },
    definition: { v2: '' },
    definitions: { v2: 'definitions' },
    endpointid: { v2: '' },
    events: { v2: 'events' },
    group: { v2: 'group' },
    groups: { v2: 'groups' },
    serviceentityid: { v2: '' },
    status: { v2: '' },
    transactions: { v2: 'transactions' }
  },
  customer: {
    'assets/devices': { v2: 'assets/devices' },
    'assets/endpoints': { v2: 'assets/endpoints' },
    'assets/endpoints/status': { v2: 'assets/endpoints/status' },
    'assets/endpoints/online': { v2: 'assets/endpoints/online' },
    'assets/endpoints/control-logs': { v2: 'assets/endpoints/control-logs' },
    'assets/devices/online': { v2: 'assets/devices/online' },
    'assets/service-entities': { v2: 'assets/service-entities' },
    'contracts/default/offers': { v2: 'contracts/default/offers' },
    'contracts/definitions': { v2: 'contracts/definitions' },
    'contracts/transactions': { v2: 'contracts/transactions' },
    'service-requests': { v2: 'service-requests' },
    'support-requests': { v2: 'support-requests' },
    orders: { v2: 'orders' },
    notifications: { v2: 'notifications' },
    changelog: { v2: 'changelog' },
    contracts: { v2: 'contracts' }
  },
  customers: {
    'security-contexts': { v2: 'context-selector/customers' },
    'support-request': { v2: 'support-requests' },
    ledgeraccounts: { v2: 'ledgeraccounts' },
    nameids: { v1: 'nameids', v2: '' },
    'app-info': { v2: 'app-info' },
    notifications: { v2: 'notifications' },
    token: { v2: 'token' },
    password: { v2: 'password' }
  },
  events: {
    'security-contexts': { v2: 'context-selector/events' },
    aggregateby: { v2: 'aggregateby' },
    'aggregateby/endpoint': { v2: 'aggregateby/endpoint' },
    definitions: { v2: 'definitions' },
    metric: { v2: 'metric' },
    metrics: { v2: 'metrics' },
    events: { v2: 'events' },
    actions: { v2: 'actions' },
    status: { v2: '' }
  },
  financials: {
    'security-contexts': { v2: 'context-selector/financials' }
  },
  telemetry: {
    'aggregate-by-id/chart-data': { v2: 'aggregate-by-id/chart-data' },
    'aggregate-by-ids/energy-data': { v2: 'aggregate-by-ids/energy-data' },
    'endpoints/energy-data': { v2: 'endpoints/energy-data' },
    'endpoints/chart-data': { v2: 'endpoints/chart-data' },
    'endpoint/source-msg': { v2: 'endpoint/source-msg' },
    'endpoints/source-msg': { v2: 'endpoints/source-msg' },
    'endpoints/source-msg/chart-data': {
      v2: 'endpoints/source-msg/chart-data'
    },
    'legacy/energy-data': { v2: 'legacy/energy-data' },
    'orchestration/events/aggregate-by-id/chart-data': {
      v2: 'orchestration/events/aggregate-by-id/chart-data'
    },
    'orchestration/events/aggregate-by-ids/chart-data': {
      v2: 'orchestration/events/aggregate-by-ids/chart-data'
    },
    'service-entity/chart-data': { v2: 'service-entity/chart-data' },
    'service-entity/flow-data': { v2: 'service-entity/flow-data' },
    legacy: { v2: 'legacy' },
    vecache: { v2: 'vecache' }
  },
  user: {
    'user-accessprofile': { v2: 'access-profile' },
    'user-group-is-member': { v2: 'group-is-member' },
    'user-groups-for-type': { v2: 'groups-for-type' },
    'user-super-admin': { v2: 'user-super-admin' },
    'user-updateprovidername': { v2: 'provider-name/update' },
    'user-updateuserprofile': { v2: 'user-profile/update' },
    'user-updateuserprofilesettings': { v2: 'profile-settings/update' },
    'user-userprofile': { v2: 'user-profile' }
  },
  users: {
    'fba/owner-context': { v2: 'fba/owner-context' },
    'group/users': { v2: 'group/users' },
    'roles/functions': { v2: 'roles/functions' },
    'roles/owner': { v2: 'roles/owner' },
    'roles/superadmin': { v2: 'roles/superadmin' },
    'roles/useradmin': { v2: 'roles/useradmin' },
    'security-contexts': { v2: 'context-selector/users' },
    'api-key': { v2: 'api-key' },
    password: { v2: 'password' },
    changelog: { v2: 'changelog' },
    group: { v2: 'group' },
    groups: { v2: 'groups' },
    notifications: { v2: 'notifications' },
    user: { v2: 'user' }
  },
  configs: {
    data: { v2: 'data' },
    setup: { v2: 'setup' },
    image: { v2: 'image' },
    'setup/dynamic': { v2: 'setup/dynamic' }
  },
  config: {
    'google-maps-api-key': { v2: 'google-maps-api-key' }
  }
}

const getRoute = (dataClass, subClass, action, dbg = false) => {
  const fn = 'getRoute'
  // todo: handle customer auth
  dbg = false
  // module: same as dataClass except for customer & user ???
  // dataClass: contracts, assets, customers (customer), users (user), pricing, events, hma,...
  // subClass: definitions, status, device, endPoint, endPointType, algorithm(s) or blank
  // action: read, add, update, delete, invoke
  doLog(fn, { dataClass, subClass, action }, dbg)
  const dc = dataClass
  let sc = subClass
  if (sc === 'security-contexts') {
    return `${routes[dc][sc][routeVersion]}`
  }
  if (dc === 'user') {
    return `${dc}/${routes[dc][sc][routeVersion]}`
  } else if (dc === 'config') {
    return `${dc}/${routes[dc][sc][routeVersion]}`
  } else if (dc === 'customer') {
    return `${dc}/${routes[dc][sc][routeVersion]}/${action}`
  }
  const routeParts = []
  routeParts.push(`${dc}`)
  if (
    action === 'update' &&
    !sc.startsWith('definition/') &&
    !sc.startsWith('aggregateby/') &&
    !sc.startsWith('transactions/')
  ) {
    if (sc.startsWith('service-entity')) sc = 'service-entity'
    if (sc.includes('/') && !sc.startsWith('endpoints')) sc = ''
  }
  doLog(fn, { sc }, dbg)
  if (sc) {
    doLog(fn, { dc, sc, routeVersion }, dbg)
    const scRoutePart = `${routes[dc][sc][routeVersion]}`
    doLog(fn, { scRoutePart }, dbg)
    if (scRoutePart) routeParts.push(scRoutePart)
  }
  if (action) routeParts.push(`${action}`)
  // add module at start
  let module = dc
  doLog(fn, { module }, dbg)
  if (dc === 'contracts' &&
    [
      'algorithms',
      'definition',
      'definitions',
      'transactions',
      'transactions/metrics',
      'transactions/chart-data',
      'transactions/status',
      'events',
      'executions',
      'events/executions',
      'groups',
      'definition/status',
      'definition/defaultappui',
      'definition/endpointtype',
      'definition/versions',
      'definition/offer',
      'definitions/pricing',
      'definitions/info',
      'definitions/pricing/calculate',
      'orchestration/events',
      'orchestration/events/progress',
      'orchestration/events/chart-data',
      'orchestration/events/aggregate-by-id/chart-data',
      'orchestration/events/aggregate-by-ids/chart-data'
    ].includes(sc)
  ) {
    module = null
  }
  if (dc === 'assets' && ['service-entities', 'service-entity'].includes(sc)) {
    module = null
  } else if (dc === 'assets') module = null
  else if (dc === 'pricing') module = null
  else if (dc === 'customers') module = null
  else if (dc === 'users') module = null
  else if (dc === 'telemetry') module = null
  else if (dc === 'configs') module = null
  else if (dc === 'events') module = null
  else if (dc === 'hma') module = null
  if (module) routeParts.unshift(module)
  doLog(fn, { routeParts }, dbg)
  const route = routeParts.join('/')
  doLog(fn, { route }, dbg)
  return route
}

const addContractDefinition = async (
  contractDefId,
  name,
  version,
  status,
  description = '',
  sc,
  globalParameters,
  contractCriteria,
  contractParameters,
  actions,
  onSuccess,
  onFailure,
  trigger,
  counterPartyId,
  groupIds,
  defaultOffer,
  offerDetails,
  termsAndConditions,
  customFields,
  uiConfig,
  dbg = false
) => {
  dbg = true
  const fn = 'addContractDefinition'
  doLog(fn, { contractDefId, name, version, status, description, termsAndConditions }, dbg)
  if (typeof globalParameters === 'string') globalParameters = JSON.parse(globalParameters)
  if (typeof contractCriteria === 'string') contractCriteria = JSON.parse(contractCriteria)
  if (typeof contractParameters === 'string') contractParameters = JSON.parse(contractParameters)
  if (typeof actions === 'string') actions = JSON.parse(actions)
  if (typeof onSuccess === 'string') onSuccess = JSON.parse(onSuccess)
  if (typeof onFailure === 'string') onFailure = JSON.parse(onFailure)
  if (typeof trigger === 'string') trigger = JSON.parse(trigger)
  if (typeof customFields === 'string') customFields = JSON.parse(customFields)
  if (typeof defaultOffer === 'string') defaultOffer = JSON.parse(defaultOffer)
  if (typeof offerDetails === 'string') offerDetails = JSON.parse(offerDetails)
  if (!termsAndConditions) termsAndConditions = ''
  if (typeof termsAndConditions !== 'string') termsAndConditions = termsAndConditions.join('\n') || ''
  if (typeof status === 'undefined') status = 'draft'
  if (!description) description = ''
  if (typeof description !== 'string') description = description.join('\n') || ''
  if (!sc.endsWith(contractDefId)) sc += '/' + contractDefId
  const createdBy = await ve.amplify.getCognitoEmail(dbg) // todo: review, set in ve-sat-api?
  const createdByRole = 'n/a' // todo: add role?
  doLog(fn, { sc }, true)
  const params = {
    contractDefId,
    name,
    version,
    status,
    description,
    sc,
    globalParameters,
    contractCriteria,
    contractParameters,
    actions,
    onSuccess,
    onFailure,
    trigger,
    counterPartyId,
    groupIds,
    defaultOffer,
    offerDetails,
    termsAndConditions,
    customFields,
    createdBy,
    createdByRole,
    uiConfig
  }
  const route = getRoute('contracts', 'definitions', 'add', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteContractDefinition = async (sc, id, dbg = true) => {
  const fn = 'deleteContractDefinition'
  const params = { sc, id }
  const route = getRoute('contracts', 'definitions', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteEventDefinition = async (sc, dbg = true) => {
  const fn = 'deleteEventDefinition'
  const params = { sc }
  const route = getRoute('events', 'definitions', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteEvent = async (sc, dbg = true) => {
  const fn = 'deleteEvent'
  const params = { sc }
  const route = getRoute('events', 'events', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteEventAction = async (sc, eventActionId, dbg = true) => {
  const fn = 'deleteEventAction'
  const route = getRoute('events', 'actions', 'delete', dbg)
  const params = { sc, eventActionId }
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, JSON.stringify(params), dbg)
  logRes(fn, res, dbg)
  return res
}

const updateContractDefinitionVeAuth = async (
  id,
  contractDefId,
  name,
  version,
  status,
  description,
  securityContext,
  globalParameters,
  contractCriteria,
  contractParameters,
  actions,
  defaultAppUI,
  endPointType,
  onSuccess,
  onFailure,
  trigger,
  counterPartyId,
  groupIds,
  defaultOffer,
  offerDetails,
  presentAsDefaultOffer,
  defaultOfferCriteria,
  termsAndConditions,
  defaultAggregation,
  customFields = {},
  uiConfig = {},
  debug = false
) => {
  let logMsg = [
    'id',
    'contractDefId',
    'securityContext',
    'name',
    'version'
  ].join(':')
  logMsg += [id, contractDefId, securityContext, name, version].join(':')
  addToLog(`updateContractDefinitionVeAuth:${logMsg}`, debug)
  if (securityContext.endsWith('/')) {
    securityContext = securityContext.slice(0, -1)
  }
  if (!securityContext.endsWith(contractDefId)) {
    securityContext += `/${contractDefId}`
  }
  addToLog(
    `updateContractDefinitionVeAuth:defaultAggregation: ${defaultAggregation}`,
    true
  )
  if (typeof globalParameters === 'string') {
    globalParameters = JSON.parse(globalParameters)
  }
  if (typeof contractCriteria === 'string') {
    contractCriteria = JSON.parse(contractCriteria)
  }
  if (typeof contractParameters === 'string') {
    contractParameters = JSON.parse(contractParameters)
  }
  if (typeof actions === 'string') actions = JSON.parse(actions)
  if (typeof onSuccess === 'string') onSuccess = JSON.parse(onSuccess)
  if (typeof onFailure === 'string') onFailure = JSON.parse(onFailure)
  if (typeof trigger === 'string') trigger = JSON.parse(trigger)
  if (typeof customFields === 'string') customFields = JSON.parse(customFields)
  if (typeof defaultOffer === 'string') defaultOffer = JSON.parse(defaultOffer)
  if (typeof offerDetails === 'string') offerDetails = JSON.parse(offerDetails)
  if (typeof termsAndConditions === 'undefined') termsAndConditions = ''
  if (termsAndConditions && typeof termsAndConditions !== 'string') {
    termsAndConditions = termsAndConditions.join('\n')
  }
  if (typeof status === 'undefined') status = 'draft'
  if (typeof description === 'undefined') description = ''
  if (typeof defaultAppUI === 'undefined') defaultAppUI = 'none'
  if (description && typeof description !== 'string') {
    description = description.join('\n')
  }
  addToLog(
    `updateContractDefinitionVeAuth:securityContext: ${securityContext}`,
    true
  )
  const params = {
    contractDefId,
    name,
    version,
    status,
    description,
    securityContext,
    globalParameters,
    contractCriteria,
    contractParameters,
    actions,
    defaultAppUI,
    onSuccess,
    onFailure,
    trigger,
    customFields,
    counterPartyId,
    groupIds,
    defaultAggregation,
    termsAndConditions,
    defaultOffer,
    offerDetails,
    presentAsDefaultOffer,
    defaultOfferCriteria,
    endPointType
  }
  if (uiConfig) params.uiConfig = uiConfig
  const route = getRoute('contracts', 'definitions', 'update', debug) // 'contracts/definitions/update'
  addToLog(
    `updateContractDefinitionVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.put2(
    route,
    { securityContext, id },
    JSON.stringify(params),
    debug
  )
  addToLog(
    `updateContractDefinitionVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const updateContractDefinitionParams = async (sc, id, params, dbg = false) => {
  const fn = 'updateContractDefinitionParams'
  if (sc.endsWith('/')) sc = sc.slice(0, -1)
  const { contractDefId: cDefId } = params
  if (cDefId && !sc.endsWith(cDefId)) sc += `/${cDefId}`
  params = sanitiseContractDefinitionParams(params, dbg)
  const route = getRoute('contracts', 'definitions', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc, id }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const sanitiseContractDefinitionParams = (params, debug = false) => {
  const fn = 'sanitiseContractDefinitionParams'
  doLog(fn, { params }, debug)
  let validKeys = 'name,version,status,description,counterPartyId,groupIds,'
  validKeys += 'globalParameters,contractCriteria,contractParameters,actions,'
  validKeys += 'customFields,onSuccess,onFailure,trigger,termsAndConditions,'
  validKeys += 'defaultAppUI,contractOnboardingUI,uiConfig,endPointType,'
  validKeys += 'contractEventExpiry,defaultOffer,offerDetails,'
  validKeys += 'presentAsDefaultOffer,defaultOfferCriteria,defaultAggregation'
  validKeys = validKeys.split(',')
  const sanitisedParams = {}
  for (const key of validKeys) {
    if (!hasProp(params, key)) continue
    switch (key) {
      case 'globalParameters':
      case 'contractCriteria':
      case 'contractParameters':
      case 'actions':
      case 'onSuccess':
      case 'onFailure':
      case 'trigger':
      case 'defaultOffer':
      case 'offerDetails':
      case 'customFields':
        sanitisedParams[key] = params[key] && typeof params[key] === 'string' ? JSON.parse(params[key]) : params[key]
        break
      case 'termsAndConditions':
      case 'description':
        sanitisedParams[key] = params[key] && typeof params[key] !== 'string' ? params[key].join('\n') : params[key]
        break
      default:
        sanitisedParams[key] = params[key]
    }
  }
  addToLog(`${fn}:sanitisedParams: ${JSON.stringify(sanitisedParams)}`, debug)
  return sanitisedParams
}

const getAllDropdownSecurityContexts = async (dataClass, dbg = false) => {
  const fn = 'getAllDropdownSecurityContexts'
  const route = getRoute(dataClass, 'security-contexts', '', dbg)
  const params = {}
  if (['assets', 'contracts'].includes(dataClass)) params.accessSecurityContexts = getAccessSecurityContexts(dataClass, dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, {}, dbg)
  logRes(fn, res, dbg)
  return res
}

const getSecurityContexts = async (dataClass, returnData = 'hierarchy', dbg = false) => {
  const fn = 'getSecurityContexts'
  const params = { returnData }
  if (['assets', 'contracts'].includes(dataClass)) params.accessSecurityContexts = getAccessSecurityContexts(dataClass, dbg)
  const route = getRoute(dataClass, 'security-contexts', '', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getAccessSecurityContexts = (dataClass, dbg = false) => {
  const fn = 'getAccessSecurityContexts'
  const allAccessScs = getRolesContextSecurityContextsFromAccessProfile()
  const accessSecurityContexts = allAccessScs.filter(sc => sc.includes(`/${dataClass.toUpperCase()}`)) // ['VE/CONTRACTS', 'Voltello/CONTRACTS/NTPWC',... ]
  doLog(fn, { accessSecurityContexts }, dbg)
  return accessSecurityContexts
}

const getRolesContextSecurityContextsFromAccessProfile = (dbg = false) => {
  const fn = 'getRolesContextSecurityContextsFromAccessProfile'
  const accessProfile = getAccessProfile(dbg)
  const rolesContextSecurityContexts = accessProfile.rolesContextSecurityContexts
  doLog(fn, { rolesContextSecurityContexts }, dbg)
  return rolesContextSecurityContexts
}

const getAllContractDefinitions = async (
  sc,
  limit,
  offset,
  status,
  search,
  projection,
  contractDefId,
  wizardSupport = false,
  debug = false
) => {
  const fn = 'getAllContractDefinitions'
  const params = {
    sc,
    limit,
    offset,
    search,
    projection,
    contractDefId,
    wizardSupport
  }
  if (status) params.status = status
  const route = getRoute('contracts', 'definitions', 'read', debug)
  logRouteParams(fn, route, params, debug)
  return await API.get2(route, params, debug)
}

const getCustomerContractDef = async (sc, contractId, status, dbg = false) => {
  const fn = 'getCustomerContractDef'
  const params = { sc, contractId, status }
  const route = getRoute('customer', 'contracts/definitions', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  return await API.get2(route, params, dbg)
}

const getSpeedoMeterData = async (debug = false) => {
  const fn = 'getSpeedoMeterData'
  const route = 'speedometer'
  logRouteParams(fn, route, {}, debug)
  const res = await API.get3(route, {}, debug)
  logRes(fn, res, debug)
  return res
}

const getDynamicConfigDataVersion = async (
  system,
  type,
  action,
  language = 'en',
  ownerContext,
  debug = false
) => {
  const fn = 'getDynamicConfigDataVersion'
  const params = { system, type, action, language, ownerContext, debug }
  const route = 'dynamic-config'
  logRouteParams(fn, route, params, debug)
  const res = await API.get3(route, params, debug)
  logRes(fn, res, debug)
  return res
}

const getDynamicConfigImagePath = async (
  imgRef,
  language = 'en',
  ownerContext,
  debug = false
) => {
  const fn = 'getDynamicConfigImagePath'
  const params = { imgRef, language, ownerContext, debug }
  const route = 'dynamic-config/image-path'
  logRouteParams(fn, route, params, debug)
  const res = await API.get3(route, params, debug)
  logRes(fn, res, debug)
  return res
}

const getDynamicConfigImageUrl = async (
  imgRef,
  language = 'en',
  ownerContext,
  debug = false
) => {
  const fn = 'getDynamicConfigImageUrl'
  const params = { imgRef, language, ownerContext, debug }
  const route = 'dynamic-config/image-url'
  logRouteParams(fn, route, params, debug)
  const res = await API.get3(route, params, debug)
  logRes(fn, res, debug)
  return res
}

const getDynamicConfigImage = async (
  imgRef,
  language = 'en',
  ownerContext,
  debug = false
) => {
  const fn = 'getDynamicConfigImage'
  const params = { imgRef, language, ownerContext, debug }
  const route = 'dynamic-config/image'
  logRouteParams(fn, route, params, debug)
  const res = await API.get3(route, params, debug)
  logRes(fn, res, debug)
  return res
}

const getContractDefsForOrchestrationVeAuth = async (
  securityContext,
  limit,
  offset,
  search,
  debug = false
) => {
  const params = { securityContext, limit, offset, search, debug }
  addToLog(
    `getContractDefsForOrchestrationVeAuth:securityContext: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const status = null
  const projection = null // todo: adjust to minimum
  const contractDefId = null
  const wizardSupport = null
  const response = await getAllContractDefinitions(
    securityContext,
    limit,
    offset,
    status,
    search,
    projection,
    contractDefId,
    wizardSupport,
    debug
  )
  let contractDefs = !Array.isArray(response)
    ? response.contract_definition
    : []
  contractDefs.forEach((cd) => {
    addToLog(
      `getContractDefsForOrchestrationVeAuth:defaultAggregation: ${JSON.stringify(
        cd.defaultAggregation
      )}`,
      debug
    )
    delete cd.defaultOffer
    delete cd.offerDetails
  })
  contractDefs = contractDefs.filter(
    (cd) => cd.defaultAggregation && cd.defaultAggregation.length > 0
  )
  addToLog(
    `getContractDefsForOrchestrationVeAuth:contractDefs: ${JSON.stringify(
      contractDefs
    )}`,
    debug
  )
  return contractDefs
}

const getContractDefsWithGroupsForOrchestrationVeAuth = async (
  securityContext,
  limit,
  offset,
  search,
  debug = false
) => {
  const status = null
  const projection = null // todo: adjust to minimum
  const contractDefId = null
  const wizardSupport = null
  const contractDefs = await getAllContractDefinitions(
    securityContext,
    limit,
    offset,
    status,
    search,
    projection,
    contractDefId,
    wizardSupport,
    debug
  )
  contractDefs.contract_definition.forEach((cd) => {
    addToLog(
      `getContractDefsWithGroupsForOrchestrationVeAuth:groupIds: ${JSON.stringify(
        cd.groupIds
      )}`,
      debug
    )
    delete cd.defaultOffer
    delete cd.offerDetails
  })
  contractDefs.contract_definition = contractDefs.contract_definition.filter(
    (cd) => cd.groupIds && cd.groupIds.length > 0
  )
  addToLog(
    `getContractDefsWithGroupsForOrchestrationVeAuth:contractDefs: ${JSON.stringify(
      contractDefs
    )}`,
    debug
  )
  return contractDefs.contract_definition
}

const getContractDefinitionVersions = async (sc, version, dbg = false) => {
  const fn = 'getContractDefinitionVersions'
  const params = { sc, version }
  const route = getRoute('contracts', 'definition/versions', 'read', dbg) // 'contracts/definition/versions/read'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  // { contract_definition: [], contract_definition_aggregate: x }
  return res
}

const addContractAlgorithmVeAuth = async (
  securityContext,
  contractAlgorithm,
  debug = false
) => {
  securityContext = 'VE/CONTRACTS'
  const params = {
    name: contractAlgorithm.algorithm,
    parameters: contractAlgorithm,
    status: contractAlgorithm.status,
    scope: contractAlgorithm.scope
  }
  const route = getRoute('contracts', 'algorithms', 'add', debug) // 'contracts/algorithm/add'
  addToLog(
    `addContractAlgorithmVeAuth:securityContext: ${JSON.stringify(
      securityContext
    )}, params: ${JSON.stringify(params)}`,
    debug
  )
  const response = await API.post2(
    route,
    { securityContext },
    JSON.stringify(params),
    debug
  )
  addToLog(
    `addContractAlgorithmVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const updateContractAlgorithmVeAuth = async (
  securityContext,
  contractAlgorithm,
  debug = false
) => {
  const params = {
    name: contractAlgorithm.algorithm,
    parameters: contractAlgorithm,
    status: contractAlgorithm.status,
    scope: contractAlgorithm.scope
  }
  addToLog(
    `updateContractAlgorithmVeAuth:securityContext: ${JSON.stringify(
      securityContext
    )}, params: ${JSON.stringify(params)}`,
    debug
  )
  const route = getRoute('contracts', 'algorithms', 'update', debug) // 'contracts/algorithm/update'
  const response = await API.post2(
    route,
    { securityContext },
    JSON.stringify(params),
    debug
  )
  addToLog(
    `updateContractAlgorithmVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const deleteContractAlgorithm = async (sc, name, dbg = false) => {
  const fn = 'deleteContractAlgorigthm'
  const params = { name }
  const route = getRoute('contracts', 'algorithms', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getAllContractAlgorithms = async (securityContext, scope = ['all'], dbg = false) => {
  const fn = 'getAllContractAlgorithms'
  if (!Array.isArray(scope)) scope = [scope]
  const params = { securityContext, scope }
  const route = getRoute('contracts', 'algorithms', 'read', dbg) // 'contracts/algorithms/read'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  // { contract_algorithm: [], contract_algorithm_aggregate: x }
  let contractAlgorithms = []
  if (res?.contract_algorithm) {
    contractAlgorithms = res.contract_algorithm.map((ca) => {
      const { id, securityContext } = ca
      const { name: algorithm, status, scope, ...rest } = ca.parameters
      const algo = { id, securityContext, algorithm, status, scope, ...rest }
      /*
      const algoOLD = {
        algorithm: ca.name,
        objectIdentifier: ca.parameters.objectIdentifier,
        executeIfTrue: ca.parameters.executeIfTrue,
        parameters: ca.parameters.parameters,
        status: ca.status,
        scope: ca.scope
      }
      */
      return algo
    })
  }
  logRes(fn, contractAlgorithms, dbg)
  const result = { contract_algorithm: contractAlgorithms, contract_algorithm_aggregate: contractAlgorithms.length }
  logRes(fn, result, dbg)
  return result
}

const getSupportedDevicesForEndPointType = async (sc, endPointType, dbg) => {
  const fn = 'getSupportedDevicesForEndPointType'
  if (!sc) sc = 'VE/ASSETS'
  const params = { sc, endPointType } // todo: review, implement common security context
  const route = getRoute('assets', 'supported-devices', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res.supported_brands || res
}

const addSupportedDevice = async (
  sc,
  endPointType,
  brand,
  models,
  svg,
  dbg = false
) => {
  const fn = 'addSupportedDevice'
  doLog(fn, { securityContext: sc }, dbg)
  const params = { endpointType: endPointType, brand, models, svg }
  const route = getRoute('assets', 'supported-devices', 'add', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateSupportedDevice = async (
  sc,
  endPointType,
  brand,
  models,
  svg,
  dbg = false
) => {
  const fn = 'updateSupportedDevice'
  // securityContext ends with id
  const params = { endpointType: endPointType, brand, models, svg }
  const route = getRoute('assets', 'supported-devices', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteSupportedDevice = async (sc, id, dbg = false) => {
  const fn = 'deleteSupportedDevice'
  const params = { id }
  const route = getRoute('assets', 'supported-devices', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const addContractGroupVeAuth = async (securityContext, name, debug = false) => {
  const params = { name }
  addToLog(
    `addContractGroupVeAuth:securityContext: ${JSON.stringify(
      securityContext
    )}, params: ${JSON.stringify(params)}`,
    debug
  )
  const route = getRoute('contracts', 'group', 'add', debug) // 'contracts/group/add'
  const response = await API.post2(
    route,
    { securityContext },
    JSON.stringify(params),
    debug
  )
  addToLog(
    `addContractGroupVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const updateContractGroupVeAuth = async (
  securityContext,
  name,
  debug = false
) => {
  const params = { name }
  addToLog(
    `updateContractGroupVeAuth:securityContext: ${JSON.stringify(
      securityContext
    )}, params: ${JSON.stringify(params)}`,
    debug
  )
  const route = getRoute('contracts', 'group', 'update', debug) // 'contracts/group/update'
  const response = await API.post2(
    route,
    { securityContext },
    JSON.stringify(params),
    debug
  )
  addToLog(
    `updateContractGroupVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const getAllContractGroupsVeAuth = async (
  securityContext,
  limit,
  offset,
  search,
  debug = false
) => {
  addToLog(
    `getAllContractGroupsVeAuth:securityContext: ${securityContext}, limit: ${limit}, offset: ${offset}`,
    debug
  )
  const params = { securityContext, limit, offset, search }
  const route = getRoute('contracts', 'groups', 'read', debug) // 'contracts/groups/read'
  addToLog(
    `getAllContractGroupsVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const result = await API.get2(route, params, debug)
  addToLog(
    `getAllContractGroupsVeAuth:result: ${JSON.stringify(result)}`,
    debug
  )
  return result
}

const addContractOfferForCustomer = async (sc, params, dbg = false) => {
  const fn = 'addContractOfferForCustomer'
  const route = getRoute('customer', 'contracts', 'add', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateContract = async (sc, params, dbg = false) => {
  const fn = 'updateContract'
  const ress = []
  const route = getRoute('contracts', '', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  let result = await API.put2(route, { sc }, params, dbg)
  logRes(fn, result, dbg)
  ress.push(result)
  const { endPointId, status, aggregateBy } = params
  if (endPointId && status && aggregateBy) {
    result = await updateContractEndPointAggregateBy(sc, endPointId, status, aggregateBy, dbg)
    ress.push(result)
  }
  logRes(fn, ress, dbg)
  return ress
}

const updateContractServiceEntityId = async (sc, serviceEntityId, dbg = false) => {
  const fn = 'updateContractServiceEntityId'
  const params = { serviceEntityId }
  const route = getRoute('contracts', 'serviceentityid', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateContractEndPointIdVeAuth = async (
  securityContext,
  endPointId,
  debug = false
) => {
  const params = { endPointId }
  const route = getRoute('contracts', 'endpointid', 'update', debug) // 'contracts/endpointid/update'
  addToLog(
    `updateContractEndPointIdVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.put2(
    route,
    { securityContext },
    JSON.stringify(params),
    debug
  )
  addToLog(
    `updateContractEndPointIdVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const updateContractStatus = async (sc, status, dbg = false) => {
  const fn = 'updateContractStatus'
  const params = { status }
  const route = getRoute('contracts', 'status', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateEventStatus = async (sc, status, dbg = false) => {
  const fn = 'updateEventStatus'
  const params = { status }
  const route = getRoute('events', 'events', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateEventActionStatus = async (sc, eventActionId, status, dbg = false) => {
  const fn = 'updateEventActionStatus'
  const route = getRoute('events', 'actions', 'update', dbg)
  const params = { status, eventActionId }
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc, eventActionId }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateEventEndPointAggregateBy = async (
  sc,
  endPointId,
  status,
  aggregateBy,
  dbg = false
) => {
  const fn = 'updateEventEndPointAggregateBy'
  if (!endPointId) return
  const params = { endPointId, status, aggregateBy }
  const route = getRoute('events', 'aggregateby/endpoint', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateContractEndPointAggregateBy = async (
  sc,
  endPointId,
  status,
  aggregateBy,
  dbg = false
) => {
  const fn = 'updateContractEndPointAggregateBy'
  if (!endPointId) return
  const params = { endPointId, status, aggregateBy }
  const route = getRoute('contracts', 'aggregateby/endpoint', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateContractAggregateByVeAuth = async (
  securityContext,
  aggregateBy,
  debug = false
) => {
  // todo: check if needed
  const params = { aggregateBy }
  const route = getRoute('contracts', 'aggregateby', 'update', debug) // 'contracts/aggregateby/update'
  addToLog(
    `updateContractAggregateByVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.put2(
    route,
    { securityContext },
    JSON.stringify(params),
    debug
  )
  addToLog(
    `updateContractAggregateByVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const deleteContract = async (sc, dbg = true) => {
  const fn = 'deleteContract'
  const params = { sc }
  const route = getRoute('contracts', '', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, params)
  logRes(fn, res, dbg)
  return res
}

const deleteContractTransaction = async (sc, dbg = true) => {
  const fn = 'deleteContractTransaction'
  const params = { sc }
  const route = getRoute('contracts', 'transactions', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, params)
  logRes(fn, res, dbg)
  return res
}

const updateContractTransactionStatus = async (sc, newStatus = 'failure', dbg = true) => {
  const fn = 'updateContractTransactionStatus'
  const params = { newStatus }
  const route = getRoute('contracts', 'transactions/status', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteContractEvent = async (sc, deltaInMilliseconds = 500, dbg = true) => {
  const fn = 'deleteContractEvent'
  const params = { sc }
  doLog(fn, { sc }, dbg)
  let res
  try {
    const resDelCe = await deleteContractExecutions(sc, dbg)
    doLog(fn, { resDelCe }, dbg)
  } catch (e) {
    doLog(fn, { error: e }, true)
  }
  await sleep(deltaInMilliseconds)
  const route = getRoute('contracts', 'events', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  try {
    res = await API.delete2(route, params)
    logRes(fn, res, dbg)
  } catch (e) {
    doLog(fn, { error: e }, true)
  }
  return res
}

const deleteContractEvents = async (sc, from, to, limit = 10, deltaInMilliseconds = 500, dbg = false) => {
  const fn = 'deleteContractEvents'
  doLog(fn, { sc, from, to, limit }, dbg)
  from = `${ensureLongISO(from).slice(0, 10)}T00:00:00+00:00`
  to = `${ensureLongISO(to).slice(0, 10)}T23:59:59+00:00`
  const offset = 0
  const search = ''
  const projection = 'securityContext'
  const vecacheProjection = null
  const status = null
  const incRec = true
  const incAgg = false
  const cDefId = null
  const ceId = null
  const oeId = null
  const uId = null
  const res = await getContractEvents(sc, from, to, limit, offset, search, projection, vecacheProjection, status, incRec, incAgg, cDefId, ceId, oeId, uId, dbg)
  const contractEvents = res.contract_event
  if (limit < 3) doLog(fn, { contractEvents }, dbg)
  const ceScs = contractEvents.map(ce => ce.securityContext)
  const ceCount = ceScs.length
  doLog(fn, { ceCount }, true)
  doLog(fn, { ceScs }, dbg)
  let counter = 0
  for await (const ceSc of ceScs) {
    counter++
    doLog(fn, { counter, ceSc }, true)
    try {
      const delRes = await deleteContractEvent(ceSc, deltaInMilliseconds, dbg)
      logRes(fn, delRes, dbg)
    } catch (e) {
      doLog(fn, { counter, ceCount, error: e }, true)
    }
    await sleep(deltaInMilliseconds)
  }
  logRes(fn, { counter, ceCount, info: 'done deleting' }, dbg)
}

const deleteContractExecutions = async (sc, dbg = true) => {
  const fn = 'deleteContractExecutions'
  const params = { sc }
  const route = getRoute('contracts', 'events/executions', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, params)
  logRes(fn, res, dbg)
  return res
}

const getContractsForStatus = async (sc, status, limit, fromDate, toDate, offset, search, contractDefId, contractId, customerId, endPointId, serviceEntityId, dbg = false) => {
  const fn = 'getContractsForStatus'
  if (typeof status === 'undefined' || status.length === 0) status = ['draft', 'offer', 'active', 'suspended', 'rejected', 'expired']
  if (!Array.isArray(status)) status = [status]
  doLog(fn, { status }, dbg)
  const params = { sc, status, limit, fromDate, toDate, offset, search, dbg }
  if (contractDefId) params.contractDefId = contractDefId
  if (contractId) params.contractId = contractId
  if (customerId) params.customerId = customerId
  if (endPointId) params.endPointId = endPointId
  if (serviceEntityId) params.serviceEntityId = serviceEntityId
  const route = getRoute('contracts', '', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getContractsForStatusDate = async (sc, status, fromDate, toDate, limit, offset, search, contractDefId, contractId, customerId, endPointId, serviceEntityId, dbg = false) => {
  const fn = 'getContractsForStatusDate'
  if (!Array.isArray(status)) status = [status]
  const params = { securityContext: sc, status, fromDate, toDate, limit, offset, search, debug: dbg }
  if (contractDefId) params.contractDefId = contractDefId
  if (contractId) params.contractId = contractId
  if (customerId) params.customerId = customerId
  if (endPointId) params.endPointId = endPointId
  if (serviceEntityId) params.serviceEntityId = serviceEntityId
  const route = getRoute('contracts', '', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getContractsForIdStatus = async (sc, status, limit, fromDate, toDate, offset, search, contractDefId, contractId, endPointId, includeDefaultOffers, serviceEntityId, onlyDefaultOffers, dbg = false) => {
  const fn = 'getContractsForIdStatus'
  const params = { sc, status, limit, fromDate, toDate, offset, search }
  if (contractDefId) params.contractDefId = contractDefId
  if (contractId) params.contractId = contractId
  if (endPointId) params.endPointId = endPointId
  if (includeDefaultOffers) params.includeDefaultOffers = includeDefaultOffers
  if (Array.isArray(serviceEntityId)) serviceEntityId = getUniqueArray(serviceEntityId)
  if (serviceEntityId) params.serviceEntityId = serviceEntityId
  if (onlyDefaultOffers) params.showOnlyDefaultOffers = onlyDefaultOffers
  const route = getRoute('customer', 'contracts', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getAllContracts = async (sc, limit, fromDate, toDate, offset, search, contractDefId, contractId, customerId, endPointId, serviceEntityId, dbg = false) => {
  return await getContractsForStatus(sc, undefined, limit, fromDate, toDate, offset, search, contractDefId, contractId, customerId, endPointId, serviceEntityId, dbg)
}

const getContractOffersForCustomer = async (sc, serviceEntityId, contractId, dbg = false) => {
  const fn = 'getContractOffersForCustomer'
  const params = { sc, serviceEntityId, contractId }
  const route = getRoute('customer', 'contracts/offers', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDefaultContractOffersForCustomer = async (sc, serviceEntityId, contractId, dbg = false) => {
  const fn = 'getDefaultContractOffersForCustomer'
  const params = { sc, serviceEntityId, contractId }
  const route = getRoute('customer', 'contracts/default/offers', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getContractEvents = async (sc, createdFrom, createdTo, limit, offset, search, projection, vecacheProjection, status, includeRecords = true,
  includeAggregate = true, contractDefId, contractEventId, orchestrationEventId, userId, dbg = false) => {
  const fn = 'getContractEvents'
  const params = { sc, createdFrom, createdTo, limit, offset, status, projection, vecacheProjection, includeRecords, includeAggregate }
  if (search) params.search = search
  if (contractDefId) params.contractDefId = contractDefId
  if (contractEventId) params.contractEventId = contractEventId
  if (orchestrationEventId) params.orchestrationEventId = orchestrationEventId
  if (userId) params.userId = userId
  const route = getRoute('contracts', 'events', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getRecentlyCompletedContractEvents = async (sc, endedFromDate, endedToDate, limit, offset, search, projection, vecacheProjection, status, dbg = false) => {
  const fn = 'getRecentlyCompletedContractEvents'
  const params = { sc, endedFromDate, endedToDate, limit, offset, status, projection, vecacheProjection }
  if (search) params.search = search
  const route = getRoute('contracts', 'events', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getAllContractTransactions = async (sc, createdFrom, createdTo, limit, offset, search, status, customStatus, projection = null, includeRecords = true, includeAggregate = true, sort, contractId, contractEventId, orchestrationEventId, contractDefId, cTxnId, customerId, serviceEntityId, dbg = false) => {
  const fn = 'getAllContractTransactions'
  const params = { sc, createdFrom, createdTo, limit, offset, projection, includeRecords, includeAggregate }
  if (search) params.search = search
  if (status) params.status = status
  if (customStatus) params.customStatus = customStatus
  if (sort) params.sort = sort
  if (contractId) params.contractId = contractId
  if (contractEventId) params.contractEventId = contractEventId
  if (orchestrationEventId) params.orchestrationEventId = orchestrationEventId
  if (contractDefId) params.contractDefId = contractDefId
  if (cTxnId) params.contractTransactionId = cTxnId
  if (customerId) params.customerId = customerId
  // if (serviceEntityId) params.serviceEntityId = serviceEntityId
  const route = getRoute('contracts', 'transactions', 'read', false)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getRecentContractTransactionsForContractIdVeAuth = async (
  sc,
  contractId,
  limit = 10,
  status,
  debug = false
) => {
  const fn = 'getRecentContractTransactionsForContractIdVeAuth'
  addToLog(
    `${fn}:sc: ${sc}, contractId: ${contractId}, limit: ${limit}, status: ${status}`,
    debug
  )
  const createdTo = getLongISOTimeStamp()
  const createdFrom = longISOAddSeconds(createdTo, -3600 * 24 * 90)
  const offset = null
  const search = ''
  const customStatus = null
  const projection = null
  const includeRecords = true
  const includeAggregate = true
  const sort = null
  const contractEventId = null
  const orchestrationEventId = null
  const contractDefId = null
  const contractTransactionId = null
  const customerId = null
  addToLog(`${fn}:createdFrom: ${createdFrom}, createdTo: ${createdTo}`, debug)
  const res = await getAllContractTransactions(
    sc,
    createdFrom,
    createdTo,
    limit,
    offset,
    search,
    status,
    customStatus,
    projection,
    includeRecords,
    includeAggregate,
    sort,
    contractId,
    contractEventId,
    orchestrationEventId,
    contractDefId,
    contractTransactionId,
    customerId,
    debug
  )
  logRes(fn, res, debug)
  return res.contract_transaction
}

const getContractTransactionsChartData = async (sc, fromDate, toDate, endPointId, interval = 300, tzIdentifier, dbg = false) => {
  if (!sc) return
  const fn = 'getContractTransactionsChartData'
  const params = { sc, fromDate, toDate, endPointId, interval, tzIdentifier }
  const route = getRoute('contracts', 'transactions/chart-data', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  for (const i in res.dataDefinition) {
    const df = res.dataDefinition[i]
    const ds = 'dataset'
    doLog(fn, { dataType: df.dataType }, dbg)
    if (df.dataType === 'number') {
      res[ds][i] = res[ds][i].map((n) => typeof n === 'string' ? parseFloat(n) : n)
    }
  }
  logRes(fn, res, dbg)
  return res
}

const getContractTransactionsChartDataWithParams = async (params, dbg = false) => {
  const fn = 'getContractTransactionsChartDataWithParams'
  // sc, fromDate, toDate, endPointId, interval = 300, tzOffset, tzIdentifier
  doLog(fn, { params }, dbg)
  const { securityContext, sc } = params
  if (!(sc || securityContext)) return
  if (!params?.interval) params.interval = 300
  const route = getRoute('contracts', 'transactions/chart-data', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  for (const i in res.dataDefinition) {
    const df = res.dataDefinition[i]
    const ds = 'dataset'
    doLog(fn, { dataType: df.dataType }, dbg)
    if (df.dataType === 'number') {
      res[ds][i] = res[ds][i].map((n) =>
        typeof n === 'string' ? parseFloat(n) : n
      )
    }
  }
  logRes(fn, res, dbg)
  return res
}

const getContractTransactionsForCustomerIdVeAuth = async (
  sc,
  limit,
  offset,
  search,
  debug = false
) => {
  if (!sc) return
  const params = { sc, limit, offset, search, debug }
  const route = getRoute('customer', 'contracts/transactions', 'read', debug)
  return await API.get2(route, params, debug)
}

const getContractTransactionsForContractEventIdVeAuth = async (
  sc,
  createdFrom,
  createdTo,
  contractEventId,
  maxCount = 500,
  dbg = false
) => {
  const fn = 'getContractTransactionsForContractEventIdVeAuth'
  doLog(`${fn}:sc`, sc, 'contractEventId', contractEventId, dbg)
  if (sc.endsWith(contractEventId)) sc = sc.split('/').slice(0, -1).join('/')
  doLog(`${fn}:sc`, sc, 'contractEventId', contractEventId, dbg)
  const limit = 500
  let offset = 0
  const search = ''
  const status = null
  const customStatus = null
  const projection = null
  const includeRecords = true
  const includeAggregate = true
  const sort = null
  const contractId = null
  const orchestrationEventId = null
  const contractDefId = null
  const contractTransactionId = null
  const customerId = null
  let totalCount
  const txns = []
  let loopCount = 0
  do {
    ++loopCount
    doLog(`${fn}:sc`, sc, 'contractEventId', contractEventId, dbg)
    const res = await getAllContractTransactions(
      sc,
      createdFrom,
      createdTo,
      limit,
      offset,
      search,
      status,
      customStatus,
      projection,
      includeRecords,
      includeAggregate,
      sort,
      contractId,
      contractEventId,
      orchestrationEventId,
      contractDefId,
      contractTransactionId,
      customerId,
      dbg
    ) // res: {"contract_transaction":[{...},...], "contract_transaction_aggregate":{"aggregate":{"count": 3}}}
    offset += res.contract_transaction.length
    txns.push(...res.contract_transaction)
    totalCount = res.contract_transaction_aggregate?.aggregate.count
    doLog(`${fn}:txns.length`, txns.length, 'totalCount', totalCount, dbg)
    if (txns.length >= maxCount || txns.length < limit || loopCount > 2) break
  } while (txns.length !== totalCount)
  doLog(`${fn}:txns.length`, txns.length, 'totalCount', totalCount, dbg)
  return {
    contract_transaction: txns,
    contract_transaction_aggregate: { aggregate: { count: totalCount } }
  }
}

const getAllServiceEntities = async (sc, limit, offset, type, search, customerId, serviceEntityId, attribute, matchExact, dbg = false, activeContracts = null, suspendedContracts = null, contractMatchMode = 'all') => {
  const fn = 'getAllServiceEntities'
  if (!sc) return
  const params = { sc, limit, offset, search, matchExact }
  if (customerId) params.customerId = customerId
  if (serviceEntityId) params.serviceEntityId = serviceEntityId
  if (attribute) params.attribute = attribute
  if (type) params.type = type
  if (activeContracts) params.activeContracts = activeContracts
  if (suspendedContracts) params.suspendedContracts = suspendedContracts
  if (contractMatchMode) params.contractMatchMode = contractMatchMode
  const route = getRoute('assets', 'service-entities', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  try {
    const accessRes = await getAllServiceEntitiesAccessList(sc, limit, offset, null, search, dbg)
    const accessEntities = accessRes?.service_entity || []
    if (res && Array.isArray(res.service_entity)) {
      res.service_entity.forEach(se => {
        const ae = accessEntities.find(a => a.securityContext === se.securityContext)
        if (ae && ae.service_entity_owners) {
          se.service_entity_owners = ae.service_entity_owners
        }
      })
    }
  } catch (e) {
    console.error(`${fn}: Failed to augment service entities with access list`, e)
  }
  logRes(fn, res, dbg)
  res.service_entity.forEach((se) => {
    let currentOwner
    if (se && se.service_entity_owners) {
      currentOwner = se.service_entity_owners.find(seo => seo.valid_from && !seo.valid_till)
      if (currentOwner) {
        se.customerId = currentOwner.customerId
        se.nickname = currentOwner.nickname
      }
    }
    if (se.serviceEntityId && !se.attributes) se.attributes = {}
  })
  logRes(fn, res, dbg)
  return res
}

const getAllServiceEntitiesForCustomer = async (sc, dbg = false) => {
  const fn = 'getAllServiceEntitiesForCustomer'
  doLog(fn, { sc }, dbg)
  if (!sc) return
  const params = { sc }
  const route = getRoute('customer', 'assets/service-entities', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res.service_entity || res
}

const getServiceEntity = async (sc, serviceEntityId, dbg = false) => {
  const fn = 'getServiceEntity'
  doLog(fn, { sc, serviceEntityId }, dbg)
  const limit = 500
  const offset = 0
  const type = null
  const search = null
  const customerId = null
  const attribute = null
  const matchExact = false
  const res = await getAllServiceEntities(sc, limit, offset, type, search, customerId, serviceEntityId, attribute, matchExact, dbg, null, null, 'all')
  doLog(fn, { res }, dbg)
  const serviceEntity = res.service_entity.find((se) => se.serviceEntityId === serviceEntityId)
  if (serviceEntity) {
    if (serviceEntity.serviceEntityId && !serviceEntity.attributes) serviceEntity.attributes = {}
    doLog(fn, { serviceEntity }, dbg)
    const delegatedAccessCustomers = serviceEntity.service_entity_delegated_accesses
    doLog(fn, { delegatedAccessCustomers }, dbg)
    serviceEntity.delegatedAccessCustomerIds = (delegatedAccessCustomers || []).map((dac) => dac.customerId)
  }
  return serviceEntity
}

const getServiceEntitiesForCustomerId = async (sc, customerId, dbg = true) => {
  const fn = 'getServiceEntitiesForCustomerId'
  const limit = 500
  const offset = 0
  const type = null
  const search = ''
  const serviceEntityId = null
  const attribute = null
  const matchExact = false
  const res = await getAllServiceEntities(sc, limit, offset, type, search, customerId, serviceEntityId, attribute, matchExact, dbg, null, null, 'all')
  logRes(fn, res, dbg)
  return res.service_entity
}

const getCustomerForServiceEntity = async (owner, serviceEntityId, dbg = false) => {
  const fn = 'getCustomerForServiceEntity'
  // todo: move to api
  let sc = `${owner}/ASSETS`
  doLog(fn, { sc, serviceEntityId }, dbg)
  let res = await getAllServiceEntities(sc, 1, 0, null, null, null, serviceEntityId, null, false, dbg, null, null, 'all')
  let serviceEntity = res?.service_entity[0] || res
  doLog(fn, { serviceEntity }, dbg)
  if (!serviceEntity) return
  const { securityContext } = serviceEntity
  // get owner record
  const status = 'approved'
  res = await getAllServiceEntitiesAccessList(securityContext, 1, 0, status, null, dbg)
  logRes(fn, res, dbg)
  serviceEntity = res?.service_entity[0]
  const serviceEntityOwner = serviceEntity?.service_entity_owners[0]
  if (!serviceEntityOwner) return
  const { customerId } = serviceEntityOwner
  doLog(fn, { customerId }, dbg)
  sc = `${owner}/CUSTOMERS`
  res = await getCustomers(sc, 1, 0, customerId, null, null, '', dbg)
  logRes(fn, res, dbg)
  return res?.user[0]
}

const getCustomerIdForServiceEntity = async (serviceEntitySecurityContext, dbg = false) => {
  const fn = 'getCustomerIdForServiceEntity'
  doLog(fn, { serviceEntitySecurityContext }, dbg)
  // get owner record
  const status = 'approved'
  const res = await getAllServiceEntitiesAccessList(serviceEntitySecurityContext, 1, 0, status, null, dbg)
  logRes(fn, res, dbg)
  const serviceEntity = res?.service_entity[0]
  const serviceEntityOwner = serviceEntity?.service_entity_owners[0]
  if (!serviceEntityOwner) return
  const { customerId } = serviceEntityOwner
  doLog(fn, { customerId }, dbg)
  return customerId
}

const updateServiceEntity = async (sc, address, city, postcode, state, country, coordinates, utilityIdentifier, serviceType, attributes, dbg = false) => {
  const fn = 'updateServiceEntity'
  doLog(fn, { sc }, dbg)
  const params = { address, city, postcode, state, country, coordinates, utilityIdentifier, serviceType, attributes }
  const route = getRoute('assets', 'service-entity', 'update', dbg) // 'assets/service-entity/update'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateServiceEntityCoordinates = async (sc, coordinates, dbg = false) => {
  const fn = 'updateServiceEntityCoordinates'
  const params = { coordinates }
  const route = getRoute('assets', 'service-entity/coordinates', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const addServiceEntityVeAuth = async (
  securityContext,
  address,
  city,
  postcode,
  state,
  country,
  coordinates,
  utilityIdentifier,
  serviceType = 'Supply',
  debug = false
) => {
  addToLog(`addServiceEntityVeAuth:securityContext: ${securityContext}`, debug)
  const params = {
    address,
    city,
    postcode,
    state,
    country,
    coordinates,
    utilityIdentifier,
    serviceType
  }
  const route = getRoute('assets', 'service-entity', 'add', debug) // 'assets/service-entity/add'
  addToLog(
    `addServiceEntityVeAuth:route: ${route}, params: ${JSON.stringify(params)}`,
    debug
  )
  const response = await API.put2(
    route,
    { securityContext },
    JSON.stringify(params),
    debug
  )
  addToLog(
    `addServiceEntityVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response.insert_service_entity_one.serviceEntityId
}

const addServiceEntitiesVeAuth = async (serviceEntities, debug = false) => {
  addToLog(
    `addServiceEntitiesVeAuth:serviceEntities: ${serviceEntities}`,
    debug
  )
  const params = serviceEntities
  const { securityContext } = serviceEntities[0]
  const route = getRoute('assets', 'service-entities', 'add', debug) // 'assets/service-entities/add'
  addToLog(
    `addServiceEntitiesVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.post2(
    route,
    { securityContext },
    JSON.stringify(params),
    debug
  )
  addToLog(
    `addServiceEntitiesVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const deleteServiceEntity = async (sc, dbg = false) => {
  const fn = 'deleteServiceEntity'
  const params = { sc }
  const route = getRoute('assets', 'service-entities', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, params)
  logRes(fn, res, dbg)
  return res
}

const getDataConfigPropValue = async (key, owner, lng, dbg = false) => {
  // satConfig.features.languageSelector.display
  // ownerConfig.information.organisationName
  const fn = 'getDataConfigPropValue'
  if (!owner) owner = 'default'
  const language = lng || 'en'
  addToLog(`${fn}:key: ${key}`, dbg)
  const keyParts = key.split('.')
  const [dcName, dcType] = keyParts
  addToLog(`${fn}:dcName: ${dcName}, dcType: ${dcType}`, dbg)
  const hasDcType = ['features', 'styles', 'translations'].includes(dcType)
  const propKey = hasDcType ? keyParts[2] : keyParts[1]
  addToLog(`${fn}:propKey: ${propKey}`, dbg)
  const dcData = await getDataConfigForKey(key, owner, dbg)
  addToLog(`${fn}:dcData: ${JSON.stringify(dcData)}`, dbg)
  const hasLanguage = typeof dcData?.[language] !== 'undefined'
  addToLog(`${fn}:hasLanguage: ${hasLanguage}`, dbg)
  const languageData = hasLanguage ? dcData[language] : dcData
  addToLog(`${fn}:languageData: ${JSON.stringify(languageData)}`, dbg)
  const hasDataKey = typeof languageData?.data !== 'undefined'
  addToLog(`${fn}:hasDataKey: ${hasDataKey}`, dbg)
  const propKeyValue = hasDataKey
    ? languageData?.data?.[propKey]
    : languageData?.[propKey]
  addToLog(`${fn}:propKeyValue: ${JSON.stringify(propKeyValue)}`, dbg)
  const hasSubKeys = key.split(`.${propKey}.`).length > 1
  addToLog(`${fn}:hasSubKeys: ${hasSubKeys}`, dbg)
  if (!hasSubKeys) return propKeyValue
  const propSubKeys = key.split(`.${propKey}.`)[1].split('.')
  addToLog(`${fn}:propSubKeys: ${JSON.stringify(propSubKeys)}`, dbg)
  let propValue = propKeyValue
  for (const psKey of propSubKeys) {
    propValue = propValue?.[psKey]
  }
  addToLog(`${fn}:propValue: ${propValue}`, dbg)
  return propValue
}

const setDataConfigPropValue = async (keys, values, owner, dbg = false) => {
  // todo: add support for dataConfig with language, e.g. satConfig
  // ownerConfig.information.organisationName
  const fn = 'setDataConfigPropValue'
  if (!Array.isArray(keys)) keys = [keys]
  if (!Array.isArray(values)) values = [values]
  const kv = {} // dcName: { keys: [], values: [] }
  for (const i in keys) {
    // group by dcName
    const dcName = `${keys[i].split('.')[0]}` // ownerConfig
    if (!kv[dcName]?.keys) kv[dcName] = { keys: [], values: [] }
    kv[dcName].keys.push(keys[i])
    kv[dcName].values.push(values[i])
  }
  doLog(fn, { kv }, dbg)
  for (const dcName of Object.keys(kv)) {
    // process dcName
    const key = kv[dcName].keys[0]
    let dcData = await getDataConfigForKey(key, owner, dbg)
    doLog(fn, { key, dcData }, dbg)
    for (const i in kv[dcName].keys) {
      const key = kv[dcName].keys[i]
      const value = kv[dcName].values[i]
      const keyWithoutDcName = key.split('.').slice(1).join('.')
      doLog(fn, { key, keyWithoutDcName, value }, dbg)
      dcData = setGetObjectKeyValue(dcData, keyWithoutDcName, value, dbg)
    }
    // update dcData for all keys of dcName
    const sc = `${owner}/CONFIGS/data/${dcName}`
    const res = await updateDataConfigData(sc, dcData, dbg)
    logRes(fn, res, dbg)
  }
}

const getDataConfigForKey = async (key, owner, dbg = false) => {
  // satConfig.features.languageSelector.display
  // ownerConfig.information.organisationName
  const fn = 'getDataConfigForKey'
  if (!owner) owner = 'default'
  addToLog(`${fn}:key: ${key}`, dbg)
  const keyParts = key.split('.')
  const [dcName, dcType] = keyParts
  const hasDcType = ['features', 'styles', 'translations'].includes(dcType)
  const propKey = hasDcType ? keyParts[2] : keyParts[1]
  addToLog(`${fn}:propKey: ${propKey}`, dbg)
  const sc = `${owner}/CONFIGS/data/${dcName}`
  const dataConfig = await getDataConfig(sc, dbg)
  // addToLog(`${fn}:dataConfig: ${JSON.stringify(dataConfig)}`, dbg)
  const dcData = hasDcType
    ? dataConfig?.[dcName]?.[dcType]
    : dataConfig?.[dcName]
  addToLog(`${fn}:dcData: ${JSON.stringify(dcData)}`, dbg)
  return dcData
}

const setGetObjectKeyValue = (data, key, value, dbg = false) => {
  // ownerConfig.information.organisationName
  const fn = 'setGetObjectKeyValue'
  let schema = data // a moving reference to internal objects within data
  const keyParts = key.split('.')
  const len = keyParts.length
  addToLog(`${fn}:key: ${key}, value: ${value}`, dbg)
  // loop over 'level' keys
  for (let i = 0; i < len - 1; i++) {
    const elem = keyParts[i]
    addToLog(`${fn}:elem: ${elem}`, dbg)
    if (!schema[elem]) schema[elem] = {}
    schema = schema[elem]
    addToLog(`${fn}:schema: ${JSON.stringify(schema)}`, dbg)
  }
  if (!value) {
    return schema[keyParts[len - 1]]
  }
  addToLog(`${fn}:setting key: ${keyParts[len - 1]} to value: ${value}`, dbg)
  schema[keyParts[len - 1]] = value
  addToLog(`${fn}:schema: ${JSON.stringify(schema)}`, dbg)
  return { ...data }
}

const getDataConfigOwners = async (debug = false) => {
  const owners = await getDataConfigs('owner', debug)
  return getUniqueArray(owners)
}

const getDataConfigs = async (returnData, debug = true) => {
  const fn = 'getDataConfigs'
  const params = { sc: 'VE/CONFIGS', returnData }
  const route = getRoute('configs', '', 'read', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.get2(route, params, debug)
  logRes(fn, res, debug)
  return res
}

const getDataConfig = async (sc, debug = true) => {
  const fn = 'getDataConfig'
  const params = { sc }
  const route = getRoute('configs', 'data', 'read', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.get2(route, params, debug)
  logRes(fn, res, debug)
  return res
}

const updateDataConfigVeAuth = async (dataConfig, debug = false) => {
  // todo: refactor?
  const fn = 'updateDataConfigVeAuth'
  const { owner } = dataConfig
  const sc = `${owner}/CONFIGS`
  const params = { owner, dataConfig }
  const route = getRoute('configs', '', 'update', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.put2(route, { sc }, params, debug)
  logRes(fn, res, debug)
  return res
}

const updateDataConfigData = async (sc, dataConfigValue, dbg = false) => {
  const fn = 'updateDataConfigData'
  dbg = true
  const params = { sc, dataConfigValue }
  const route = getRoute('configs', 'data', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getSetupConfigOwners = async (dbg = false) => {
  const owners = await getSetupConfigs('owner', dbg)
  return getUniqueArray(owners)
}

const getSetupConfigs = async (returnData, debug = false) => {
  const fn = 'getSetupConfigs'
  const params = { sc: 'VE/CONFIGS', returnData }
  const route = getRoute('configs', '', 'read', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.get2(route, params, debug)
  logRes(fn, res, debug)
  return res
}

const getSetupConfig = async (securityContext, debug = true) => {
  const fn = 'getSetupConfig'
  const params = { securityContext }
  const route = getRoute('configs', 'setup', 'read', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.get2(route, params, debug)
  logRes(fn, res, debug)
  return res
}

const getSetupWizardUiConfig = async (owner, lang = 'en', debug = false) => {
  const sc = `${owner}/CONFIGS/setup/setupWizardUiConfig`
  const action = 'all'
  return await getDynamicSetupConfigVeAuth(sc, lang, action, debug)
}

const getDynamicSetupConfigVeAuth = async (
  sc,
  language = 'en',
  action = 'all',
  debug = true
) => {
  const fn = 'getDynamicSetupConfigVeAuth'
  const params = { sc, language, action }
  const route = getRoute('configs', 'setup/dynamic', 'read', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.get2(route, params, debug)
  logRes(fn, res, debug)
  return res
}

const updateSetupConfigVeAuth = async (setupConfig, debug = false) => {
  const fn = 'updateSetupConfigVeAuth'
  const { owner } = setupConfig
  const sc = `${owner}/CONFIGS` // todo: review??
  const params = { owner, setupConfig }
  const route = getRoute('configs', '', 'update', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.put2(route, { sc }, params, debug)
  logRes(fn, res, debug)
  return res
}

const updateSetupConfigDataVeAuth = async (
  sc,
  setupConfigValue,
  debug = false
) => {
  const fn = 'updateSetupConfigDataVeAuth'
  const params = { sc, setupConfigValue }
  const route = getRoute('configs', 'setup', 'update', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.put2(route, { sc }, params, debug)
  logRes(fn, res, debug)
  return res
}

const getImageConfigVeAuth = async (params, debug = true) => {
  const fn = 'getImageConfigVeAuth'
  const route = getRoute('configs', 'image', 'read', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.get2(route, params, debug)
  logRes(fn, res, debug)
  return res
}

const getCustomers = async (sc, limit, offset, search, appAccess, status, projection, dbg = false) => {
  const fn = 'getCustomers'
  const params = { sc, limit, offset, search, appAccess, status, projection }
  const route = getRoute('customers', '', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  return await API.get2(route, params, dbg)
}

const getCustomersForSecurityContext = async (sc, limit, offset, search, appAccess, status, customerId, projection, subscriptionExpiry = null, activeContracts = null, suspendedContracts = null, contractMatchMode = 'all', apiTokens = null, preferredComms = null, dbg = false, contractTypes = null, assetCountries = null, assetStates = null, utilities = null) => {
  const fn = 'getCustomersForSecurityContext'
  const params = { sc, limit, offset, search, appAccess, status, projection, subscriptionExpiry, activeContracts, suspendedContracts, contractMatchMode, apiTokens, preferredComms, contractTypes, assetCountries, assetStates, utilities }
  if (customerId) params.customerId = customerId
  const route = getRoute('customers', '', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  return await API.get2(route, params, dbg)
}

const getCustomer = async (sc, returnData, dbg = false) => {
  const fn = 'getCustomer'
  const res = await getCustomers(sc, 1, 0, '', null, null, returnData, dbg)
  logRes(fn, { res }, dbg)
  return res.user[0]
}

const getCustomerAppInfo = async (sc, dbg = false) => {
  const fn = 'getCustomerAppInfo'
  const params = { sc }
  const route = getRoute('customers', 'app-info', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteCustomerAppInfo = async (sc, appClientIds, dbg = false) => {
  const fn = 'deleteCustomerAppInfo'
  const params = { sc, appClientIds }
  const route = getRoute('customers', 'app-info', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getCustomerToken = async (sc, dbg = false) => {
  const fn = 'getCustomerToken'
  const params = { sc }
  const route = getRoute('customers', 'token', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getCustomerTokenForTokenId = async (sc, tokenId, dbg = false) => {
  const fn = 'getCustomerTokenForTokenId'
  const params = { sc, tokenId }
  const route = getRoute('customers', 'token', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteCustomerToken = async (sc, tokenIds, dbg = false) => {
  const fn = 'deleteCustomerToken'
  const params = { sc, tokenIds }
  const route = getRoute('customers', 'token', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const addCustomerToken = async (sc, tokenId, dbg = false) => {
  const fn = 'addCustomerToken'
  const params = { sc, tokenId }
  const route = getRoute('customers', 'token', 'add', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getCustomerNameIds = async (sc, limit = 100, offset, search, projection = 'id,firstName,lastName', dbg = false) => {
  const fn = 'getCustomerNameIds'
  const params = { sc, limit, offset, search, projection }
  const route = getRoute('customers', 'nameids', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getCustomerLedgerAccounts = async (securityContext, dbg = false) => {
  const fn = 'getCustomerLedgerAccounts'
  const params = { securityContext }
  const route = getRoute('customers', 'ledgeraccounts', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  return await API.get2(route, params, dbg)
}

const getSupportRequestsForCustomer = async (sc, apiParams = {}, dbg) => {
  const fn = 'getSupportRequestsForCustomer'
  const params = { sc, ...apiParams }
  const route = getRoute('customer', 'support-requests', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getOrdersForCustomer = async (sc, apiParams = {}, dbg) => {
  const fn = 'getOrdersForCustomer'
  const params = { sc, ...apiParams }
  const route = getRoute('customer', 'orders', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const addSupportRequest = async (sc, summary, description, dbg) => {
  const fn = 'addSupportRequest'
  const params = { summary, description }
  const route = getRoute('customers', 'support-request', 'add', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getCustomerChangeLog = async (sc, limit, createdAt, id, dbg) => {
  const fn = 'getCustomerChangeLog'
  const params = { sc, limit, createdAt, id }
  // todo: review, add performedBy, fromDate, toDate, changedFields
  const route = getRoute('customer', 'changelog', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const addCustomer = async (params, dbg = false) => {
  const fn = 'addCustomer'
  // const params = { email, firstName, lastName, status, appAccess }
  const { email, securityContext } = params
  if (!email) return
  const route = getRoute('customers', '', 'add', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { securityContext }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const addCustomers = async (customers, dbg = false) => {
  const fn = 'addCustomers'
  // todo: implement as single API call
  // [{ email, firstName, lastName, status, appAccess },...]
  const customerParams = []
  for (const customer of customers) {
    if (customer.email) customerParams.push(customer)
  }
  doLog(fn, { customerParams }, dbg)
  const ress = []
  for (const customer of customerParams) {
    const res = await addCustomer(customer, dbg)
    ress.push(res)
  }
  logRes(fn, ress, dbg)
  return ress
}

const getMsApiBaseUrl = (dbg = false) => {
  const veEnvLoc = (typeof getVeEnvLoc === 'function' ? getVeEnvLoc(dbg) : null) ||
    window.sessionStorage.getItem('veEnvLoc') ||
    've-cd-dev-au-1'

  let msApiUrl = null
  if (typeof getVeEnvLocMsApiUrl === 'function') {
    msApiUrl = getVeEnvLocMsApiUrl(vecfg, veEnvLoc, dbg)
  }

  // Fallback map matching ve-sat/deployConfig/veEnvLocs.json
  if (!msApiUrl) {
    const fallbackMsApiUrls = {
      've-cd-dev-au-1': 'https://msapi.vecddevau1.village.energy',
      've-cd-dev-in-1': 'https://api.vecddev.village.energy',
      'sy-prod-au-1': 'https://msapi.syprodau1.village.energy',
      'tp-test-in-1': 'https://msapi.tptest.village.energy'
    }
    msApiUrl = fallbackMsApiUrls[veEnvLoc] || 'https://msapi.vecddevau1.village.energy'
  }

  return msApiUrl.replace(/\/+$/, '')
}

const resolveMsApiFetchUrl = (endpointUrl) => {
  const useProxy = process.env.NODE_ENV === 'development'
  let fetchUrl = endpointUrl
  if (useProxy) {
    fetchUrl = fetchUrl.replace('https://api.vecddev.village.energy', '/proxy/ms/ve-cd-dev-in-1')
    fetchUrl = fetchUrl.replace('https://msapi.vecddevau1.village.energy', '/proxy/ms/ve-cd-dev-au-1')
    fetchUrl = fetchUrl.replace('https://msapi.syprodau1.village.energy', '/proxy/ms/sy-prod-au-1')
    fetchUrl = fetchUrl.replace('https://msapi.tptest.village.energy', '/proxy/ms/tp-test-in-1')
  }
  return fetchUrl
}

const requestOnboardingPin = async (params, dbg = false) => {
  const fn = 'requestOnboardingPin'
  const msApiBaseUrl = getMsApiBaseUrl(dbg)
  const fullUrl = `${msApiBaseUrl}/webhook/handler/voltello/onboard-start`
  const fetchUrl = resolveMsApiFetchUrl(fullUrl)

  doLog(fn, { msApiBaseUrl, fullUrl, fetchUrl, params }, dbg)

  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json, text/plain, */*'
  }

  try {
    const res = await window.fetch(fetchUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(params)
    })
    const text = await res.text()
    let data
    try {
      data = JSON.parse(text)
    } catch (e) {
      data = { result: res.ok, message: text }
    }
    doLog(fn, { data }, dbg)
    return data
  } catch (err) {
    doLog(fn, { error: err.message }, true)
    throw err
  }
}

const onboardCustomerDirect = async (params, dbg = false) => {
  const fn = 'onboardCustomerDirect'
  if (params.suppress2faPinEmail === undefined) {
    params.suppress2faPinEmail = true
  }
  if (params.skipDiscovery === undefined) {
    params.skipDiscovery = true
  }

  const oemBrand = params.oemBrand || 'voltello'
  const msApiBaseUrl = getMsApiBaseUrl(dbg)
  const fullUrl = `${msApiBaseUrl}/webhook/handler/${oemBrand}/onboard-direct`
  const fetchUrl = resolveMsApiFetchUrl(fullUrl)

  doLog(fn, { msApiBaseUrl, fullUrl, fetchUrl, params }, dbg)

  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json, text/plain, */*'
  }

  try {
    const res = await window.fetch(fetchUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(params)
    })
    const text = await res.text()
    let data
    try {
      data = JSON.parse(text)
    } catch (e) {
      data = { result: res.ok, message: text }
    }
    doLog(fn, { data }, dbg)
    return data
  } catch (err) {
    doLog(fn, { error: err.message }, true)
    throw err
  }
}

const getOnboardingStatus = async (token, oemBrand = 'voltello', dbg = false) => {
  const fn = 'getOnboardingStatus'
  const msApiBaseUrl = getMsApiBaseUrl(dbg)
  const fullUrl = `${msApiBaseUrl}/webhook/handler/${oemBrand}/onboard-status?token=${encodeURIComponent(token)}`
  const fetchUrl = resolveMsApiFetchUrl(fullUrl)

  doLog(fn, { msApiBaseUrl, fullUrl, fetchUrl, token, oemBrand }, dbg)

  const headers = {
    Accept: 'application/json, text/plain, */*'
  }

  try {
    const res = await window.fetch(fetchUrl, {
      method: 'GET',
      headers
    })
    const text = await res.text()
    let data
    try {
      data = JSON.parse(text)
    } catch (e) {
      data = { result: res.ok, message: text }
    }
    doLog(fn, { data }, dbg)
    return data
  } catch (err) {
    doLog(fn, { error: err.message }, true)
    throw err
  }
}

const deleteCustomer = async (sc, dbg = true) => {
  const fn = 'deleteCustomer'
  const updateStatusRes = await updateCustomerStatus([sc], 'inactive', dbg)
  await sleep(200)
  const params = {}
  const route = getRoute('customers', '', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const deleteRes = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, deleteRes, dbg)
  return [updateStatusRes, deleteRes]
}

const deleteTestCustomers = async (limit = 0, deltaInMilliseconds = 1000, dbg = false) => {
  const fn = 'deleteTestCustomers'
  const sc = 'Test/CUSTOMERS/'
  const res = await getCustomers(sc, limit, 0, '', null, null, 'securityContext', dbg)
  const customers = res.user
  if (limit < 3) doLog(fn, { customers }, dbg)
  const cCount = customers.length
  doLog(fn, { cCount }, dbg)
  const ress = []
  let counter = 0
  for await (const customer of customers) {
    counter++
    const sc = customer?.securityContext
    try {
      if (sc.startsWith('Test/CUSTOMERS/')) ress.push(await deleteCustomer(sc, dbg))
      await sleep(deltaInMilliseconds)
    } catch (e) {
      const sleepingInMilliseconds = 5 * deltaInMilliseconds
      doLog(fn, { sleepingInMilliseconds, counter, cCount, sc, error: e }, true)
      await sleep(sleepingInMilliseconds)
    }
    doLog(fn, { counter, cCount }, dbg)
  }
  logRes(fn, ress, dbg)
}

const deleteCPCCustomers = async (limit = 0, dbg = false) => {
  const fn = 'deleteCPCCustomers'
  const sc = 'CPC/CUSTOMERS/'
  const res = await getCustomers(sc, limit, 0, '', null, null, 'securityContext', dbg)
  const customers = res.user
  if (limit < 3) doLog(fn, { customers }, dbg)
  const ress = []
  for await (const customer of customers) {
    const sc = customer?.securityContext
    if (sc.startsWith('CPC/CUSTOMERS/')) ress.push(await deleteCustomer(sc, dbg))
    await sleep(500)
  }
  logRes(fn, ress, dbg)
}

const invokeContractVeAuth = async (sc, params, dbg = false) => {
  const fn = 'invokeContractVeAuth'
  const route = getRoute('contracts', '', 'invoke', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const invokeContractExecuteAlgorithmVeAuth = async (
  securityContext,
  algo,
  metric,
  contract,
  debug = true
) => {
  const params = { satAction: 'executeAlgorithm', algo, metric, contract }
  addToLog(
    `veapi:invokeContractExecuteAlgorithmVeAuth:securityContext: ${securityContext}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await invokeContractVeAuth(securityContext, params, debug)
  addToLog(
    'veapi:invokeContractExecuteAlgorithmVeAuth:response: ' +
    JSON.stringify(response),
    debug
  )
  return response
}

const invokeContractExecuteGlobalAlgorithmsVeAuth = async (
  securityContext,
  metric,
  debug = true
) => {
  const params = { satAction: 'executeGlobalAlgorithms', metric }
  addToLog(
    `veapi:invokeContractExecuteGlobalAlgorithmsVeAuth:securityContext: ${securityContext}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await invokeContractVeAuth(securityContext, params, debug)
  addToLog(
    `veapi:invokeContractExecuteGlobalAlgorithmsVeAuth:response: ${JSON.stringify(
      response
    )}`,
    debug
  )
  return response
}

const invokeContractExecuteContractAlgorithmsVeAuth = async (
  securityContext,
  metric,
  debug = true
) => {
  const params = { satAction: 'executeContractAlgorithms', metric }
  addToLog(
    `veapi:invokeContractExecuteContractAlgorithms:securityContext: ${securityContext}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await invokeContractVeAuth(securityContext, params, debug)
  addToLog(
    `veapi:invokeContractExecuteContractAlgorithms:response: ${JSON.stringify(
      response
    )}`,
    debug
  )
  return response
}

const invokeContractExecuteContractActionVeAuth = async (
  securityContext,
  act,
  metric,
  contract,
  debug
) => {
  const params = { satAction: 'executeContractAction', act, metric, contract }
  addToLog(
    `veapi:invokeContractExecuteContractActionVeAuth:securityContext: ${securityContext}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await invokeContractVeAuth(securityContext, params, debug)
  addToLog(
    `veapi:invokeContractExecuteContractActionVeAuth:response: ${JSON.stringify(
      response
    )}`,
    debug
  )
  return response
}

const invokeContractExecuteContractActions = async (sc, metric, dbg) => {
  const fn = 'invokeContractExecuteContractActions'
  const params = { satAction: 'executeContractActions', metric }
  doLog(`${fn}:sc`, sc, 'params', params, dbg)
  const res = await invokeContractVeAuth(sc, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const invokeContractCallIeeeAggClient = async (sc, metric, dbg) => {
  const fn = 'invokeContractCallIeeeAggClient'
  const debug = dbg || false
  metric = { ...metric, debug }
  const params = { satAction: 'callIeeeAggClient', metric, debug }
  doLog(`${fn}:sc`, sc, 'metric', metric, dbg)
  const res = await invokeContractVeAuth(sc, params, dbg)
  logRes(fn, res, dbg)
  return res?.contractData[0] || res
}

const scheduleContractDef = async (sc, contractDefs, metric, action, dbg = false) => { // version, trigger
  const fn = 'scheduleContractDef'
  const params = { satAction: 'scheduleContractDef', action, contractDefs, metric } // version, trigger
  const route = getRoute('contracts', '', 'schedule', dbg) // 'contracts/schedule'
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const listScheduledContractDefs = async (params, dbg = false) => {
  const fn = 'listScheduledContractDefs'
  const action = 'listScheduledContractDefs'
  doLog(fn, { params }, dbg)
  const { securityContext: sc, contractId, maxSchedules = 300 } = params
  const metric = contractId ? { contractIds: [contractId] } : {}
  metric.maxSchedules = maxSchedules
  const res = await scheduleContractDef(sc, [{}], metric, action, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteScheduledContractDef = async (sc, contractDefs, dbg = false) => {
  const fn = 'deleteScheduledContractDef'
  const action = 'deleteContractDefSchedule'
  // const { securityContext, contractDefVersion, trigger } = contractDef
  const res = await scheduleContractDef(sc, contractDefs, {}, action, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteHistoricContractDefSchedules = async (sc, dbg = false) => {
  const fn = 'deleteHistoricContractDefSchedules'
  const action = 'deleteHistoricContractDefSchedules'
  const res = await scheduleContractDef(sc, [{}], {}, action, dbg)
  logRes(fn, res, dbg)
  return res
}

const getNextStartForContractDefs = async (sc, contractDefs, dbg = false) => {
  const fn = 'getNextStartForContractDefs'
  const action = 'getNextStartForContractDefs'
  const res = await scheduleContractDef(sc, contractDefs, {}, action, dbg)
  logRes(fn, res, dbg)
  return res
}

const getContractDefCountByPricingProfile = async (sc, pricingProfiles, dbg = false) => {
  const fn = 'getContractDefCountByPricingProfile'
  const params = { sc, pricingProfiles }
  const route = getRoute('contracts', 'definitions/pricing', 'read', dbg) // 'contracts/definitions/pricing/read'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getContractDefInfoByPricingProfileVeAuth = async (
  securityContext,
  pricingProfiles,
  debug = false
) => {
  const params = { securityContext, pricingProfiles }
  const route = getRoute('contracts', 'definitions/info', 'read', debug) // 'contracts/definitions/info/read'
  addToLog(
    `veapi:getContractDefInfoByPricingProfileVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(
    `veapi:getContractDefInfoByPricingProfileVeAuth:response: ${JSON.stringify(
      response
    )}`,
    debug
  )
  return response
}

const calculateCapacityPriceVeAuth = async (
  capacity,
  contractDefIdVersions,
  returnData,
  debug = false
) => {
  const { securityContext } = contractDefIdVersions[0] // todo: review, using security context of first one
  const params = {
    securityContext,
    capacity,
    contractDefIdVersions,
    returnData
  }
  const route = getRoute(
    'contracts',
    'definitions/pricing/calculate',
    'read',
    debug
  ) // 'contracts/definitions/pricing/calculate/read'
  addToLog(
    `veapi:calculateCapacityPriceVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(
    `veapi:calculateCapacityPriceVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

// used in Admin / Data
const createContractsFromContractDef = async (contractDef, contractParams, serviceEntityCriteria, batchSize = 150, dbg = false) => {
  const fn = 'createContractsFromContractDef'
  const { serviceEntityIds } = serviceEntityCriteria
  let serviceEntityIdsBatch = [serviceEntityIds]
  if (serviceEntityIds && serviceEntityIds.length > batchSize) {
    serviceEntityIdsBatch = splitArray(serviceEntityIds, batchSize)
  }
  const route = 'contracts/batch/add'
  const { securityContext } = contractDef
  const ress = []
  for (const serviceEntityIds of serviceEntityIdsBatch) {
    if (serviceEntityIds) serviceEntityCriteria.serviceEntityIds = serviceEntityIds
    const params = { satAction: 'createContractsFromContractDef', contractDef, contractParams, serviceEntityCriteria }
    logRouteParams(fn, route, params, dbg)
    const res = await API.post2(route, { securityContext }, params, dbg)
    logRes(fn, res, dbg)
    ress.push(res)
  }
  return ress
}

const getDeviceIdForDeviceSerialNumber = async (id, dbg = false) => {
  const fn = 'getDeviceIdForDeviceSerialNumber'
  const deviceSerialNumber = id
  const sc = 'VE/ASSETS'
  const params = { sc, deviceSerialNumber }
  const route = getRoute('assets', 'devices/deviceserialnumber', 'read')
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDevicesForDeviceSerialNumber = async (
  deviceSerialNumber,
  returnData,
  limit,
  debug = false
) => {
  const params = { deviceSerialNumber, returnData, limit, debug }
  addToLog(
    `veapi:getDevicesForDeviceSerialNumber:params: ${JSON.stringify(
      params
    )}, returnData: ${returnData}, limit: ${limit}`,
    debug
  )
  const response = await API.get(
    '/deviceserialnumber/devices/all',
    params,
    undefined,
    100,
    undefined,
    debug
  )
  addToLog(
    `veapi:getDevicesForDeviceSerialNumber:response: ${JSON.stringify(
      response
    )}`,
    debug
  )
  return response.Item || response.Items || response
}

const getMeterIdsForDeviceId = async (deviceId, debug = false) => {
  const params = { deviceId, debug }
  addToLog(
    'veapi:getMeterIdsForDeviceId:params: ' + JSON.stringify(params),
    debug
  )
  const response = await API.get(
    '/device/meterids',
    params,
    undefined,
    100,
    undefined,
    debug
  )
  addToLog(
    'veapi:getMeterIdsForDeviceId:response: ' + JSON.stringify(response),
    debug
  )
  return response.Item || response.Items || response
}

const getMetersForDeviceId = async (deviceId, context, debug = false) => {
  const params = { deviceId, debug }
  addToLog(
    'veapi:getMetersForDeviceId:params: ' + JSON.stringify(params),
    debug
  )
  const response = await API.get(
    '/device/meters',
    params,
    context,
    100,
    undefined,
    debug
  )
  addToLog(
    'veapi:getMetersForDeviceId:response: ' + JSON.stringify(response),
    debug
  )
  return response.Item || response.Items || response
}

const getEndPointsForDeviceSerialNumber = async (deviceSerialNumber, dbg = false) => {
  const fn = 'getEndPointsForDeviceSerialNumber'
  const params = { deviceSerialNumber }
  const route = '/deviceserialnumber/endpoints/all'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get(route, params, undefined, 100, undefined, dbg)
  logRes(fn, res, dbg)
  return res.Item || res.Items || res
}

const verifyDeviceSerialNumber = async (
  deviceSerialNumber,
  userId,
  debug = false
) => {
  const params = { deviceSerialNumber, userId, debug }
  addToLog(
    `veapi:verifyDeviceSerialNumber:params: ${JSON.stringify(params)}`,
    debug
  )
  const response = await API.get(
    '/deviceserialnumber/endpoints',
    params,
    undefined,
    100,
    undefined,
    debug
  )
  addToLog(
    `veapi:verifyDeviceSerialNumber:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const getMeter = async ( // todo: review, remove if not needed
  meterId,
  includePreviouslyOwnedMeters,
  debug = false
) => {
  includePreviouslyOwnedMeters =
    typeof includePreviouslyOwnedMeters === 'undefined'
      ? false
      : includePreviouslyOwnedMeters
  const params = {
    meterId,
    includePreviouslyOwnedMeters,
    dataSource: getDataSource(debug), // todo: review, not required??
    debug
  }
  addToLog('veapi:getMeter:params: ' + JSON.stringify(params), debug)
  const response = await API.get(
    '/meter',
    params,
    undefined,
    100,
    undefined,
    debug
  )
  addToLog('veapi:getMeter:response: ' + JSON.stringify(response), debug)
  let meter = response
  if (hasProp(response, 'Item')) meter = response.Item
  if (hasProp(response, 'Items')) meter = response.Items
  if (Array.isArray(response) && response.length === 1) meter = response[0]
  addToLog('veapi:getMeter:meter: ' + JSON.stringify(meter), debug)
  return meter
}

const getMeterData = async (meterId, returnData, debug = false) => {
  const params = { meterId, debug }
  if (returnData) params.returnData = returnData
  addToLog('veapi:getMeterData:params: ' + JSON.stringify(params), debug)
  const meter = await API.get(
    '/meter/data',
    params,
    undefined,
    100,
    undefined,
    debug
  )
  addToLog('veapi:getMeterData:meter: ' + JSON.stringify(meter), debug)
  return meter
}

const getEndPointData = async (endPointId, returnData, dbg = false) => { // todo: remove
  const fn = 'getEndPointData'
  const params = { endPointId }
  if (returnData) params.returnData = returnData
  const route = '/endpoint/data'
  logRouteParams(fn, route, params, dbg)
  const endPoint = await API.get(route, params, undefined, 100, undefined, dbg)
  logRes(fn, endPoint, dbg)
  return endPoint
}

const getEndPointDataVeAuth = async (sc, returnData, dbg = false) => {
  const fn = 'getEndPointDataVeAuth'
  const params = { sc }
  if (returnData) params.returnData = returnData
  const route = getRoute('assets', 'endpoints', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const endPoint = await API.get2(route, params, dbg)
  logRes(fn, endPoint, dbg)
  return endPoint
}

const getEndPointsForCustomer = async (params, dbg = false) => {
  const fn = 'getEndPointsForCustomer'
  const route = getRoute('customer', 'assets/endpoints', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDevicesForCustomer = async (params, dbg = false) => {
  const fn = 'getDevicesForCustomer'
  const route = getRoute('customer', 'assets/devices', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getServiceEntitiesForCustomer = async (sc, serviceEntityId, dbg) => {
  const fn = 'getServiceEntitiesForCustomer'
  doLog(fn, { sc }, dbg)
  const params = { sc }
  if (serviceEntityId) params.serviceEntityId = serviceEntityId
  const route = getRoute('customer', 'assets/service-entities', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getMeters = async (meterId, deviceId, selectedContext, debug = false) => { // todo: remove?
  // todo: ensure selectedContext is provided
  meterId = typeof meterId === 'undefined' ? null : meterId
  deviceId = typeof deviceId === 'undefined' ? null : deviceId
  const requestContext = selectedContext
  let params // requestContext: requestContext }
  if (meterId) params = { meterId, debug }
  if (deviceId) params = { deviceId, debug }
  if (meterId && deviceId) params = { meterId, deviceId, debug }
  params.dataSource = getDataSource(debug)
  addToLog('veapi:getMeters:params: ' + JSON.stringify(params), debug)
  const response = await API.get('/meters/devicemetergrid', params, requestContext, 200, undefined, debug)
  addToLog('veapi:getMeters:response.Items: ' + JSON.stringify(response.Items), debug)
  return response.Items
}

const getEndPointsForContext = async (
  context,
  pageSize,
  exclusiveStartKey,
  endPointTypes,
  returnData,
  debug = false
) => {
  if (!pageSize) pageSize = 200
  const params = { debug }
  if (endPointTypes) params.endPointTypes = endPointTypes
  if (returnData) params.returnData = returnData
  addToLog(
    'veapi:getEndPointsForContext:params: ' + JSON.stringify(params),
    debug
  )
  const response = await API.get(
    '/endpoints',
    params,
    context,
    pageSize,
    exclusiveStartKey,
    debug
  )
  addToLog(
    'veapi:getEndPointsForContext:response.Items: ' +
    JSON.stringify(response.Items),
    debug
  )
  return response.Items // todo: review ? handle paging????
}

// const getEndPointsForSecurityContext = async (sc, limit, esk, epTypes, search, endPointId, deviceId, serviceEntityId, returnData, matchExact = false, dbg = false) => {
//   const fn = 'getEndPointsForSecurityContext'
//   if (!sc) return {}
//   if (typeof esk === 'object') esk = JSON.stringify(esk)
//   const params = {
//     sc,
//     limit,
//     exclusiveStartKey: esk,
//     endPointTypes: epTypes,
//     search,
//     returnData,
//     matchExact,
//     endPointId,
//     deviceId,
//     serviceEntityId
//   }
//   const route = getRoute('assets', 'endpoints', 'read', dbg)
//   logRouteParams(fn, route, params, dbg)
//   const res = await API.get2(route, params, dbg)
//   logRes(fn, res, dbg)
//   return res
// }

const getEndPointsForSecurityContextWithParams = async (params, dbg = false) => {
  // securityContext, limit, esk, epTypes, search, endPointId, deviceId, serviceEntityId, returnData, matchExact = false, dbg = false) => {
  const fn = 'getEndPointsForSecurityContextWithParams'
  const { securityContext, exclusiveStartKey } = params
  if (!securityContext) return {}
  if (typeof exclusiveStartKey === 'object') params.exclusiveStartKey = JSON.stringify(exclusiveStartKey)
  const route = getRoute('assets', 'endpoints', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getOcppConnectionsForSecurityContext = async (params, dbg = false) => {
  const fn = 'getOcppConnectionsForSecurityContext'
  const { securityContext, exclusiveStartKey } = params
  if (!securityContext) return {}
  if (typeof exclusiveStartKey === 'object') params.exclusiveStartKey = JSON.stringify(exclusiveStartKey)
  params.type = 'connections'
  const route = getRoute('assets', 'endpoints/ocpp', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getOcppNotificationsForSecurityContext = async (params, dbg = false) => {
  const fn = 'getOcppNotificationsForSecurityContext'
  const { securityContext, exclusiveStartKey } = params
  if (!securityContext) return {}
  if (typeof exclusiveStartKey === 'object') params.exclusiveStartKey = JSON.stringify(exclusiveStartKey)
  params.type = 'notifications'
  const route = getRoute('assets', 'endpoints/ocpp', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getOcppRequestsForSecurityContext = async (params, dbg = false) => {
  const fn = 'getOcppRequestsForSecurityContext'
  const { securityContext, exclusiveStartKey } = params
  if (!securityContext) return {}
  if (typeof exclusiveStartKey === 'object') params.exclusiveStartKey = JSON.stringify(exclusiveStartKey)
  params.type = 'requests'
  const route = getRoute('assets', 'endpoints/ocpp', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getOcppChargerStatus = async (params, dbg = false) => {
  const fn = 'getOcppChargerStatus'
  const { sc, chargePointId } = params
  if (!sc || !chargePointId) return {}
  params.type = 'status'
  const route = getRoute('assets', 'endpoints/ocpp', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const invokeOcppAction = async (params, dbg = false) => {
  const fn = 'invokeOcppAction'
  const { sc, action, chargePointId } = params
  if (!sc || !action || !chargePointId) return {}
  const route = getRoute('assets', 'endpoints/ocpp', 'invoke', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEndPointsStatus = async (sc, endPointIds, dbg = false) => {
  const fn = 'getEndPointStatus'
  const params = { sc, endPointIds }
  const route = getRoute('assets', 'endpoints/status', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getControlEndPointLogs = async (sc, endPointId, minTimestamp, dbg = false) => {
  const fn = 'getControlEndPointLogs'
  const params = { sc, endPointId }
  if (minTimestamp) params.minTimestamp = minTimestamp
  const route = getRoute('assets', 'endpoints/control-logs', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getControlEndPointLogsForCustomer = async (sc, endPointIds, minTimestamp, dbg = false) => {
  const fn = 'getControlEndPointLogsForCustomer'
  const params = { sc, endPointIds }
  if (minTimestamp) params.minTimestamp = minTimestamp
  const route = getRoute('customer', 'assets/endpoints/control-logs', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEndPointsOnline = async (sc, endPointIds, dbg = false) => {
  const fn = 'getEndPointsOnline'
  const params = { sc, endPointIds }
  const route = getRoute('assets', 'endpoints/online', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEndPointsOnlineForCustomer = async (sc, endPointIds, dbg = false) => {
  const fn = 'getEndPointsOnlineForCustomer'
  const params = { sc, endPointIds }
  const route = getRoute('customer', 'assets/endpoints/online', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDevicesOnline = async (sc, deviceIds, autoOta, dbg = false) => {
  const fn = 'getDevicesOnline'
  const params = { sc, deviceIds, autoOta }
  const route = getRoute('assets', 'devices/online', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDevicesOnlineForCustomer = async (sc, deviceIds, autoOta, dbg = false) => {
  const fn = 'getDevicesOnlineForCustomer'
  const params = { sc, deviceIds, autoOta }
  const route = getRoute('customer', 'assets/devices/online', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDevicesVersionToOta = async (sc, deviceIds, dbg = false) => {
  const fn = 'getDevicesVersionToOta'
  const params = { sc, deviceIds, getVersionToOta: true }
  const route = getRoute('assets', 'devices/online', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDevicesVersionToOtaForCustomer = async (sc, deviceIds, dbg = false) => {
  const fn = 'getDevicesVersionToOtaForCustomer'
  const params = { sc, deviceIds, getVersionToOta: true }
  const route = getRoute('customer', 'assets/devices/online', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDevicesForSecurityContext = async (params, dbg = false) => {
  const fn = 'getDevicesForSecurityContext'
  const { securityContext, exclusiveStartKey, online, offline } = params
  if (!securityContext) return {}
  if (!isUndefined(online) && online !== null && online !== '') params.online = online
  if (!isUndefined(offline) && offline !== null && offline !== '') params.offline = offline
  if (typeof exclusiveStartKey === 'object') params.exclusiveStartKey = JSON.stringify(exclusiveStartKey)
  const route = 'assets/devices/read'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDeviceIdsForMicrogridId = async (params, debug = false) => {
  const response = await API.get(
    '/meters/deviceIdsformicrogridid',
    params,
    undefined,
    300,
    undefined,
    debug
  )
  addToLog(
    'veapi:getDeviceIdsForMicrogridId:response: ' + JSON.stringify(response),
    debug
  )
  return response
}

const getDeviceData = async (deviceId, returnData, dbg = false) => { // todo: migrate to veauth
  const fn = 'getDeviceData'
  const params = { deviceId }
  if (returnData) params.returnData = returnData
  const route = '/device/data'
  logRouteParams(fn, route, params, dbg)
  const device = await API.get(route, params, undefined, 100, undefined)
  logRes(fn, device, dbg)
  return device
}

const updateEndPoint = async (updateEndPointParams, dbg = false) => {
  const fn = 'updateEndPoint'
  const { securityContext: sc } = updateEndPointParams
  const params = { updateEndPointParams }
  const route = getRoute('assets', 'endpoints', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const controlEndPoint = async (controlEndPointParams, dbg = false) => {
  const fn = 'controlEndPoint'
  const { securityContext: sc } = controlEndPointParams
  const params = { controlEndPointParams }
  const route = getRoute('assets', 'endpoints', 'control', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const createEndPoint = async (createEndPointParams, dbg = false) => {
  // hma
  const fn = 'createEndPoint'
  const { securityContext: sc } = createEndPointParams
  const params = { createEndPointParams }
  const route = getRoute('assets', 'endpoints', 'add', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateDevice = async (updateDeviceParams, dbg = false) => {
  const fn = 'updateDevice'
  const { securityContext } = updateDeviceParams
  const params = { updateDeviceParams }
  const route = getRoute('assets', 'devices', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { securityContext }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const createEndPoints = async (endPoints, debug = false) => {
  // todo: migrate to veauth
  // todo: remove, migrate Data / Import / EndPoints to veauth first
  const params = { endPoints, debug }
  addToLog('veapi:createEndPoints:params: ' + JSON.stringify(params), debug)
  const response = await API.put('/endpoints/create', null, params, debug)
  addToLog('veapi:createEndPoints:response: ' + JSON.stringify(response), debug)
  return response
}

// assumption: user with endPointSecurityContext access has access to contractSecurityContext // todo: review if needed
const updateEndPointAggregateByVeAuth = async (
  securityContext,
  contractId,
  contractAggregateBy,
  action = 'update',
  debug = false
) => {
  const params = { contractId, contractAggregateBy, action }
  const route = getRoute('assets', 'endpoint/aggregateby', 'update', debug) // 'assets/endpoint/aggregateby/update'
  addToLog(
    `updateEndPointAggregateByVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.put2(
    route,
    { securityContext },
    JSON.stringify(params),
    debug
  )
  addToLog(
    `updateEndPointAggregateByVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const deleteEndPoint = async (sc, dbg = false) => {
  const fn = 'deleteEndPoint'
  const params = { sc }
  const route = getRoute('assets', 'endpoints', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, params)
  logRes(fn, res, dbg)
  return res
}

const deleteDevice = async (sc, dbg = false) => {
  const fn = 'deleteDevice'
  const params = { sc }
  const route = getRoute('assets', 'devices', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, params)
  logRes(fn, res, dbg)
  return res
}

const removeServiceEntityIdFromEndPoint = async (
  endPointId,
  serviceEntityId,
  debug = false
) => {
  addToLog(
    `veapi:removeServiceEntityIdFromEndPoint:endPointId: ${endPointId}, serviceEntityId: ${serviceEntityId}`,
    debug
  )
  const params = { endPointId, serviceEntityId, debug }
  const response = await API.put(
    '/endpoints/serviceentity/remove',
    null,
    params,
    debug
  )
  addToLog(
    'veapi:removeServiceEntityIdFromEndPoint:response: ' +
    JSON.stringify(response),
    debug
  )
  return response
}

const diagnoseDevice = async (deviceId, param1, param2, param3, debug = false) => {
  const iotParams = { satAction: 'diagnoseDevice', deviceId, param1: param1 || '', param2: param2 || '', param3: param3 || '', debug }
  addToLog('diagnoseDevice:iotParams: ' + JSON.stringify(iotParams), debug)
  const response = await API.put('/dvi/iot', null, iotParams, debug)
  addToLog('diagnoseDevice:response: ' + JSON.stringify(response), debug)
  return response
}

const diagnoseDevices = async (deviceIds, param1, param2, param3, debug = false) => {
  const iotParams = { satAction: 'diagnoseDevices', deviceIds, param1: param1 || '', param2: param2 || '', param3: param3 || '', debug }
  addToLog('diagnoseDevices:iotParams: ' + JSON.stringify(iotParams), debug)
  const response = await callIotApi(iotParams, debug)
  addToLog('diagnoseDevices:response: ' + JSON.stringify(response), debug)
  return response
}

const setDeviceLog = async (deviceId, microgridId, enableParam, logLevelParam, dbg = false) => {
  const fn = 'setDeviceLog'
  const iotParams = { satAction: 'setDeviceLog', deviceId, microgridId, enableParam, logLevelParam }
  doLog(fn, { iotParams }, dbg)
  const res = await callIotApi(iotParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDeviceLogFromS3 = async (deviceId, s3KeyFolder, microgridId, dbg = false) => {
  const fn = 'getDeviceLogFromS3'
  // todo: add returnOnlyRecentDeviceLogs param
  const satAction = 'getDeviceLogFromS3'
  const params = { satAction, microgridId, deviceId, debug: dbg, s3KeyFolder }
  // e.g. vedaDeviceLog/IN-518390-0001_30aea4cac398/20200428T105545Z
  const route = '/meters/devicelog'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put(route, null, params, dbg)
  logRes(fn, res, dbg)
  const txtContent = hasProp(res, 'txtContent') ? res.txtContent : ''
  return txtContent
}

const getDeviceLogList = async (deviceId, microgridId, dbg = false) => {
  const fn = 'getDeviceLogList'
  // return list of log 'instances', e.g. vedaDeviceLog/IN-518390-0001_30aea4cac398/20200428T105545Z
  // each containing a series of small consecutive log files
  const satAction = 'getDeviceLogList'
  const params = { satAction, microgridId, deviceId, debug: dbg }
  const route = '/meters/deviceloglist'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put(route, null, params, dbg)
  logRes(fn, res, dbg)
  // const resPayload = res?.Payload ? JSON.parse(res.Payload) : res
  // const list = typeof resPayload.list === 'undefined' ? '' : resPayload.list
  const list = res?.list || res
  doLog(fn, { list })
  return list
}

const getDeviceStatusMsg = async (deviceId, s3KeyFolder, start, dbg = false) => {
  const fn = 'getDeviceStatusMsg'
  const params = { deviceId }
  if (typeof s3KeyFolder !== 'undefined') {
    params.s3KeyFolder = s3KeyFolder // e.g. raw/mqtt/status/yyyy/mm/dd/status_30aea4cac398_20200428[T105545Z.json]
  } else {
    params.start = start // 20200428[T105545Z.json]
  }
  const route = '/meter/statusmsg'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put(route, null, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDeviceStatusList = async (deviceId, start, dbg = false) => {
  const fn = 'getDeviceStatusList'
  // return list of status msg s3Keys, e.g. raw/mqtt/status/yyyy/mm/dd/status_30aea4cac398_20200428[T105545Z.json]
  const params = { deviceId, start }
  const route = '/meter/statuslist'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put(route, null, params, dbg)
  logRes(fn, res, dbg)
  const resPayload = hasProp(res, 'Payload') ? JSON.parse(res.Payload) : res
  const list = typeof resPayload.data === 'undefined' ? [] : resPayload.data
  return list
}

const getDeviceStatusListWithFilter = async (deviceId, start, filters = [], dbg = false) => {
  const fn = 'getDeviceStatusListWithFilter'
  // return list of status msg s3Keys, e.g. raw/mqtt/status/yyyy/mm/dd/status_30aea4cac398_20200428[T105545Z.json]
  const params = { deviceId, start, filters }
  const route = '/meter/statuslistfilter'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put(route, null, params, dbg)
  logRes(fn, res, dbg)
  const resPayload = hasProp(res, 'Payload') ? JSON.parse(res.Payload) : res
  const list = typeof resPayload.data === 'undefined' ? [] : resPayload.data
  return list
}

const getRecentDeviceStatusList = async (deviceId, dbg = false) => {
  const fn = 'getRecentDeviceStatusList'
  // return list of status msg s3Keys, e.g. raw/mqtt/status/yyyy/mm/dd/status_30aea4cac398_20200428[T105545Z.json]
  const params = { deviceId, s3Bucket: getDatalakeBucketName(vecfg, getVeEnvLoc(dbg), dbg) }
  const route = '/meter/recentstatuslist'
  const res = await API.put(route, null, params, dbg)
  logRes(fn, res, dbg)
  const resPayload = hasProp(res, 'Payload') ? JSON.parse(res.Payload) : res
  const list = typeof resPayload.list === 'undefined' ? '' : resPayload.list
  return { deviceStatusList: list }
}

const getRecentDeviceStatusListByAge = async (deviceId, ageInSeconds, dbg = false) => {
  const fn = 'getRecentDeviceStatusListByAge'
  // return list of status msg s3Keys, e.g. raw/mqtt/status/yyyy/mm/dd/status_30aea4cac398_20200428[T105545Z.json]
  const params = { deviceId, ageInSeconds }
  const route = '/meter/recentstatuslistbyage'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put(route, null, params, dbg)
  logRes(fn, res, dbg)
  return res.data || res
}

const getRecentDeviceStatusListByAgeWithFilter = async (deviceId, ageInSeconds, filters, dbg = false) => {
  const fn = 'getRecentDeviceStatusListByAgeWithFilter'
  // return list of status msg s3Keys, e.g. raw/mqtt/status/yyyy/mm/dd/status_30aea4cac398_20200428[T105545Z.json]
  const params = { deviceId, ageInSeconds, filters }
  const route = '/meter/recentstatuslistbyagefilter'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put(route, null, params, dbg)
  logRes(fn, res, dbg)
  return res.data || res
}

const isDeviceOnline = async (deviceId, dbg = false) => {
  const fn = 'isDeviceOnline'
  const params = { deviceId }
  const route = '/device/online'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get(route, params)
  logRes(fn, res, dbg)
  return res
}

const pingDevice = async (deviceId, returnRecentStatusMsg, dbg = false) => {
  const fn = 'pingDevice'
  const satAction = 'pingDevice'
  const params = { satAction, deviceId, returnRecentStatusMsg }
  const route = '/dvi/iot'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put(route, null, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const setWifiDvi = async (deviceId, wifiSsid, wifiPassword, debug = false) => {
  const iotParams = {
    satAction: 'setWifi',
    deviceId,
    param1: wifiSsid,
    param2: wifiPassword,
    debug
  }
  addToLog(`veapi:setWifiDvi:iotParams: ${JSON.stringify(iotParams)}`, debug)
  const response = await API.put('/dvi/iot', null, iotParams, debug)
  addToLog(`veapi:setWifiDvi:response: ${JSON.stringify(response)}`, debug)
  return response
}

const setWifi = async (deviceId, wifiSsid, wifiPassword, debug = false) => {
  const iotParams = {
    satAction: 'setWifi',
    deviceId,
    param1: wifiSsid,
    param2: wifiPassword,
    debug
  }
  addToLog(`veapi:setWifi:iotParams: ${JSON.stringify(iotParams)}`, debug)
  const response = await callIotAdminApi(iotParams, debug)
  addToLog(`veapi:setWifi:response: ${JSON.stringify(response)}`, debug)
  return response
}

const clearWifi = async (deviceId, debug = false) => {
  const iotParams = { satAction: 'clearWifi', deviceId, debug }
  addToLog(`veapi:clearWifi:iotParams: ${JSON.stringify(iotParams)}`, debug)
  const response = await callIotAdminApi(iotParams, debug)
  addToLog(`veapi:clearWifi:response: ${JSON.stringify(response)}`, debug)
  return response
}

const rebootDevice = async (deviceOrEndPointId, dbg = true) => {
  const fn = 'rebootDevice'
  const sc = 'VE/ASSETS'
  const satAction = 'rebootDevice'
  const params = { satAction, deviceOrEndPointId }
  const route = getRoute('assets', 'devices/reboot', 'invoke', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const rebootAndOtaDevice = async (deviceOrEndPointId, s3Key, dbg = true) => {
  const fn = 'rebootAndOtaDevice'
  const sc = 'VE/ASSETS'
  const satAction = 'rebootAndOtaDevice'
  const params = { satAction, deviceOrEndPointId, s3Key }
  const route = getRoute('assets', 'devices/reboot-and-ota', 'invoke', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const otaDevice = async (deviceId, microgridId, signedUrl, major, minor, patch, action, s3BucketName, s3Key, s3BucketAwsRegion, veEnvLoc, dbg = false) => {
  const fn = 'otaDevice'
  if (typeof microgridId === 'undefined') microgridId = 'nomg'
  const params = { satAction: 'otaDevice', deviceId, microgridId, major, minor, patch, action, veEnvLoc, debug: dbg }
  if (typeof signedUrl === 'undefined') {
    params.s3BucketName = s3BucketName
    params.s3Key = s3Key
    params.s3BucketAwsRegion = s3BucketAwsRegion
    params.signedUrlExpireSeconds = 120
  } else {
    params.signedUrl = signedUrl
  }
  doLog(fn, { params }, dbg)
  const res = await callIotAdminApi(params, dbg)
  logRes(fn, res, dbg)
  return res
}

const factoryResetDevice = async (deviceId, debug = false) => {
  const iotParams = { satAction: 'factoryResetDevice', deviceId, debug }
  addToLog(
    'veapi:factoryResetDevice:iotParams: ' + JSON.stringify(iotParams),
    debug
  )
  const response = await API.put('/dvi/iot', null, iotParams, debug)
  addToLog(
    'veapi:factoryResetDevice:response: ' + JSON.stringify(response),
    debug
  )
  return response
}

const updateDeviceSoftware = async (deviceId, debug = false) => {
  const iotParams = { satAction: 'updateDeviceSoftware', deviceId, debug }
  addToLog(
    'veapi:updateDeviceSoftware:iotParams: ' + JSON.stringify(iotParams),
    debug
  )
  const response = await API.put('/dvi/iot', null, iotParams, debug)
  addToLog(
    'veapi:updateDeviceSoftware:response: ' + JSON.stringify(response),
    debug
  )
  return response
}

// todo: refactor or remove setDevicesSourceMsg

const setDeviceSourceMsg = async (deviceId, meterIds, intervalInSeconds, msgCount, microgridId, dbg = false) => {
  const fn = 'setDeviceSourceMsg'
  const satAction = 'setDeviceSourceMsg'
  const action = 'app-svc-source-message'
  const iotParams = { satAction, deviceId, meterIds, microgridId, action, intervalInSeconds, msgCount }
  logRouteParams(fn, '', iotParams, dbg)
  const responseIot = await callIotApi(iotParams, dbg)
  logRes(fn, responseIot, dbg)
  return responseIot
}

const setDeviceSerialNumberVeAuth = async (deviceId, deviceSerialNumber, microgridId, dbg = false) => {
  const fn = 'setDeviceSerialNumber'
  const params = { action: 'setDeviceSerialNumber', deviceId, deviceSerialNumber, microgridId }
  doLog(fn, { params }, dbg)
  const res = await provisionDevice(params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDeviceProvisionInfo = async (idParams, dbg = false) => {
  const fn = 'getDeviceProvisionInfo' // todo: move to api
  logRouteParams(fn, '', idParams, dbg)
  const localCvmResponse = await getProvisionInfoForLocalEnvLoc(idParams, dbg)
  const veprovCvmResponse = await getProvisionInfoForProvEnvLoc(idParams, dbg)
  logRes(fn, { localCvmResponse, veprovCvmResponse }, dbg)
  return [localCvmResponse, veprovCvmResponse]
}

const getProvisionInfoForLocalEnvLoc = async (idParams, dbg = false) => {
  const fn = 'getProvisionInfoForLocalEnvLoc'
  const cvmResponse = await getProvisionInfoForEnvLoc(idParams, undefined, dbg)
  logRes(fn, cvmResponse, dbg)
  return cvmResponse
}

const getProvisionInfoForProvEnvLoc = async (idParams, dbg = false) => {
  const fn = 'getProvisionInfoForProvEnvLoc'
  const targetVeEnvLoc = 've-prov-in-1'
  const cvmResponse = await getProvisionInfoForEnvLoc(idParams, targetVeEnvLoc, dbg)
  logRes(fn, cvmResponse, dbg)
  return cvmResponse
}

const getProvisionInfoForEnvLoc = async (idParams, targetVeEnvLoc, dbg = false) => {
  const fn = 'getProvisionInfoForEnvLoc'
  doLog(fn, { idParams }, dbg)
  const action = 'getDeviceProvisionInfo'
  const provParams = { action, ...idParams, debug: dbg }
  if (targetVeEnvLoc) provParams.targetVeEnvLoc = targetVeEnvLoc
  const cvmResponse = await provisionDevice(provParams, dbg)
  logRes(fn, cvmResponse, dbg)
  return cvmResponse
}

const getDeviceStatus = async (id, returnData, dbg = false) => {
  const fn = 'getDeviceStatus'
  const deviceIdOrDeviceSerialNumber = id
  const sc = 'VE/ASSETS'
  const params = { sc, deviceIdOrDeviceSerialNumber, returnData }
  const route = getRoute('assets', 'devices/status', 'read')
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDeviceSoftAPWifiCredentialsForSerialNumber = async (deviceSerialNumber, dbg = false) => {
  const fn = 'getDeviceSoftAPWifiCredentialsForSerialNumber'
  const params = { deviceSerialNumber }
  const route = '/deviceserialnumber/wificredentials/softap'
  logRouteParams(fn, route, params, dbg)
  const credentials = await API.get(route, params)
  logRes(fn, credentials, dbg)
  return credentials
}

const saveWifiCredentials = async (deviceId, wifiSsid, wifiPassword, dbg = false) => {
  const fn = 'saveWifiCredentials'
  const params = { deviceId, wifiSsid, wifiPassword }
  const route = '/device/wificredentials'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put(route, null, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const provisionDevice = async (params, dbg = false) => {
  const fn = 'provisionDevice'
  const sc = 'VE/ASSETS'
  params.debug = true
  const route = getRoute('assets', 'devices/provision', '', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const moveDevice = async (params, dbg = false) => {
  const fn = 'moveDevice'
  params.useLambda = 'moveDevice'
  doLog(fn, { params }, dbg)
  return await provisionDevice(params, dbg)
}

const deleteOldCertificates = async (params, dbg = false) => {
  const fn = 'deleteOldCertificates'
  params.action = 'deleteOldCertificates'
  doLog(fn, { params }, dbg)
  return await provisionDevice(params, dbg)
}

const getNanoId = async (idLength = 6, upperCase = true, dbg = false) => {
  const fn = 'getNanoId'
  const params = { idLength, upperCase }
  const route = 'nanoid'
  logRouteParams(fn, route, params, dbg)
  const { nanoId } = await API.get3(route, params, dbg)
  logRes(fn, { nanoId }, dbg)
  return nanoId
}

const getSatVersionInfo = async (dbg = false) => {
  const fn = 'getSatVersionInfo'
  const params = {}
  const route = 'satversioninfo'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get3(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getVeXApiInfo = async (dbg = false) => {
  const fn = 'getVeXApiInfo'
  const params = {}
  const route = 'vexapiinfo'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get3(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateCurrentUserUserProfileSettings = async (
  updateParams,
  debug = false
) => {
  const fn = 'updateCurrentUserUserProfileSettings'
  logRes(fn, updateParams, debug)
  const params = { updateParams, debug }
  const route = getRoute('user', 'user-updateuserprofilesettings', '', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.put3(route, {}, params, debug)
  logRes(fn, res, debug)
  return res
}

const updateCurrentUserProviderName = async (providerName, debug = false) => {
  const fn = 'updateCurrentUserProviderName'
  const params = { providerName, debug }
  const route = getRoute('user', 'user-updateprovidername', '', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.put3(route, {}, params, debug)
  logRes(fn, res, debug)
  return res
}

const addUser = async ({ emailAddress, password, firstName, lastName, mobile, status, securityContext, userType = 'Cognito', satGroups }, dbg = true) => {
  const fn = 'addUser'
  dbg = true
  const params = { emailAddress, password, firstName, lastName, mobile, status, userType, satGroups }
  const route = getRoute('users', '', 'add', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { securityContext }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const resetUserPassword = async (params, dbg = false) => {
  const fn = 'resetUserPassword'
  dbg = true
  const sc = params.securityContext
  const route = getRoute('users', 'password', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const addUserApiKey = async (sc, dbg = true) => {
  const fn = 'addUserApiKey'
  dbg = true
  const params = { sc }
  const route = getRoute('users', 'api-key', 'add', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const isMemberOfSatGroup = async (satGroupId, dbg = false) => {
  const fn = 'isMemberOfSatGroup'
  const params = { satGroupId }
  const route = getRoute('user', 'user-group-is-member', '', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get3(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getGroupsForSecurityContext = async (sc, exactMatch = false, categorise = true, dbg = false) => {
  const fn = 'getGroupsForSecurityContext'
  if (!sc.includes('/')) sc += '/USERS'
  const params = { sc, exactMatch, categorise }
  const route = getRoute('users', 'groups', 'read', dbg) // 'users/groups/read'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getGroupForSecurityContextVeAuth = async (sc, dbg = false) => {
  const fn = 'getGroupForSecurityContextVeAuth'
  const exactMatch = true
  const categorise = false
  const groups = await getGroupsForSecurityContext(sc, exactMatch, categorise, dbg)
  doLog(fn, { groups }, dbg)
  return groups[0] || groups
}

const getCurrentUserGroupsForType = async (groupType = 'satUiGroups', dbg = false) => {
  const fn = 'getCurrentUserGroupsForType'
  const params = { groupType }
  const route = getRoute('user', 'user-groups-for-type', '', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get3(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const hasAssetManagementAccess = async (dbg = false) => {
  const fn = 'hasAssetManagementAccess'
  const satUiGroups = await getCurrentUserGroupsForType('satUiGroups', dbg)
  doLog(fn, { satUiGroups }, dbg)
  return satUiGroups.includes('Asset Management')
}

const getRolesForOwner = async (sc, owner = 'default', dbg = false) => {
  const fn = 'getRolesForOwner'
  const params = { sc, owner }
  const route = getRoute('users', 'roles/owner', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  let roles = await API.get2(route, params, dbg)
  logRes(fn, roles, dbg)
  roles = roles.map((role) => role.role || role)
  roles = getUniqueArray(roles.sort())
  logRes(fn, roles, dbg)
  return roles.sort()
}

const getRolesFunctionsMappingsForOwner = async (sc, owner = 'default', dbg = false) => {
  const fn = 'getRolesFunctionsMappingsForOwner'
  const params = { sc, owner }
  const route = getRoute('users', 'roles/functions', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res.roleFunction
}

const updateCurrentUserUserProfile = async (userProfile, satUserId, dbg = false) => {
  const fn = 'updateCurrentUserUserProfile'
  doLog(fn, { userProfile, satUserId }, dbg)
  if (typeof userProfile === 'undefined' || satUserId === '') return
  if (typeof userProfile === 'string') userProfile = JSON.parse(userProfile)
  if (typeof satUserId === 'string' && !hasProp(userProfile, 'username')) {
    userProfile.username = satUserId
  }
  if (userProfile.selectedMicrogridId === '') {
    userProfile.selectedMicrogridId = 'n/a'
  }
  doLog(fn, { userProfile }, dbg)
  setUserProfile(userProfile)
  const params = { userProfile }
  const route = getRoute('user', 'user-updateuserprofile', '', dbg) // 'user-updateuserprofile'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put3(route, {}, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateUserVeAuth = async (userParams, dbg = true) => {
  const fn = 'updateUserVeAuth'
  dbg = true
  const { securityContext: sc } = userParams.userProfile
  const params = { userParams }
  const route = getRoute('users', 'user', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateGroupVeAuth = async (groupParams, dbg = false) => {
  const fn = 'updateGroupVeAuth'
  const { securityContext } = groupParams
  const params = { groupParams }
  const route = getRoute('users', 'group', 'update', dbg) // 'users/group/update'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(
    route,
    { securityContext },
    JSON.stringify(params),
    dbg
  )
  logRes(fn, res, dbg)
  return res
}

const deleteGroup = async (sc, dbg = false) => {
  const fn = 'deleteGroup'
  const params = { sc }
  const route = getRoute('users', 'group', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, params)
  logRes(fn, res, dbg)
  return res
}

const deleteUser = async (sc, dbg = false) => {
  const fn = 'deleteUser'
  const params = { sc }
  const route = getRoute('users', '', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, params)
  logRes(fn, res, dbg)
  return res
}

const deleteUserApiKey = async (sc, dbg = false) => {
  const fn = 'deleteUser'
  const params = { sc }
  const route = getRoute('users', 'api-key', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, params)
  logRes(fn, res, dbg)
  return res
}

const getCurrentUserUserProfile = async (dbg = false) => {
  const fn = 'getCurrentUserUserProfile'
  const params = {}
  const route = getRoute('user', 'user-userprofile', '', dbg)
  logRouteParams(fn, route, params, dbg)
  const userProfile = await API.get3(route, params, dbg)
  logRes(fn, userProfile, dbg)
  const userProfileInSessionStorage = getUserProfile(dbg)
  if (!userProfileInSessionStorage) setUserProfile(userProfile)
  return userProfile
}

const getCurrentUserAccessProfile = async (dbg = false) => {
  const fn = 'getCurrentUserAccessProfile'
  const params = {}
  const route = getRoute('user', 'user-accessprofile', '', dbg)
  logRouteParams(fn, route, params, dbg)
  const accessProfile = await API.get3(route, params, dbg)
  logRes(fn, accessProfile, dbg)
  return accessProfile
}

const isCurrentUserSuperAdmin = async (dbg = false) => {
  const fn = 'isCurrentUserSuperAdmin'
  let isSuperAdmin = false // default
  try {
    const userProfileInSessionStorage = getUserProfile(dbg)
    doLog(fn, { userProfileInSessionStorage }, dbg)
    const { securityContext } = userProfileInSessionStorage
    doLog(fn, { securityContext }, dbg)
    if (!securityContext) return false
    const params = { securityContext, owner: 'VE' } // todo: review owner
    const route = getRoute('users', 'roles/superadmin', 'read', dbg)
    logRouteParams(fn, route, params, dbg)
    isSuperAdmin = await API.get2(route, params, dbg)
  } catch (e) {
    // ignore errors
  }
  doLog(fn, { isSuperAdmin }, dbg)
  return isSuperAdmin
}

const isCurrentUserUserAdmin = async (securityContext, dbg = false) => {
  const fn = 'isCurrentUserAdmin'
  let isUserAdmin = false // default
  try {
    if (!securityContext) {
      const userProfileInSessionStorage = getUserProfile(dbg)
      securityContext = userProfileInSessionStorage.securityContext
      if (!securityContext) return false
    }
    doLog(fn, { securityContext }, dbg)
    const params = { securityContext, owner: 'VE' } // todo: review owner
    const route = getRoute('users', 'roles/useradmin', 'read', dbg)
    logRouteParams(fn, route, params, dbg)
    isUserAdmin = await API.get2(route, params, dbg)
  } catch (e) {
    // ignore errors
  }
  doLog(fn, { isUserAdmin }, dbg)
  return isUserAdmin
}

const getUsersForGroupVeAuth = async (
  securityContext,
  returnData,
  debug = false
) => {
  const params = { securityContext, returnData, debug }
  const route = getRoute('users', 'group/users', 'read', debug) // 'users/group/users/read'
  addToLog(
    `getUsersForGroupVeAuth:route: ${route}, params: ${JSON.stringify(params)}`,
    debug
  )
  return await API.get2(route, params, debug)
}

const getUserForSecurityContext = async (sc, dbg = false) => {
  const fn = 'getUserForSecurityContext'
  const exactMatch = true
  const users = await getUsersForSecurityContext(sc, exactMatch, dbg)
  const res = users[0]
  logRes(fn, res, dbg)
  return res
}

const getUsersForSecurityContext = async (sc, exactMatch, dbg = false) => {
  const fn = 'getUsersForSecurityContext'
  const params = { sc, exactMatch }
  const route = getRoute('users', '', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getUserDataVeAuth = async (
  securityContext,
  returnData,
  debug = false
) => {
  const params = { securityContext, returnData, debug }
  const route = getRoute('users', 'user', 'read', debug)
  addToLog(
    `getUserDataVeAuth:route: ${route}, params: ${JSON.stringify(params)}`,
    debug
  )
  return await API.get2(route, params, debug)
}

const getUserChangeLog = async (sc, dbg = false) => {
  // SAT user
  const fn = 'getUserChangeLog'
  const params = { sc }
  const route = getRoute('users', 'changelog', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getNotificationsForSatUser = async (sc, limit, offset, messageType, template, fromDate, toDate, search, dbg = false) => {
  const fn = 'getNotificationsForSatUser'
  if (!sc) return
  const params = { sc, limit, offset, search, messageType, template, fromDate, toDate }
  const route = getRoute('users', 'notifications', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getNotificationsForCustomer = async (sc, limit, offset, messageType, template, fromDate, toDate, search, status, message, dbg = false) => {
  const fn = 'getNotificationsForCustomer'
  if (!sc) return
  const params = { sc, limit, offset, search, status, messageType, template, fromDate, toDate, message }
  const route = getRoute('customer', 'notifications', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteNotificationForCustomer = async (sc, notificationSecurityContext, dbg = false) => {
  const fn = 'deleteNotification'
  const params = { notificationSecurityContext }
  const route = getRoute('customer', 'notifications', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getNotificationsForSecurityContext = async (sc, limit, offset, messageType, template, fromDate, toDate, search, status, message, dbg = false) => {
  const fn = 'getNotificationsForSecurityContext'
  if (!sc) return
  const params = { sc, limit, offset, search, status, messageType, template, fromDate, toDate, message }
  const route = getRoute('customers', 'notifications', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const useGMC = (debug = false) => {
  return vebUseGMC(vecfg, debug)
}

const getDataSource = (debug = false) => {
  return vebGetDataSource(vecfg, debug)
}

const callLedgerApi = async (ledgerDataParams, debug = false) => {
  addToLog(
    'veapi:callLedgerApi:ledgerDataParams: ' + JSON.stringify(ledgerDataParams),
    debug
  )
  const response = await API.put('/ledger', null, ledgerDataParams, debug)
  addToLog('veapi:callLedgerApi:response: ' + JSON.stringify(response), debug)
  return response
}

const callIotApi = async (iotParams, dbg = false) => {
  const fn = 'callIotApi'
  iotParams.debug = dbg
  const route = '/iot'
  logRouteParams(fn, route, iotParams, dbg)
  const res = await API.put(route, null, iotParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const callIotAdminApi = async (iotParams, dbg = true) => {
  const fn = 'callIotAdminApi'
  iotParams.debug = dbg
  const route = '/iot/admin'
  logRouteParams(fn, route, iotParams, dbg)
  const res = await API.put(route, null, iotParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const testHmaApi = async (hmaParams, dbg = false) => {
  const fn = 'testHmaApi'
  const { securityContext: sc } = hmaParams
  const params = { hmaParams }
  const route = getRoute('hma', '', 'test', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const callHmaApi = async (hmaParams, dbg = false) => {
  const fn = 'callHmaApi'
  hmaParams.debug = dbg
  doLog(fn, { hmaParams }, dbg)
  const res = await API.put('/hma', null, hmaParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const callFbaApi = async (fbaParams, debug = false) => {
  fbaParams.debug = debug
  addToLog(`veapi:callFbaApi:fbaParams: ${JSON.stringify(fbaParams)}`, debug)
  const response = await API.put('/fba', null, fbaParams, debug)
  addToLog(`veapi:callFbaApi:response: ${JSON.stringify(response)}`, debug)
  return response
}

const addFbaOwnerContextVeAuth = async (sc, newOwnerContext, newActivationCode, dbg = false) => {
  const fn = 'addFbaOwnerContext'
  const params = { newOwnerContext, newActivationCode, debug: dbg }
  const route = getRoute('users', 'fba/owner-context', 'add', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getSourceMsg = async (start, deltaInSeconds, currentContext, ctxName, microgridIds, excludeVirtualDevices, returnData, dbg = false) => {
  const fn = 'getSourceMsg'
  const securityContext = 'VE/TELEMETRY/legacy'
  const params = {
    securityContext,
    satAction: 'getSourceMsg',
    start,
    deltaInSeconds,
    currentContext,
    ctxName,
    microgridIds,
    excludeVirtualDevices,
    returnData
  }
  const route = getRoute('telemetry', 'legacy/energy-data', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  const sourceMsg = res.sourceMsgs || res
  return sourceMsg
}

const exportEndPointSourceMsg = async (id, start, delta, rd, dbg = false) => {
  const fn = 'exportEndPointSourceMsg'
  if (!isNumeric(delta)) delta = 86400
  const sourceMsgs = await getEndPointSourceMsg(id, start, delta, rd, dbg)
  logRes(fn, sourceMsgs, dbg)
  const csvContent = await convertJsonToCsv(sourceMsgs)
  let filename = `endPoint_${id}_source_msg_${start}_${delta}_`
  filename += `${getIntervalTimeStamp()}.csv`
  download(csvContent, filename, 'text/csv;encoding:utf-8')
}

const getEndPointSourceMsg = async (sc, start, delta, rd, dbg = false) => {
  const fn = 'getEndPointSourceMsg'
  if (!delta) delta = 86400
  if (sc.split('/')[1] === 'ASSETS') sc = sc.replace(/ASSETS/, 'TELEMETRY')
  const ts = getIntervalTimeStamp()
  const defaultStart = dateAddShortISO(ts, 'second', -delta)
  if (!start) start = defaultStart
  if (start.length === 8) start = `${start}T000000Z`
  doLog(fn, { defaultStart, start, delta }, dbg)
  // returnData = ['start', 'meterId', 'microgridId', 'deviceType', 'energy', 'version', 'Vrms', 'Irms', 'f']
  // not including microgridId, state, build, interval, version (to avoid Function.ResponseSizeTooLarge error)
  const defaultReturnData = 'start,energy,p,pA,pB,pC,q,qA,qB,qC,v,vB,vC,Irms,f,re,pf,batSoc,batMode,batOpMode,pSolar,pInverter'
  if (!rd) rd = defaultReturnData
  const satAction = 'getMeterSourceMsg' // not used anymore
  const params = { sc, satAction, start, deltaInSeconds: delta, returnData: rd, debug: dbg }
  const route = getRoute('telemetry', 'endpoint/source-msg', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  const sourceMsgs = res.sourceMsgs || res
  const numericFields = 'interval:energy:re:p:pA:pB:pC:q:qA:qB:qC:f:pf:Irms:v:Vrms:vB:vC'.split(':')
  if (!Array.isArray(sourceMsgs)) return sourceMsgs
  sourceMsgs.forEach((sm) => {
    numericFields.forEach((k) => {
      if (typeof sm[k] === 'string' && isNumeric(sm[k])) {
        sm[k] = parseFloat(sm[k])
      }
    })
  })
  logRes(fn, sourceMsgs, dbg)
  return sourceMsgs
}

const exportEndPointsSourceMsg = async (sc, ids, start, delta, rd, dbg = false, endPoint = null) => {
  const fn = 'exportEndPointsSourceMsg'
  if (!isNumeric(delta)) delta = 86400
  let startDeltas = [{ start, delta }] // [{ start: '20210101T000000Z', delta: 3600 }]
  const dayInSeconds = 86400
  if (delta > dayInSeconds) {
    const days = parseInt(delta / dayInSeconds, 10)
    const remainder = delta % dayInSeconds
    const deltas = new Array(days).fill(dayInSeconds)
    if (remainder) deltas.push(remainder)
    doLog(fn, { days, remainder, deltas }, dbg)
    let dayCount = 0
    startDeltas = []
    for (const delta of deltas) {
      const thisStart = dayCount ? shortISOAddSeconds(start, dayCount * dayInSeconds) : start
      startDeltas.push({ start: thisStart, delta })
      dayCount++
    }
    doLog(fn, { startDeltas }, dbg)
  }
  const sourceMsgs = []
  for (const startDelta of startDeltas) {
    const { start, delta } = startDelta
    const res = await getEndPointsSourceMsg(sc, ids, start, delta, rd, dbg)
    sourceMsgs.push(...res)
  }
  if (endPoint?.type === 'battery' && Array.isArray(sourceMsgs)) {
    const batCap = parseFloat(endPoint?.connection?.batCap || endPoint?.batCap)
    if (isNumeric(batCap)) {
      sourceMsgs.forEach((msg) => {
        const batSoc = isNumeric(msg.batSoc) ? parseFloat(msg.batSoc) : (isNumeric(msg.soc) ? parseFloat(msg.soc) : null)
        if (isNumeric(batSoc)) {
          msg.storedEnergy = roundToTwo((batCap * batSoc) / 100)
        }
      })
    }
  }
  logRes(fn, sourceMsgs, dbg)
  const owner = sc.split('/')[0]
  const filename = `${owner}_endPoints_${ids[0]}_source_msg_${start}_${delta}_${getIntervalTimeStamp()}.csv`
  const csvContent = sourceMsgs?.length ? await convertJsonToCsv(sourceMsgs) : ''
  download(csvContent, filename, 'text/csv;encoding:utf-8')
}

const getEndPointsSourceMsg = async (sc, endPointIds, start, deltaInSeconds, returnData, dbg = false) => {
  const fn = 'getEndPointsSourceMsg'
  if (!deltaInSeconds) deltaInSeconds = 86400
  if (sc.split('/')[1] === 'ASSETS') sc = sc.replace(/ASSETS/, 'TELEMETRY')
  const ts = getIntervalTimeStamp()
  const defaultStart = dateAddShortISO(ts, 'second', -deltaInSeconds)
  if (!start) start = defaultStart
  if (start.length === 8) start = `${start}T000000Z`
  doLog(fn, { defaultStart, start, deltaInSeconds }, dbg)
  // not including microgridId, state, build, interval, version (to avoid Function.ResponseSizeTooLarge error)
  const defaultReturnData = 'start,endPointId,energy,p,pA,pB,pC,q,qA,qB,qC,v,vB,vC,Irms,f,re,pf,batSoc,batMode,batOpMode,pSolar,pInverter'
  if (!returnData) returnData = defaultReturnData // todo: remove meterId
  const params = { sc, endPointIds, start, deltaInSeconds, returnData, debug: dbg }
  const route = getRoute('telemetry', 'endpoints/source-msg', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  const sourceMsgs = res.sourceMsgs || res
  const numericFields = 'interval:energy:re:p:pA:pB:pC:q:qA:qB:qC:f:pf:Irms:v:vB:vC:pSolar:pInverter'.split(':')
  if (!Array.isArray(sourceMsgs)) return sourceMsgs
  sourceMsgs.forEach((sm) => {
    numericFields.forEach((k) => {
      if (typeof sm[k] === 'string' && isNumeric(sm[k])) sm[k] = parseFloat(sm[k])
    })
  })
  logRes(fn, sourceMsgs, dbg)
  return sourceMsgs
}

const getAllAggregatedEnergyAggregateByIds = async (dbg = false) => {
  const satAction = 'getAllAggregatedEnergyAggregateByIds'
  return await getVeCacheData(satAction, {}, dbg)
}

const getAllAggregateByIds = async (dbg = false) => {
  const satAction = 'getAllAggregateByIds'
  return await getVeCacheData(satAction, {}, dbg)
}

const getSrcMsgExpiryHashValue = async (dbg = false) => {
  const satAction = 'getSrcMsgExpiryHashValue'
  return await getVeCacheData(satAction, {}, dbg)
}

const getAllDynamicProfileIds = async (prefix, dbg = false) => {
  const satAction = 'getAllDynamicProfileIds'
  return await getVeCacheData(satAction, { prefix }, dbg)
}

const getDynamicProfileHashValue = async (dynamicProfileId, dbg = false) => {
  const satAction = 'getDynamicProfileHashValue'
  return await getVeCacheData(satAction, { dynamicProfileId }, dbg)
}

const getDynamicProfileHashFieldValue = async (dynamicProfileId, start, dbg = false) => {
  const satAction = 'getDynamicProfileHashFieldValue'
  return await getVeCacheData(satAction, { dynamicProfileId, start }, dbg)
}

const getDeviceTxnHashValue = async (microgridId, endPointId, localDate, dbg = false) => {
  const satAction = 'getDeviceTxnHashValue'
  return await getVeCacheData(satAction, { microgridId, endPointId, localDate }, dbg)
}

const getVecacheInfo = async (debug = false) => {
  const satAction = 'getVecacheInfo'
  return await getVeCacheData(satAction, {}, debug) // todo: convert to csv, e.g. replace \r\n, split(':') ???, add ui
}

const getPermanentKeyNames = async (dbg = false) => {
  const satAction = 'getPermanentKeyNames'
  return await getVeCacheData(satAction, {}, dbg)
}

const getSrcMsgEndPointIdsSetValue = async (dbg = false) => {
  const satAction = 'getSrcMsgEndPointIdsSetValue'
  return await getVeCacheData(satAction, {}, dbg)
}

const getDynamicProfileIdsSetValue = async (dbg = false) => {
  const satAction = 'getDynamicProfileIdsSetValue'
  return await getVeCacheData(satAction, {}, dbg)
}

const getEndPointIdsForAggregateById = async (aggregateById, dbg = false) => {
  const satAction = 'getEndPointIdsForAggregateById'
  return await getVeCacheData(satAction, { aggregateById }, dbg)
}

const addUpdateDynamicProfile = async (params, dbg = false) => {
  const fn = 'addUpdateDynamicProfile'
  const sc = 'VE/TELEMETRY'
  const satAction = 'addUpdateDynamicProfile'
  // { dynamicProfileId, start, value, count, interval, ref }
  const vecacheParams = { satAction, ...params }
  const route = getRoute('telemetry', 'vecache', 'update', dbg)
  logRouteParams(fn, route, vecacheParams, dbg)
  const res = await API.put2(route, { sc }, vecacheParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteDynamicProfile = async (dynamicProfileId, dbg = false) => {
  const fn = 'deleteDynamicProfile'
  const sc = 'VE/TELEMETRY'
  const satAction = 'deleteDynamicProfile'
  const params = { dynamicProfileId, satAction, debug: dbg }
  const route = getRoute('telemetry', 'vecache', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteAggregateById = async (aggregateById, dbg = false) => {
  const fn = 'deleteAggregateById'
  const sc = 'VE/TELEMETRY'
  const satAction = 'deleteAggregateById'
  const params = { aggregateById, satAction, debug: dbg }
  const route = getRoute('telemetry', 'vecache', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const setSrcMsgExpiry = async (endPointIds, expiry, dbg = false) => {
  const fn = 'setSrcMsgExpiry'
  const sc = 'VE/TELEMETRY'
  const satAction = 'setSrcMsgExpiry'
  const params = { endPointIds, satAction, expiry, debug: dbg }
  const route = getRoute('telemetry', 'vecache', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const completeContractEvent = async (params, dbg = false) => {
  const fn = 'completeContractEvent'
  const sc = 'VE/TELEMETRY'
  const satAction = 'completeContractEvent'
  const vecacheParams = { satAction, ...params }
  const route = getRoute('telemetry', 'vecache', 'update', dbg)
  logRouteParams(fn, route, vecacheParams, dbg)
  const res = await API.put2(route, { sc }, vecacheParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const addUpdateEventWindow = async (params, dbg = false) => {
  const fn = 'addUpdateEventWindow'
  const sc = 'VE/TELEMETRY'
  const satAction = 'addUpdateEventWindow'
  const vecacheParams = { satAction, ...params }
  const route = getRoute('telemetry', 'vecache', 'update', dbg)
  logRouteParams(fn, route, vecacheParams, dbg)
  const res = await API.put2(route, { sc }, vecacheParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEventWindowHashValue = async (dbg = false) => {
  const fn = 'getEventWindowHashValue'
  const sc = 'VE/TELEMETRY'
  const satAction = 'getEventWindowHashValue'
  const vecacheParams = { sc, satAction }
  const route = getRoute('telemetry', 'vecache', 'read', dbg)
  logRouteParams(fn, route, vecacheParams, dbg)
  const res = await API.get2(route, vecacheParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEventWindow = async (params, dbg = false) => {
  const fn = 'getEventWindow'
  const sc = 'VE/TELEMETRY'
  const satAction = 'getEventWindow'
  const vecacheParams = { sc, satAction, ...params }
  const route = getRoute('telemetry', 'vecache', 'read', dbg)
  logRouteParams(fn, route, vecacheParams, dbg)
  const res = await API.get2(route, vecacheParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteEventWindow = async (params, dbg = false) => {
  dbg = true
  const fn = 'deleteEventWindow'
  const sc = 'VE/TELEMETRY'
  const satAction = 'deleteEventWindow'
  const vecacheParams = { satAction, ...params }
  const route = getRoute('telemetry', 'vecache', 'delete', dbg)
  logRouteParams(fn, route, vecacheParams, dbg)
  const res = await API.delete2(route, { sc }, vecacheParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateIacConfigInVeCache = async (params, dbg = false) => {
  const fn = 'updateIacConfigInVeCache'
  const sc = 'VE/TELEMETRY'
  const satAction = 'updateIacConfig'
  const vecacheParams = { satAction, ...params }
  const route = getRoute('telemetry', 'vecache', 'update', dbg)
  logRouteParams(fn, route, vecacheParams, dbg)
  const res = await API.put2(route, { sc }, vecacheParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteIacConfigInVeCache = async (params, dbg = false) => {
  dbg = true
  const fn = 'deleteIacConfigInVeCache'
  const sc = 'VE/TELEMETRY'
  const satAction = 'deleteIacConfig'
  const vecacheParams = { satAction, ...params }
  const route = getRoute('telemetry', 'vecache', 'delete', dbg)
  logRouteParams(fn, route, vecacheParams, dbg)
  const res = await API.delete2(route, { sc }, vecacheParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateSecretInVeCache = async (params, dbg = false) => {
  const fn = 'updateSecretInVeCache'
  const sc = 'VE/TELEMETRY'
  const satAction = 'updateSecret'
  const vecacheParams = { satAction, ...params }
  const route = getRoute('telemetry', 'vecache', 'update', dbg)
  logRouteParams(fn, route, vecacheParams, dbg)
  const res = await API.put2(route, { sc }, vecacheParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteSecretInVeCache = async (params, dbg = false) => {
  dbg = true
  const fn = 'deleteSecretInVeCache'
  const sc = 'VE/TELEMETRY'
  const satAction = 'deleteSecret'
  const vecacheParams = { satAction, ...params }
  const route = getRoute('telemetry', 'vecache', 'delete', dbg)
  logRouteParams(fn, route, vecacheParams, dbg)
  const res = await API.delete2(route, { sc }, vecacheParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateAggregateByIds = async (epIds, aggIds, action, dbg = false) => {
  const fn = 'updateAggregateByIds'
  const sc = 'VE/TELEMETRY'
  const endPointIds = epIds
  const aggregateByIds = aggIds
  const updateAction = action || 'add'
  const params = { endPointIds, aggregateByIds, updateAction, debug: dbg }
  const route = getRoute('telemetry', 'vecache', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const doVeCacheHashMaintenance = async (satAction, dbg = false) => {
  const fn = 'doVeCacheHashMaintenance'
  dbg = true
  const sc = 'VE/TELEMETRY'
  const params = { satAction }
  const route = getRoute('telemetry', 'vecache', 'invoke', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const doAggregatedEnergyHashMaintenance = async (dbg = false) => {
  return await doVeCacheHashMaintenance('doAggregatedEnergyHashMaintenance', dbg)
}

const doSrcMsgEnergyHashMaintenance = async (dbg = false) => {
  return await doVeCacheHashMaintenance('doSrcMsgEnergyHashMaintenance', dbg)
}

const doContractEventInProgressHashMaintenance = async (dbg = false) => {
  return await doVeCacheHashMaintenance('doContractEventInProgressHashMaintenance', dbg)
}

const doContractEventCompletedHashMaintenance = async (dbg = false) => {
  return await doVeCacheHashMaintenance('doContractEventCompletedHashMaintenance', dbg)
}
const doMostRecentContractTransactionsHashMaintenance = async (dbg = false) => {
  return await doVeCacheHashMaintenance('doMostRecentContractTransactionsHashMaintenance', dbg)
}

const addPriceProfile = async (pricingProfile, type, start, price, count, dbg = false) => {
  const fn = 'addPriceProfile'
  doLog(fn, { pricingProfile, type, start, price, count }, dbg)
  const hasFutureStart = shortISOToUnixTimestamp(start) - getUnixTimeStamp() > 30
  if (!hasFutureStart) {
    doLog(fn, { start, info: "can't add past prices" }, true)
    return false
  }
  const params = { type, start, price, count }
  const sc = pricingProfile
  const route = getRoute('pricing', '', 'add', dbg) // 'pricing/add'
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deletePriceProfileVeAuth = async (pricingProfile, type, start, dbg = true) => {
  const fn = 'deletePriceProfile'
  const sc = pricingProfile
  const params = { type, start, debug: dbg }
  const route = getRoute('pricing', '', 'add', dbg) // 'pricing/delete'
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getPriceProfile = async (pricingProfile, start, dbg = false) => {
  const fn = 'getPriceProfile'
  const params = { satAction: 'getPriceProfile', securityContext: pricingProfile, start }
  const route = getRoute('pricing', '', 'read', dbg) // 'pricing/read'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getPrice = async (pricingProfile, start, dbg = false) => {
  const fn = 'getPrice'
  const params = { satAction: 'getPrice', securityContext: pricingProfile, start }
  const route = getRoute('pricing', '', 'read', dbg) // 'pricing/read'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getActivePriceProfileVeAuth = async (
  pricingProfile,
  start,
  debug = false
) => {
  addToLog(
    `veapi:getActivePriceProfileVeAuth:pricingProfile: ${pricingProfile}, start: ${start}`,
    debug
  )
  const params = {
    satAction: 'getActivePriceProfile',
    securityContext: pricingProfile,
    start
  }
  const route = getRoute('pricing', '', 'read', debug) // 'pricing/read'
  addToLog(
    `getActivePriceProfileVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(
    `veapi:getActivePriceProfileVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const getAllActivePricingProfilesVeAuth = async (owner, debug = false) => {
  addToLog(`veapi:getAllActivePricingProfilesVeAuth:owner: ${owner}`, debug)
  const params = {
    satAction: 'getAllActivePricingProfiles',
    securityContext: `${owner}/PRICING`,
    owner
  }
  const route = getRoute('pricing', '', 'read', debug) // 'pricing/read'
  addToLog(
    `getAllActivePricingProfilesVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(
    `veapi:getAllActivePricingProfilesVeAuth:response: ${JSON.stringify(
      response
    )}`,
    debug
  )
  return response
}

const getPriceProfileInfo = async (sc, start, dbg = false) => {
  const fn = 'getPriceProfileInfo'
  const params = { satAction: 'getPriceProfileInfo', sc, start }
  const route = getRoute('pricing', '', 'read', dbg) // 'pricing/read'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getVeCacheData = async (satAction, idParams, dbg = false) => {
  const fn = 'getVeCacheData'
  dbg = true
  const params = { satAction, securityContext: 'VE/TELEMETRY', ...idParams }
  const route = getRoute('telemetry', 'vecache', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getAllSrcMsgAggregatedEnergyHashKeys = async (debug = false) => {
  const satAction = 'getAllSrcMsgAggregatedEnergyHashKeys'
  return await getVeCacheData(satAction, {}, debug)
}

const getAllEnergyMsgAggregatedEnergyHashKeys = async (debug = false) => {
  const satAction = 'getAllEnergyMsgAggregatedEnergyHashKeys'
  return await getVeCacheData(satAction, {}, debug)
}

const getAllTxnContractAggregatedEnergyHashKeys = async (dbg = false) => {
  const satAction = 'getAllTxnContractAggregatedEnergyHashKeys'
  return await getVeCacheData(satAction, {}, dbg)
}

const getSrcMsgAggregatedEnergyHashValue = async (aggregateById, dbg = false) => {
  const satAction = 'getSrcMsgAggregatedEnergyHashValue'
  return await getVeCacheData(satAction, { aggregateById }, dbg)
}

const getEnergyMsgAggregatedEnergyHashValue = async (aggregateById, dbg = false) => {
  const satAction = 'getEnergyMsgAggregatedEnergyHashValue'
  return await getVeCacheData(satAction, { aggregateById }, dbg)
}

const getTxnContractAggregatedEnergyHashValue = async (aggregateById, dbg = false) => {
  const satAction = 'getTxnMsgAggregatedEnergyHashValue' // todo: review
  return await getVeCacheData(satAction, { aggregateById }, dbg)
}

const getSrcMsgEnergyHashValue = async (endPointId, dbg = false) => {
  const satAction = 'getSrcMsgEnergyHashValue'
  return await getVeCacheData(satAction, { endPointId }, dbg)
}

const getContractEventHashValue = async (contractEventId, dbg = false) => {
  const satAction = 'getContractEventHashValue'
  return await getVeCacheData(satAction, { contractEventId }, dbg)
}

const getContractEventsInProgressHashValue = async (dbg = false) => {
  const satAction = 'getContractEventsInProgressHashValue'
  const res = await getVeCacheData(satAction, {}, dbg)
  logRes('getContractEventsInProgressHashValue', res, true)
  return hasProp(res, 'energyData') ? res.energyData : res
}

const getContractEventsCompletedHashValue = async (dbg = false) => {
  const satAction = 'getContractEventsCompletedHashValue'
  const res = await getVeCacheData(satAction, {}, dbg)
  logRes('getContractEventsCompletedHashValue', res, true)
  return hasProp(res, 'energyData') ? res.energyData : res
}

const getMostRecentContractTransactionsHashValue = async (dbg = false) => {
  const satAction = 'getMostRecentContractTransactionsHashValue'
  const res = await getVeCacheData(satAction, {}, dbg)
  logRes('getMostRecentContractTransactionsHashValue', res, true)
  return hasProp(res, 'energyData') ? res.energyData : res
}

const getContractTransactionHashValue = async (contractTransactionId, dbg = false) => {
  const satAction = 'getContractTransactionHashValue'
  return await getVeCacheData(satAction, { contractTransactionId }, dbg)
}

const getContractTransactionsFromVeCache = async (contractTransactionIds, dbg = false) => {
  const satAction = 'getContractTransactions'
  return await getVeCacheData(satAction, { contractTransactionIds }, dbg)
}

const getMostRecentServiceEntitiesHashValue = async (dbg = false) => {
  const satAction = 'getMostRecentServiceEntitiesHashValue'
  return await getVeCacheData(satAction, {}, dbg)
}

const getMostRecentServiceEntity = async (serviceEntityId, dbg = false) => {
  const satAction = 'getMostRecentServiceEntitiesForIds'
  const serviceEntityIds = [serviceEntityId]
  const res = await getVeCacheData(satAction, { serviceEntityIds }, dbg)
  return Array.isArray(res) ? res[0] : res
}

const updateServiceEntityInVeCache = async (params, dbg = false) => {
  const fn = 'updateServiceEntityInVeCache'
  const sc = 'VE/TELEMETRY'
  const satAction = 'updateServiceEntity'
  const vecacheParams = { satAction, ...params }
  const route = getRoute('telemetry', 'vecache', 'update', dbg)
  logRouteParams(fn, route, vecacheParams, dbg)
  const res = await API.put2(route, { sc }, vecacheParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteServiceEntityInVeCache = async (params, dbg = false) => {
  dbg = true
  const fn = 'deleteServiceEntityInVeCache'
  const sc = 'VE/TELEMETRY'
  const satAction = 'deleteServiceEntity'
  const vecacheParams = { satAction, ...params }
  const route = getRoute('telemetry', 'vecache', 'delete', dbg)
  logRouteParams(fn, route, vecacheParams, dbg)
  const res = await API.delete2(route, { sc }, vecacheParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const getIacConfigsHashValue = async (dbg = false) => {
  const satAction = 'getIacConfigsHashValue'
  return await getVeCacheData(satAction, {}, dbg)
}

const getIacConfig = async (iacConfigName, dbg = false) => {
  const satAction = 'getIacConfig'
  return await getVeCacheData(satAction, { iacConfigName }, dbg)
}

const getSecretsHashValue = async (dbg = false) => {
  const satAction = 'getSecretsHashValue'
  return await getVeCacheData(satAction, {}, dbg)
}

const getSecret = async (secretName, dbg = false) => {
  const satAction = 'getSecret'
  return await getVeCacheData(satAction, { secretName }, dbg)
}

const publishPrice = async (
  dynamicPrice,
  durationInSeconds,
  deviceId,
  microgridId,
  topic,
  target,
  debug = false
) => {
  let msg =
    'dynamicPrice: ' +
    dynamicPrice +
    ', durationInSeconds: ' +
    durationInSeconds
  msg +=
    ', deviceId: ' +
    deviceId +
    ', microgridId: ' +
    microgridId +
    ', topic: ' +
    topic +
    ', target: ' +
    target
  addToLog('veapi:publishPrice:' + msg, debug)
  const iotParams = {
    satAction: 'publishPrice',
    deviceId: typeof deviceId === 'undefined' ? '' : deviceId,
    dynamicPrice: parseFloat(dynamicPrice),
    durationInSeconds: parseInt(durationInSeconds, 10),
    microgridId: microgridId || target,
    topic,
    target,
    debug
  }
  addToLog('veapi:publishPrice:iotParams: ' + JSON.stringify(iotParams), debug)
  const response = await callIotAdminApi(iotParams, debug)
  addToLog('veapi:publishPrice:response: ' + JSON.stringify(response), debug)
  return response
}

const getContextDataVeAuth = async (
  start,
  deltaInSeconds,
  currentContext,
  ctxName,
  microgridIds,
  excludeVirtualDevices,
  useVecache,
  debug = false
) => {
  const securityContext = 'VE/TELEMETRY/legacy'
  const params = {
    securityContext,
    satAction: 'getContextData',
    start,
    deltaInSeconds,
    currentContext,
    ctxName,
    microgridIds,
    excludeVirtualDevices,
    useVecache
  }
  const route = getRoute('telemetry', 'legacy/energy-data', 'read', debug) // 'telemetry/legacy/energy-data/read'
  addToLog(
    `getContextDataVeAuth:route: ${route}, params: ${JSON.stringify(params)}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(`getContextDataVeAuth:response: ${JSON.stringify(response)}`, debug)
  return response
}

const getEndPointEnergy = async (
  sc,
  fromDate,
  toDate,
  returnData,
  splitExportImport = false,
  actualOrForecast = 'A',
  dbg = false
) => {
  const fn = 'getEndPointEnergy'
  doLog(
    fn,
    { sc, fromDate, toDate, returnData, splitExportImport, actualOrForecast },
    dbg
  )
  if (!returnData) returnData = 'alignedHourlyRead'
  if (sc.split('/')[1] === 'ASSETS') sc = sc.replace(/ASSETS/, 'TELEMETRY')
  const params = {
    sc,
    fromDate,
    toDate,
    returnData,
    splitExportImport,
    actualOrForecast
  }
  const route = getRoute('telemetry', 'endpoints/energy-data', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEndPointsEnergyChartData = async (
  sc,
  endPointIds,
  fromDate,
  toDate,
  returnData,
  splitExportImport = false,
  actualOrForecast = 'A',
  tzOffset,
  dbg = false
) => {
  const fn = 'getEndPointsEnergyChartData'
  doLog(
    fn,
    {
      sc,
      endPointIds,
      fromDate,
      toDate,
      returnData,
      splitExportImport,
      actualOrForecast,
      tzOffset
    },
    dbg
  )
  if (!returnData) returnData = 'alignedHourlyRead'
  if (sc.split('/')[1] === 'ASSETS') sc = sc.replace(/ASSETS/, 'TELEMETRY')
  const params = {
    sc,
    endPointIds,
    fromDate,
    toDate,
    returnData,
    splitExportImport,
    actualOrForecast,
    tzOffset
  }
  const route = getRoute('telemetry', 'endpoints/chart-data', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const exportEndPointEnergy = async (sc, fromDate, toDate, returnData, splitExportImport = false, actualOrForecast = 'A', dbg = false) => {
  const fn = 'exportEndPointEnergy'
  const energyData = []
  const localDates = getDateRange(fromDate, toDate)
  doLog(fn, { localDates }, dbg)
  for (const localDate of localDates) {
    doLog(fn, { localDate }, dbg)
    try {
      const thisEnergyData = await getEndPointEnergy(sc, localDate, localDate, returnData, splitExportImport, actualOrForecast, dbg)
      energyData.push(...thisEnergyData)
    } catch (e) {
      doLog(fn, { error: e }, true)
    }
  }
  doLog(fn, { energyData }, dbg)
  const csvContent = await convertJsonToCsv(energyData)
  doLog(fn, { csvContent }, dbg)
  const endPointId = sc.split('/').slice(-1)[0]
  let filename = `endPoint_${endPointId}_energy_`
  filename += `${localDates[0]}_to_${localDates.slice(-1)[0]}_`
  filename += `${getIntervalTimeStamp()}.csv`
  download(csvContent, filename, 'text/csv;encoding:utf-8')
  return { filename, csvContent }
}

const getAggregatedEnergyVeAuth = async (
  microgridId,
  localStart,
  localEnd,
  debug = false
) => {
  const tz = getTimezoneFromMicrogridId(microgridId)
  if (typeof localStart === 'undefined') {
    localStart = dateAddShortISOLocal(
      getMicrogridTimeStamp(microgridId),
      'hour',
      -0.5,
      tz
    )
  }
  const securityContext = 'VE/TELEMETRY/legacy'
  const params = {
    securityContext,
    satAction: 'getAggregatedEnergy',
    microgridId,
    localStart,
    localEnd
  }
  const route = getRoute('telemetry', 'legacy/energy-data', 'read', debug) // 'telemetry/legacy/energy-data/read'
  addToLog(
    `getAggregatedEnergyVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(
    `getAggregatedEnergyVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const getMeterDailyTotalEnergyVeAuth = async (
  meterIds,
  microgridId,
  localDate,
  debug = false
) => {
  addToLog(
    `veapi:getMeterDailyTotalEnergyVeAuth:meterIds: ${JSON.stringify(
      meterIds
    )}, microgridId: ${microgridId}, localDate: ${localDate}`,
    debug
  )
  const securityContext = 'VE/TELEMETRY/legacy'
  const params = {
    securityContext,
    satAction: 'getMeterDailyTotalEnergy',
    meterIds,
    microgridId,
    localDate
  }
  const route = getRoute('telemetry', 'legacy/energy-data', 'read', debug) // 'telemetry/legacy/energy-data/read'
  addToLog(
    `getMeterDailyTotalEnergyVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(
    `getMeterDailyTotalEnergyVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const updateMonthlyTxnForEndPointIds = async (ids, localDate, dbg = false) => {
  const fn = 'updateMonthlyTxnForEndPointIds'
  const sc = 'VE/TELEMETRY/legacy'
  const satAction = 'updateMonthlyTxnForEndPointIds'
  const params = { satAction, endPointIds: ids, localDate }
  const route = getRoute('telemetry', 'legacy', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const regenTxnFromS3 = async (endPointIds, localDates, dbg = false) => {
  const fn = 'regenTxnFromS3'
  const sc = 'VE/TELEMETRY/legacy'
  const satAction = 'processTxnFromS3ForEndPointIds'
  const route = getRoute('telemetry', 'legacy', 'update', dbg)
  const batchSize = 3
  const endPointIdBatches = splitArray(endPointIds, batchSize)
  const allResults = []
  for (const epBatch of endPointIdBatches) {
    for (const localDate of localDates) {
      const params = { satAction, endPointIds: epBatch, localDates: [localDate] }
      doLog(fn, { epBatch, localDate }, dbg)
      logRouteParams(fn, route, params, dbg)
      try {
        const res = await API.put2(route, { sc }, params, dbg)
        logRes(fn, res, dbg)
        allResults.push(res)
      } catch (e) {
        doLog(fn, { error: e, epBatch, localDate }, true)
        allResults.push({ error: e.message, endPointIds: epBatch, localDate })
      }
    }
  }
  return allResults
}

const getDeviceRawTxn = async (
  microgridId,
  meterId,
  localDate,
  removeDuplicates,
  dbg = false
) => {
  const fn = 'getDeviceRawTxn'
  doLog(fn, { meterId, microgridId, localDate }, dbg)
  const sc = 'VE/TELEMETRY/legacy'
  const satAction = 'getDeviceRawTxn'
  const params = {
    sc,
    satAction,
    meterId,
    microgridId,
    localDate,
    removeDuplicates
  }
  const route = getRoute('telemetry', 'legacy/energy-data', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEndPointRawTxn = async (
  microgridId,
  endPointId,
  localDate,
  removeDuplicates,
  dbg = false
) => {
  const fn = 'getEndPointRawTxn'
  doLog(fn, { endPointId, microgridId, localDate }, dbg)
  const sc = 'VE/TELEMETRY/legacy'
  const satAction = 'getEndPointRawTxn'
  const params = {
    sc,
    satAction,
    endPointId,
    microgridId,
    localDate,
    removeDuplicates
  }
  const route = getRoute('telemetry', 'legacy/energy-data', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getSignedUrl = async (params, debug = false) => {
  addToLog(`veapi:getSignedUrl:params: ${JSON.stringify(params)}`, debug)
  params.satAction = 'getSignedUrl'
  const url = await callIotApi(params, debug)
  addToLog(`veapi:getSignedUrl:url: ${url}`, debug)
  return url
}

const getCsvFromS3VeAuth = async (s3Key, s3Bucket, debug = false) => {
  addToLog(
    `veapi:getCsvFromS3VeAuth:s3Key: ${s3Key}, s3Bucket: ${s3Bucket}`,
    debug
  )
  const securityContext = 'VE/TELEMETRY/legacy'
  const params = {
    securityContext,
    satAction: 'getCsvFromS3',
    s3Key,
    s3Bucket: getDatalakeBucketName(vecfg, getVeEnvLoc(debug), debug)
  }
  const route = getRoute('telemetry', 'legacy/energy-data', 'read', debug) // 'telemetry/legacy/energy-data/read'
  addToLog(
    `getCsvFromS3VeAuth:route: ${route}, params: ${JSON.stringify(params)}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(`getCsvFromS3VeAuth:response: ${JSON.stringify(response)}`, debug)
  const responsePayload = response.Payload || response
  const csvContent =
    typeof responsePayload.csvContent === 'undefined'
      ? ''
      : responsePayload.csvContent
  return csvContent
}

const getTxtFromS3 = async (s3Key, s3Bucket, dbg = false) => {
  const fn = 'getTxtFromS3'
  doLog(fn, { s3Key, s3Bucket }, dbg)
  const securityContext = 'VE/TELEMETRY/legacy'
  const params = {
    securityContext,
    satAction: 'getTxtFromS3',
    s3Key,
    s3Bucket: getDatalakeBucketName(vecfg, getVeEnvLoc(dbg), dbg)
  }
  const route = getRoute('telemetry', 'legacy/energy-data', 'read', dbg) // 'telemetry/legacy/energy-data/read'
  logRouteParams(route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  const resPayload = res.Payload || res
  return typeof resPayload.txtContent === 'undefined' ? '' : resPayload.txtContent
}

const putTxtToS3VeAuth = async (
  s3Key,
  s3Bucket,
  text,
  contentType,
  debug = false
) => {
  const securityContext = 'VE/TELEMETRY/legacy'
  if (!s3Bucket) s3Bucket = getDatalakeBucketName(vecfg, getVeEnvLoc(debug))
  const params = { satAction: 'putTxtToS3', s3Key, s3Bucket, text, contentType }
  const route = getRoute('telemetry', 'legacy', 'update', debug) // 'telemetry/legacy/update'
  addToLog(
    `putTxtToS3VeAuth:route: ${route}, params: ${JSON.stringify(params)}`,
    debug
  )
  const response = await API.put2(
    route,
    { securityContext },
    JSON.stringify(params),
    debug
  )
  addToLog(`putTxtToS3VeAuth:response: ${JSON.stringify(response)}`, debug)
  const responsePayload = response.Payload || response
  return responsePayload
}

const putBinToS3VeAuth = async (
  s3Key,
  s3Bucket,
  blob,
  contentType,
  debug = false
) => {
  const securityContext = 'VE/TELEMETRY/legacy'
  if (!s3Bucket) s3Bucket = getDatalakeBucketName(vecfg, getVeEnvLoc(debug))
  const params = { satAction: 'putBinToS3', s3Key, s3Bucket, blob, contentType }
  const route = getRoute('telemetry', 'legacy', 'update', debug) // 'telemetry/legacy/update'
  addToLog(
    `putBinToS3VeAuth:route: ${route}, params: ${JSON.stringify(params)}`,
    debug
  )
  const response = await API.put2(
    route,
    { securityContext },
    JSON.stringify(params),
    debug
  )
  addToLog(`putBinToS3VeAuth:response: ${JSON.stringify(response)}`, debug)
  const responsePayload = response.Payload || response
  return responsePayload
}

// new event handler Api
const getAllEventDefinitions = async (sc, eventDefId, fromDate, toDate, type, metricType, dbLayer, limit, offset, status, search, projection, dbg = false) => {
  const fn = 'getAllEventDefinitions'
  const params = { sc, fromDate, toDate, type, metricType, dbLayer, limit, offset, status, search, projection }
  if (status) params.status = status
  if (eventDefId) params.eventDefId = eventDefId
  const route = getRoute('events', 'definitions', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  return await API.get2(route, params, dbg)
}

const addEventDefinition = async (eventDefId, name, status, description, type, metricType, sc, criteria, properties, aggregatedProperties, dbLayer, windowEvents, actions, dbg = false) => {
  dbg = true
  const fn = 'addEventDefinition'
  doLog(fn, { eventDefId, name, status, description }, dbg)
  if (typeof status === 'undefined') status = 'draft'
  if (typeof description !== 'string') description = description.join('\n') || ''
  if (!sc.endsWith(eventDefId)) sc += '/' + eventDefId
  const createdBy = await ve.amplify.getCognitoEmail() // todo: review, set in ve-sat-api?
  const createdByRole = 'n/a' // todo: add role?
  addToLog(fn, { createdBy, createdByRole }, dbg)
  const params = { eventDefId, status, description, type, metricType, sc, criteria, properties, aggregatedProperties, dbLayer, windowEvents, actions, name }
  const keys = 'criterial,properties,aggregatedProperties,windowEvents,actions'
  for (const k of keys.split(',')) {
    const paramType = typeof params[k]
    if (params[k] && paramType === 'string') params[k] = JSON.parse(params[k])
  }
  const route = getRoute('events', 'definitions', 'add', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateEventDefinitionParams = async (sc, id, params, dbg = false) => {
  const fn = 'updateEventDefinitionParams'
  if (sc.endsWith('/')) sc = sc.slice(0, -1)
  const { eventDefId: eDefid } = params
  if (eDefid && !sc.endsWith(eDefid)) sc += `/${eDefid}`
  params = sanitiseEventDefinitionParams(params, dbg)
  const route = getRoute('events', 'definitions', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const sanitiseEventDefinitionParams = (params, dbg = false) => {
  const fn = 'sanitiseEventDefinitionParams'
  doLog(fn, { params }, dbg)
  let validKeys = 'eventDefId,type,metricType,criteria,properties'
  validKeys += ',aggregatedProperties,actions,name,status,description'
  validKeys += ',dbLayer,securityContext,windowEvents'
  validKeys = validKeys.split(',')
  const sanitisedParams = {}
  for (const key of validKeys) {
    if (!hasProp(params, key)) continue
    switch (key) {
      case 'criteria':
      case 'properties':
      case 'aggregatedProperties':
      case 'actions':
      case 'windowEvents':
        sanitisedParams[key] =
          typeof params[key] === 'string'
            ? JSON.parse(params[key])
            : params[key]
        break
      case 'termsAndConditions':
      case 'description':
        sanitisedParams[key] =
          params[key] && typeof params[key] !== 'string'
            ? params[key].join('\n')
            : params[key]
        break
      default:
        sanitisedParams[key] = params[key]
    }
  }
  doLog(fn, { sanitisedParams }, dbg)
  return sanitisedParams
}

const getEventDefinition = async (securityContext, dbg = false) => {
  const fn = getEventDefinition.name
  const params = { securityContext }
  const route = getRoute('events', 'definitions', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg) // { event_definition: [], event_definition_aggregate: x }
  return res
}

const getEvents = async (sc, params, dbg = false) => {
  const fn = 'getEvents'
  doLog(fn, { sc, params }, dbg)
  params = { ...params, sc }
  const route = getRoute('events', 'events', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEventMetrics = async (sc, keys, dbg = false) => {
  const fn = 'getEventMetrics'
  doLog(fn, { sc, keys }, dbg)
  const params = { keys, sc }
  const route = getRoute('events', 'metrics', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getAssetsMetricsList = async (params, dbg = false) => {
  const fn = 'getAssetsMetricsList'
  const route = getRoute('assets', 'metrics/list', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getMetricsListWithFilter = async (sc, start, filters, dbg = false) => {
  const fn = 'getMetricsListWithFilter'
  const params = { sc, start, filters, satAction: 'getMetricsListWithFilter' }
  doLog(fn, { params }, dbg)
  return await getAssetsMetricsList(params, dbg)
}

const getRecentMetricsListByAgeWithFilter = async (sc, ageInSeconds, filters, dbg = false) => {
  const fn = 'getRecentMetricsListByAgeWithFilter'
  const params = { sc, ageInSeconds, filters, satAction: 'getRecentMetricsListByAgeWithFilter' }
  doLog(fn, { params }, dbg)
  return await getAssetsMetricsList(params, dbg)
}

const getAllEventActions = async (sc, params, dbg = false) => {
  const fn = 'getAllEventActions'
  doLog(fn, { sc, params }, dbg)
  params = { ...params, sc }
  const route = getRoute('events', 'actions', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const downloadEventMetrics = async (sc, eventId, s3Keys, returnData = 'csv', dbg = false) => { // pre-2024 event handler
  const fn = 'downloadEventMetrics'
  doLog(fn, { todo: 'migrate to new event handler', sc, eventId, s3Keys, returnData }, dbg)
}

const submitMetric = async (metric, dbg = false) => {
  const fn = 'submitMetric'
  doLog(fn, { todo: 'migrate to new event handler', metric }, dbg)
}

const submitDpChange = async (metric, debug = false) => {
  addToLog('veapi:submitDpChange:metric: ' + JSON.stringify(metric), debug)
  const response = await API.put('/dpe/dpchange', null, metric, debug)
  addToLog('veapi:submitDpChange:response: ' + JSON.stringify(response), debug)
  return response
}

const getDynamicPrice = async (targetId, type, debug = false) => {
  const params = { targetId, debug }
  if (type) params.type = type
  addToLog('veapi:getDynamicPrice:params: ' + JSON.stringify(params), debug)
  const response = await API.get('/dpe/dynamicprice', params, undefined, 500, undefined)
  addToLog('veapi:getDynamicPrice:response: ' + JSON.stringify(response), debug)
  return response
}

const getPostcodeCoords = async (postcode, debug = false) => {
  const params = { postcode, debug }
  addToLog(`veapi:getPostcodeCoords:params: '${JSON.stringify(params)}`, debug)
  const response = await API.get(
    '/dpe/postcodecoords',
    params,
    undefined,
    500,
    undefined
  )
  addToLog(
    `veapi:getPostcodeCoords:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const sendTxtMessage = async (message, meterIds, phoneNumbers, dbg = false) => {
  const fn = 'sendTxtMessage'
  doLog(fn, { message, meterIds, phoneNumbers }, dbg)
  /*
  {"meterIds": ["0vY3nvvp"],
  "message": "మీ విలేజ్ ఎనర్జీ రాయితీలు చెల్లింపు మారుతుంది. ఇప్పటి నుండి మీ రాయితీలు సాయంత్రం జమ చేయబడతాయి. కాబట్టి మీ తదుపరి రాయితీ రేపు సాయంత్రం జమ అవుతుంది."}
  or
  {"microgridId": "AU-6003-0001",
  "message": "Hi {user.name} of {user.mobile}, your meter {meter.meterId} has a balance of {meter.balance}{meter.currency}"}
  or
  {"microgridId": "AU-6003-0001",
  "message": "Hi {user.name} of {user.mobile}, your meter {meter.meterId} has a balance of {meter.balance}{meter.currency}"}
  */
  if (typeof message === 'undefined' || message.length === 0 || (typeof meterIds === 'undefined' && typeof phoneNumbers === 'undefined')) return
  const params = { message, debug: dbg }
  if (Array.isArray(meterIds)) params.meterIds = meterIds
  if (Array.isArray(phoneNumbers)) params.phoneNumbers = phoneNumbers
  const route = '/smsbroadcast'
  logRouteParams(fn, route, params, dbg)
  const res = await API.put(route, null, params, dbg)
  logRes(fn, res, dbg)
  return res // { statusCode: 200 }
}

const notifyCustomer = async (sc, msg, msgTitle, method, userIds, phoneNumbers, microgridId, deeplinkingOptions, dryRun = false, contentAvailable = null, template, payload, selection, dbg = false) => {
  const fn = 'notifyCustomer'
  doLog(fn, { sc, msg, msgTitle, method, phoneNumbers, userIds }, dbg)
  /*
  {"meterIds": ["0vY3nvvp"],
  "message": "మీ విలేజ్ ఎనర్జీ రాయితీలు చెల్లింపు మారుతుంది. ఇప్పటి నుండి మీ రాయితీలు సాయంత్రం జమ చేయబడతాయి. కాబట్టి మీ తదుపరి రాయితీ రేపు సాయంత్రం జమ అవుతుంది."}
  or
  {"microgridId": "AU-6003-0001",
  "message": "Hi {user.name} of {user.mobile}, your meter {meter.meterId} has a balance of {meter.balance}{meter.currency}"}
  or
  {"microgridId": "AU-6003-0001",
  "message": "Hi {user.name} of {user.mobile}, your meter {meter.meterId} has a balance of {meter.balance}{meter.currency}"}
  */
  if (isUndefined(msg) || msg.length === 0) return
  if (isUndefined(phoneNumbers) && isUndefined(userIds) && isUndefined(selection)) return
  const message = msg
  const messageTitle = msgTitle
  const params = { message, messageTitle, method, contentAvailable }
  if (dbg) params.debug = dbg
  if (Array.isArray(userIds) && userIds.length) params.userIds = userIds
  if (microgridId) params.microgridId = microgridId
  if (Array.isArray(phoneNumbers)) params.phoneNumbers = phoneNumbers
  if (deeplinkingOptions) params.deeplinkingOptions = deeplinkingOptions
  if (dryRun) params.dryRun = dryRun
  if (template) params.template = template
  if (payload) params.payload = payload
  if (selection) params.selection = selection
  const route = 'customers/notify'
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const notifyCustomerWithTestPayload = async (sc, testPayload, dbg = false) => {
  const fn = 'notifyCustomerWithTestPayload'
  const params = { testPayload, debug: dbg }
  const route = 'customers/notify'
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const invokeFcmAdmin = async (adminPayload, dbg = false) => {
  const fn = 'invokeFcmAdmin'
  adminPayload.debug = dbg
  const params = adminPayload
  const sc = 'VE/CUSTOMERS'
  // const route = '/customer/notify/admin'
  const route = 'customers/firebase-admin/invoke'
  logRouteParams(fn, route, params, dbg)
  // const res = await API.put(route, null, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const sendEmailToSatUser = async (sc, msg, msgTitle, emails, dbg = false) => {
  const fn = 'sendEmailToSatUser'
  if (typeof msg === 'undefined' || msg?.length === 0 || typeof emails === 'undefined') return
  if (!Array.isArray(emails)) emails = [emails]
  const params = {
    message: msg,
    messageTitle: msgTitle,
    method: 'email',
    emailAddresses: emails,
    debug: dbg
  }
  // const route = '/customer/notify'
  const route = 'customers/notify'
  logRouteParams(fn, route, params, dbg)
  // const res = await API.put(route, null, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEndPointsStatusForCustomer = async (sc, endPointId, dbg = false) => {
  const fn = 'getEndPointsStatusForCustomer'
  const params = { sc, endPointId, debug: dbg }
  const route = getRoute('customer', 'assets/endpoints/status', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const triggerInverterDiscovery = async (id, idc, doc = false, dbg = false) => {
  const fn = 'triggerInverterDiscovery'
  const sc = 'VE/ASSETS'
  const inverterDiscoveryConfig = idc
  const doOnlineCheck = doc
  const params = { endPointId: id, inverterDiscoveryConfig, doOnlineCheck }
  const route = getRoute('assets', 'endpoints/inverterdiscovery', 'invoke')
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const triggerDeviceDiscovery = async (params, dbg = false) => {
  const fn = 'triggerDeviceDiscovery'
  const sc = 'VE/ASSETS'
  const { id, endPointType, ddc, doc = false, action } = params
  const deviceDiscoveryConfig = ddc
  const doOnlineCheck = doc
  const discoveryParams = { endPointId: id, endPointType, deviceDiscoveryConfig, doOnlineCheck, action }
  const route = getRoute('assets', 'endpoints/devicediscovery', 'invoke', dbg)
  logRouteParams(fn, route, discoveryParams, dbg)
  const res = await API.put2(route, { sc }, discoveryParams, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDeviceDiscoveryResults = async (id, epType, action, start, dbg = false) => {
  const fn = 'getDeviceDiscoveryResults'
  const sc = 'VE/ASSETS'
  const params = { sc, endPointId: id, endPointType: epType, action, start }
  const route = getRoute('assets', 'endpoints/devicediscovery/results', 'read')
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getInverterDiscoveryResults = async (id, dbg = false) => {
  const fn = 'getInverterDiscoveryResults'
  const sc = 'VE/ASSETS'
  const params = { sc, endPointId: id }
  const route = getRoute(
    'assets',
    'endpoints/inverterdiscovery/results',
    'read'
  )
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getInverterNameplate = async (deviceId, endPointIds, dbg = false) => {
  const fn = 'getInverterNameplate'
  const sc = 'VE/ASSETS' // todo: review
  const params = { sc, deviceId, endPointIds }
  const route = getRoute('assets', 'endpoints/inverter/nameplate', 'read')
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getDeviceStatistics = async (deviceId, dbg = false) => {
  const fn = 'getDeviceStatistics'
  const sc = 'VE/ASSETS' // todo: review
  const params = { sc, deviceId }
  const route = getRoute('assets', 'devices/statistics', 'read')
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getInverterToken = async (endPointId, debug) => {
  const hmaParams = {
    satAction: 'getInverterData',
    endPointId,
    returnData: 'token',
    debug
  }
  addToLog('getInverterToken:hmaParams: ' + JSON.stringify(hmaParams), debug)
  const response = await callHmaApi(hmaParams, debug)
  addToLog('getInverterToken:response: ' + JSON.stringify(response), debug)
  return response
}

const getInverterFiveMinutesData = async (
  endPointId,
  startTS,
  endTS,
  debug
) => {
  // startTS ... 20210901T000000+08:00
  // shortISOToUnixTimestamp('20210901T000000+08:00') ... 1630425600
  const start = shortISOToUnixTimestamp(startTS)
  const end = shortISOToUnixTimestamp(endTS)
  const oneDayInSeconds = 24 * 60 * 60
  let thisStart = start
  addToLog('getInverterFiveMinutesData:start,end: ' + start + ', ' + end, debug)
  if (start > end) return
  const jsonData = []
  const token = await getInverterToken(endPointId, debug)
  while (thisStart < end) {
    const hmaParams = {
      satAction: 'getInverterData',
      endPointId,
      returnData: 'plantDataFiveMinutes',
      start: thisStart * 1000, // epoch time in milliseconds
      end: (thisStart + oneDayInSeconds - 1) * 1000,
      token,
      debug
    }
    addToLog(
      'getInverterFiveMinutesData:hmaParams: ' + JSON.stringify(hmaParams),
      debug
    )
    const response = await callHmaApi(hmaParams, debug)
    addToLog(
      'getInverterFiveMinutesData:response: ' + JSON.stringify(response),
      debug
    )
    if (response.data) jsonData.push(...response.data)
    addToLog(
      'getInverterFiveMinutesData:jsonData.length: ' + jsonData.length,
      debug
    )
    thisStart += oneDayInSeconds
    await sleep(500)
  }
  addToLog(
    'getInverterFiveMinutesData:jsonData.length: ' + jsonData.length,
    debug
  )
  const csvData = await convertJsonToCsv(jsonData)
  const filename =
    [
      endPointId,
      'plantDataFiveMinutes',
      startTS.replace(':', ''),
      'to',
      endTS.replace(':', '')
    ].join('-') + '.csv'
  download(csvData, filename, 'text/csv;encoding:utf-8')
}

const getDeviceDynamicProfile = async (deviceId, dynamicProfileId = 'ALL', dbg = false) => {
  const fn = 'getDeviceDynamicProfile'
  const sc = 'VE/ASSETS'
  const params = { sc, deviceId, dynamicProfileId }
  const route = getRoute('assets', 'endpoints/profile', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  let res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  res = Array.isArray(res) ? res[1] : res
  res = Array.isArray(res) ? res.slice(-1)[0] : res
  return res
}

const publishDeviceDynamicProfile = async (params, dbg) => {
  const fn = 'publishDeviceDynamicProfile'
  const sc = 'VE/ASSETS'
  const route = getRoute('assets', 'endpoints/profile', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  if (params?.profileId && !params?.dynamicProfileId) {
    params.dynamicProfileId = params.profileId
    delete params.profileId
  }
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEndPointConfig = async (deviceId, endPointId, dbg = false) => {
  const fn = 'getEndPointConfig'
  const sc = 'VE/ASSETS'
  const params = { sc, deviceId, endPointId }
  const route = getRoute('assets', 'endpoints/config', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const setEndPointConfig = async (endPointId, supportedDevice, dbg) => {
  const fn = 'setEndPointConfig'
  const sc = 'VE/ASSETS'
  const params = { sc, endPointId, action: 'set', supportedDevice }
  const route = getRoute('assets', 'endpoints/config', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const sendEndPointConfig = async (endPointId, endPointConfigMsg, dbg) => {
  const fn = 'sendEndPointConfig'
  const sc = 'VE/ASSETS'
  const params = { sc, endPointId, action: 'send', endPointConfigMsg }
  const route = getRoute('assets', 'endpoints/config', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const setEndPointContract = async (endPointId, endPointContractMsg, dbg) => {
  const fn = 'setEndPointContract'
  const sc = 'VE/ASSETS'
  const params = { sc, endPointId, action: 'set', endPointContractMsg }
  const route = getRoute('assets', 'endpoints/contract', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const sendEndPointContract = async (endPointId, endPointContractMsg, dbg) => {
  const fn = 'sendEndPointContract'
  const sc = 'VE/ASSETS'
  const params = { sc, endPointId, action: 'send', endPointContractMsg }
  const route = getRoute('assets', 'endpoints/contract', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const clearEndPointConfig = async (deviceId, endPointId, endPointIdToClear, dbg = false) => {
  const fn = 'clearEndPointConfig'
  const sc = 'VE/ASSETS'
  const params = { deviceId, endPointId, endPointIdToClear }
  const route = getRoute('assets', 'endpoints/config', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEndPointContract = async (deviceId, endPointId, contractId, dbg = false) => {
  const fn = 'getEndPointContract'
  const sc = 'VE/ASSETS'
  const params = { sc, deviceId, contractId }
  if (endPointId) params.endPointId = endPointId
  const route = getRoute('assets', 'endpoints/contract', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const clearEndPointContracts = async (deviceId, endPointId, contractIdToClear, dbg = false) => {
  const fn = 'clearEndPointContracts'
  const sc = 'VE/ASSETS'
  const params = { deviceId, endPointId, contractIdToClear }
  const route = getRoute('assets', 'endpoints/contract', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const clearAllEndPointConfigsAndContracts = async (
  deviceIdOrDeviceSerialNumber,
  dbg = false
) => {
  const fn = 'clearAllEndPointConfigsAndContracts'
  const sc = 'VE/ASSETS'
  const params = { deviceIdOrDeviceSerialNumber }
  const route = getRoute('assets', 'endpoints/configcontract', 'delete', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

// https://stackoverflow.com/questions/14964035/how-to-export-javascript-array-info-to-csv-on-client-side
// Example data given in question text
// var sampledata = [['name1', 'city1', 'some other info'], ['name2', 'city2', 'more info']]
// Building the CSV from the Data two-dimensional array
// Each column is separated by ";" and new line "\n" for next row
// var csvContent = ''
// sampledata.forEach(function(infoArray, index) {
//  dataString = infoArray.join(';')
//  csvContent += index < sampledata.length ? dataString + '\n' : dataString
// })
// The download function takes a CSV string, the filename and mimeType as parameters
// Scroll/look down at the bottom of this snippet to see how download is called
const download = (content, fileName, mimeType) => {
  const a = document.createElement('a')
  mimeType = mimeType || 'application/octet-stream'
  if (navigator.msSaveBlob) {
    // IE10
    navigator.msSaveBlob(new Blob([content], { type: mimeType }), fileName)
  } else if (URL && 'download' in a) {
    // html5 A[download]
    a.href = URL.createObjectURL(new Blob([content], { type: mimeType }))
    a.setAttribute('download', fileName)
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  } else {
    location.href =
      'data:application/octet-stream,' + encodeURIComponent(content) // only this mime type is supported
  }
}

const updateCustomer = async (sc, countryCode, contact, preferredName, preferredComms, status, dbg = false) => {
  const fn = 'updateCustomer'
  const params = { countryCode, contact, preferredName, preferredComms, status }
  const res = await updateCustomerParams(sc, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateCustomerParams = async (sc, params, dbg = false) => {
  const fn = 'updateCustomerParams'
  // countryCode, contact, preferredName, preferredComms, status, firstName, lastName, appAccess, status, accessProfile
  // email, securityContext, subscriptionLevel, subscriptionExpiry // requires super admin rights
  if (!hasKeys(params)) return ['params are required']
  const route = getRoute('customers', '', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateCustomerPassword = async (sc, password, dbg = false) => {
  const fn = 'updateCustomerPassword'
  const params = { password }
  const route = getRoute('customers', 'password', 'update', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateCustomerAppAccess = async (scs, appAccess, dbg = false) => {
  const fn = 'updateCustomerAppAccess'
  const ress = []
  for (const sc of scs) {
    const res = await updateCustomerParams(sc, { appAccess }, dbg)
    logRes(fn, res, dbg)
    ress.push(res)
  }
  return ress
}

const updateCustomerStatus = async (scs, status, dbg = false) => {
  const fn = 'updateCustomerStatus'
  const ress = []
  for (const sc of scs) {
    const res = await updateCustomerParams(sc, { status }, dbg)
    logRes(fn, res, dbg)
    ress.push(res)
  }
  return ress
}

const getVeEnvLocs = (dbg = false) => {
  return getVeEnvLocNames(vecfg, dbg)
}

const getDeploymentS3Buckets = async (dbg = false) => {
  const fn = 'getDeploymentS3Buckets'
  const params = {}
  const route = '/veenvlocs/deployments3bucket'
  logRouteParams(fn, route, params, dbg)
  const deploymentS3Buckets = await API.get(route, params)
  logRes(fn, deploymentS3Buckets, dbg)
  return deploymentS3Buckets
}

const getContractOrchestrationEvent = async (
  securityContext,
  orchestrationEventId,
  contractEventId,
  debug = false
) => {
  const params = { securityContext, orchestrationEventId, contractEventId }
  const route = getRoute('contracts', 'orchestration/events', 'read', debug) // 'contracts/orchestration/events/read'
  debug = true
  addToLog(
    `getContractOrchestrationEvent:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  return await API.get2(route, params, debug)
}

const getContractOrchestrationEventProgress = async (
  securityContext,
  orchestrationEventId,
  contractEventId,
  debug = false
) => {
  const params = { securityContext, orchestrationEventId, contractEventId }
  const route = getRoute(
    'contracts',
    'orchestration/events/progress',
    'read',
    debug
  ) // 'contracts/orchestration/events/progress/read'
  addToLog(
    `getContractOrchestrationEventProgress:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(
    `getContractOrchestrationEventProgress:response: ${JSON.stringify(
      response
    )}`,
    debug
  )
  return response
}

const getContractOrchestrationEventChartData = async (
  securityContext,
  tzOffset = '+00:00',
  returnData = 'aligned15sRead',
  debug = false
) => {
  const params = { securityContext, tzOffset, returnData, debug }
  const route = getRoute(
    'contracts',
    'orchestration/events/chart-data',
    'read',
    debug
  ) // 'contracts/orchestration/events/chart-data/read'
  addToLog(
    `getContractOrchestrationEventChartData:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(
    `getContractOrchestrationEventChartData:response: ${JSON.stringify(
      response
    )}`,
    debug
  )
  return response
}

const getContractEnergyForAggregateByIdChartData = async (
  securityContext,
  tzOffset,
  fromDate,
  toDate,
  returnData,
  aggregateByIdDataSource,
  debug = false
) => {
  if (securityContext.split('/')[1] === 'CONTRACTS') {
    securityContext = securityContext.replace(
      /CONTRACTS/,
      'TELEMETRY/CONTRACTS'
    )
  }
  const params = {
    securityContext,
    tzOffset,
    fromDate,
    toDate,
    returnData,
    aggregateByIdDataSource
  }
  const route = getRoute(
    'telemetry',
    'orchestration/events/aggregate-by-id/chart-data',
    'read',
    debug
  )
  addToLog(
    `getContractEnergyForAggregateByIdChartData:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(
    `getContractEnergyForAggregateByIdChartData:response: ${JSON.stringify(
      response
    )}`,
    debug
  )
  return response
}

// todo: rename to getContractOrchestrationEventsChartData ???
const getContractEnergyForAggregateByIdsChartData = async (
  sc,
  tzOffset,
  returnData,
  dbg = false
) => {
  const fn = 'getContractEnergyForAggregateByIdChartData'
  if (sc.split('/')[1] === 'CONTRACTS') {
    sc = sc.replace(/CONTRACTS/, 'TELEMETRY/CONTRACTS')
  }
  const params = { sc, tzOffset, returnData }
  const subClass = 'orchestration/events/aggregate-by-ids/chart-data'
  const route = getRoute('telemetry', subClass, 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getAggregatedEnergyForAggregateByIdsVeAuth = async (
  securityContext = 'VE/TELEMETRY',
  aggregateByIds,
  start,
  deltaInSeconds,
  debug = false
) => {
  if (securityContext.split('/')[1] === 'ASSETS') {
    securityContext = securityContext.replace(/ASSETS/, 'TELEMETRY')
  }
  const params = {
    securityContext,
    satAction: 'getAggregatedEnergyForAggregateByIds',
    aggregateByIds,
    start,
    deltaInSeconds
  }
  const route = getRoute(
    'telemetry',
    'aggregate-by-ids/energy-data',
    'read',
    debug
  ) // 'telemetry/aggregate-by-ids/energy-data/read'
  addToLog(
    `getAggregatedEnergyForAggregateByIdsVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(
    `getAggregatedEnergyForAggregateByIdsVeAuth:response: ${JSON.stringify(
      response
    )}`,
    debug
  )
  return response
}

const getTxnForAggregateByIdsVeAuth = async (
  securityContext = 'VE/TELEMETRY',
  aggregateByIds,
  startDates,
  returnData,
  debug = false
) => {
  if (securityContext.split('/')[1] === 'ASSETS') {
    securityContext = securityContext.replace(/ASSETS/, 'TELEMETRY')
  }
  const params = {
    securityContext,
    satAction: 'getTxnForAggregateByIds',
    aggregateByIds,
    startDates,
    returnData,
    debug
  }
  const route = getRoute(
    'telemetry',
    'aggregate-by-ids/energy-data',
    'read',
    debug
  ) // 'telemetry/aggregate-by-ids/energy-data/read'
  addToLog(
    `getTxnForAggregateByIdsVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(
    `getTxnForAggregateByIdsVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const getDataForAggregateByIdsVeAuth = async (
  securityContext = 'VE/TELEMETRY',
  aggregateByIds,
  starts,
  returnData,
  tzOffset,
  deltaInSeconds,
  aggregateByIdDataSource,
  aggregateAtRuntime,
  debug = false
) => {
  if (securityContext.split('/')[1] === 'ASSETS') {
    securityContext = securityContext.replace(/ASSETS/, 'TELEMETRY')
  }
  const params = {
    securityContext,
    satAction: 'getDataForAggregateByIds',
    aggregateByIds,
    starts,
    returnData,
    tzOffset,
    deltaInSeconds,
    aggregateByIdDataSource,
    aggregateAtRuntime,
    debug
  }
  const route = getRoute(
    'telemetry',
    'aggregate-by-ids/energy-data',
    'read',
    debug
  ) // 'telemetry/aggregate-by-ids/energy-data/read'
  addToLog(
    `getDataForAggregateByIdsVeAuth:route: ${route}, params: ${JSON.stringify(
      params
    )}`,
    debug
  )
  const response = await API.get2(route, params, debug)
  addToLog(
    `getDataForAggregateByIdsVeAuth:response: ${JSON.stringify(response)}`,
    debug
  )
  return response
}

const getEnergyForAggregateByIdChartData = async (sc, tzOffset, fromDate, toDate, returnData, aggregateByIdDataSource, dbg = false) => {
  const fn = 'getEnergyForAggregateByIdChartData'
  if (sc.split('/')[1] === 'ASSETS') sc = sc.replace(/ASSETS/, 'TELEMETRY')
  const params = { sc, tzOffset, fromDate, toDate, returnData, aggregateByIdDataSource }
  const route = getRoute('telemetry', 'aggregate-by-id/chart-data', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEnergyForServiceEntityChartData = async (params, dbg = false) => {
  const fn = 'getEnergyForServiceEntityChartData'
  let { securityContext: sc } = params
  if (sc.split('/')[1] === 'ASSETS') sc = sc.replace(/ASSETS/, 'TELEMETRY')
  params.securityContext = sc
  params.satAction = 'getEnergyForEndPointsChartData'
  const route = getRoute('telemetry', 'service-entity/chart-data', 'read')
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getEnergyForServiceEntity = async (sc, tzOffset, debug = false) => {
  const fn = 'getEnergyForServiceEntity'
  if (sc.split('/')[1] === 'ASSETS') sc = sc.replace(/ASSETS/, 'TELEMETRY')
  const params = { sc, tzOffset, satAction: 'getEnergyForServiceEntity' }
  const route = getRoute('telemetry', 'service-entity/flow-data', 'read', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.get2(route, params, debug)
  logRes(fn, res, debug)
  return res
}

const getPowerForServiceEntity = async (sc, tzOffset, debug = false) => {
  const fn = 'getPowerForServiceEntity'
  if (sc.split('/')[1] === 'ASSETS') sc = sc.replace(/ASSETS/, 'TELEMETRY')
  const params = { sc, tzOffset, satAction: 'getPowerFlowData' } // todo: review if satAction is needed
  const route = getRoute('telemetry', 'service-entity/flow-data', 'read', debug)
  logRouteParams(fn, route, params, debug)
  const res = await API.get2(route, params, debug)
  logRes(fn, res, debug)
  return res
}

const getContractChangeLog = async (securityContext, contractId, dbg = false) => {
  const fn = 'getContractChangeLog'
  if (typeof contractId === 'boolean') {
    dbg = contractId
    contractId = null
  }
  if (!contractId && securityContext && securityContext.includes('/')) {
    contractId = securityContext.split('/').pop()
  }
  const params = { securityContext }
  if (contractId) params.contractId = contractId
  const route = getRoute('contracts', 'changelog', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getAllServiceEntitiesAccessList = async (sc, limit, offset, status, search, dbg = false) => {
  const fn = 'getAllServiceEntitiesAccessList'
  doLog(fn, { sc, limit, offset, status, search }, dbg)
  if (!sc) return
  const params = { sc, limit, offset, status, search }
  const route = getRoute('assets', 'service-entities/access', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const addServiceEntityOwnerAccess = async (sc, serviceEntityId, nickname, dbg = false) => {
  const fn = 'addServiceEntityOwnerAccess'
  if (!sc) return
  const params = { serviceEntityId, nickname }
  const route = `customer/assets/${routes.assets['service-entities/owner-access'].v2}/add`
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateServiceEntityOwnerAccess = async (sc, serviceEntityOwnerRecordId, validTill, dbg = false) => {
  const fn = 'updateServiceEntityOwnerAccess'
  if (!sc) return
  if (!validTill) validTill = getLongISOTimeStamp()
  const params = { valid_till: validTill }
  const route = `assets/${routes.assets['service-entities/owner-access'].v2}/update`
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, { sc, serviceEntityOwnerRecordId }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteServiceEntityOwnerAccess = async (sc, serviceEntityOwnerRecordId, dbg = false) => {
  const fn = 'deleteServiceEntityOwnerAccess'
  if (!sc) return
  const params = { serviceEntityOwnerRecordId }
  const route = `assets/${routes.assets['service-entities/owner-access'].v2}/delete`
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getServiceEntityAccess = async (sc, status, dbg = false) => {
  const fn = 'getServiceEntityAccess'
  if (!sc) return
  const params = { sc, status }
  const route = `assets/${routes.assets['service-entities/access'].v2}/read`
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getServiceEntityAccessForCustomer = async (sc, status, dbg = false) => {
  const fn = 'getServiceEntityAccessForCustomer'
  if (!sc) return
  const params = { sc, status }
  const route = `customer/assets/${routes.assets['service-entities/access'].v2}/read`
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getServiceEntityDelegatedAccessForCustomer = async (sc, status, dbg = false) => {
  const fn = 'getServiceEntityDelegatedAccessForCustomer'
  if (!sc) return
  const params = { sc, status }
  const route = `customer/assets/${routes.assets['service-entities/delegated-access'].v2}/read`
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const addServiceEntityDelegatedAccessForCustomer = async (sc, serviceEntityId, nickname, dbg = false) => {
  const fn = 'addServiceEntityDelegatedAccessForCustomer'
  if (!sc) return
  const params = { serviceEntityId, nickname }
  const route = `customer/assets/${routes.assets['service-entities/delegated-access'].v2}/add`
  logRouteParams(fn, route, params, dbg)
  const res = await API.post2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateServiceEntityDelegatedAccess = async (sc, status, serviceEntityOwnerRecordId, serviceEntityDelegationRecordId, dbg = false) => {
  const fn = 'updateServiceEntityDelegatedAccess'
  const params = { status }
  const route = `assets/${routes.assets['service-entities/delegated-access'].v2}/update`
  logRouteParams(fn, route, params, dbg)
  const qsParams = { sc, status, serviceEntityOwnerRecordId, serviceEntityDelegationRecordId }
  const res = await API.put2(route, qsParams, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const updateServiceEntityDelegatedAccessParams = async (sedaParams, dbg = false) => {
  const fn = 'updateServiceEntityDelegatedAccessParams'
  const { securityContext, serviceEntityOwnerRecordId, serviceEntityDelegationRecordId, ...params } = sedaParams
  // const params = { status }
  // const qsParams = { sc, status, serviceEntityOwnerRecordId, serviceEntityDelegationRecordId }
  const qsParams = sedaParams
  const route = `assets/${routes.assets['service-entities/delegated-access'].v2}/update`
  logRouteParams(fn, route, params, dbg)
  const res = await API.put2(route, qsParams, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const deleteServiceEntityDelegatedAccess = async (sc, serviceEntityDelegationRecordId, dbg = false) => {
  const fn = 'deleteServiceEntityDelegatedAccess'
  if (!sc) return
  const params = { serviceEntityDelegationRecordId }
  const route = `assets/${routes.assets['service-entities/delegated-access'].v2}/delete`
  logRouteParams(fn, route, params, dbg)
  const res = await API.delete2(route, { sc }, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getContractTransactionsMetrics = async (sc, status = null, fromDate, toDate, dbg = false) => {
  const fn = 'getContractTransactionsMetrics'
  const params = { sc, fromDate, toDate }
  if (status) params.status = status
  const route = getRoute('contracts', 'transactions/metrics', 'read', dbg)
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getContractsMetrics = async (sc, fromDate, toDate, status, dbg) => {
  const fn = 'getContractsMetrics'
  const params = { sc, fromDate, toDate, status }
  // const route = getRoute('contracts', 'contracts/metrics', 'read', debug)
  const route = 'contracts/contracts/metrics/read'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getContractEventsMetrics = async (sc, fromDate, toDate, dbg) => {
  const fn = 'getContractEventMetrics'
  const params = { sc, fromDate, toDate }
  // const route = getRoute('contracts', 'events/metrics', 'read', debug)
  const route = 'contracts/events/metrics/read'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getContractsGrowth = async (sc = '', timespan, dbg = false) => {
  const fn = 'getContractsGrowth'
  const params = { sc, timespan }
  // const route = getRoute('contracts', 'contracts/growth', 'read', debug)
  const route = 'contracts/contracts/growth/read'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

const getGoogleMapsApiKey = async (dbg = false) => {
  const fn = 'getGoogleMapsApiKey'
  // This function should call your backend endpoint that securely holds the API key.
  // The backend should return the key in a JSON response.
  const satAction = 'getGoogleMapsApiKey'
  const params = { satAction }
  const route = getRoute('config', 'google-maps-api-key', '', dbg)
  logRouteParams(fn, route, params, dbg)
  doLog(fn, 'Calling backend to fetch Google Maps API key...', true)
  try {
    // IMPORTANT: You need to implement the backend logic for the 'getGoogleMapsApiKey' action.
    // todo: implement
    const res = await API.get3(route, params, dbg)
    logRes(fn, res, dbg)
    // const res = { apiKey: 'AIzaSyDUpa-S_dPonAY0PUmKXC7KF2Im3L5nlgU' }
    if (res && typeof res.apiKey === 'string' && res.apiKey.length > 0) {
      doLog(fn, 'Successfully fetched Google Maps API key.', true)
      return res.apiKey
    } else {
      doLog(fn, { info: 'API response for Google Maps key was invalid or empty.' }, true)
      return '' // Fallback to an empty string, which will cause the map to fail visibly.
    }
  } catch (e) {
    doLog(fn, { info: 'failed to get Google Maps api key', error: e }, true)
    return '' // Return empty string on error
  }
}

// =============== legacy ('old' vedev & veprod env) =====================

const getExcludedContracts = async (sc, l, o, r, id, s, isE = true, dbg) => {
  const fn = 'getExcludedContracts'
  const params = { sc, limit: l, offset: o, isExcluded: isE }
  if (r) params.reason = r
  if (id) params.contractId = id
  if (s) params.search = s
  const route = getRoute('contracts', 'events/executions', 'read', dbg)
  // const route = 'contracts/events/executions/read'
  logRouteParams(fn, route, params, dbg)
  const res = await API.get2(route, params, dbg)
  logRes(fn, res, dbg)
  return res
}

export {
  addContractDefinition,
  updateContractDefinitionVeAuth,
  updateContractDefinitionParams,
  getAllContractDefinitions,
  getCustomerContractDef,
  getSpeedoMeterData,
  getDynamicConfigDataVersion,
  getDynamicConfigImagePath,
  getDynamicConfigImageUrl,
  getDynamicConfigImage,
  getContractDefsForOrchestrationVeAuth,
  getContractDefsWithGroupsForOrchestrationVeAuth,
  getContractDefinitionVersions,
  updateContractServiceEntityId,
  updateContractEndPointIdVeAuth,
  updateContractStatus,
  updateContractEndPointAggregateBy,
  deleteContract,
  deleteContractTransaction,
  updateContractTransactionStatus,
  deleteContractEvent,
  deleteContractEvents,
  deleteContractExecutions,
  getAllContracts,
  getContractsForStatus,
  getContractsForStatusDate,
  getContractOffersForCustomer,
  getDefaultContractOffersForCustomer,
  getContractTransactionsForContractEventIdVeAuth,
  addContractAlgorithmVeAuth,
  updateContractAlgorithmVeAuth,
  deleteContractAlgorithm,
  getAllContractAlgorithms,
  getSupportedDevicesForEndPointType,
  addSupportedDevice,
  updateSupportedDevice,
  deleteSupportedDevice,
  addContractGroupVeAuth,
  updateContractGroupVeAuth,
  addServiceEntityVeAuth,
  addServiceEntitiesVeAuth,
  updateServiceEntity,
  updateServiceEntityCoordinates,
  deleteServiceEntity,
  getAllServiceEntities,
  getAllServiceEntitiesForCustomer,
  getServiceEntity,
  getServiceEntitiesForCustomerId,
  getCustomerForServiceEntity,
  getCustomerIdForServiceEntity,
  getDataConfigPropValue,
  setDataConfigPropValue,
  getDataConfigForKey,
  setGetObjectKeyValue,
  getDataConfigOwners,
  getDataConfigs,
  getDataConfig,
  updateDataConfigVeAuth,
  updateDataConfigData,
  getSetupConfigOwners,
  getSetupConfigs,
  getSetupConfig,
  getSetupWizardUiConfig,
  getDynamicSetupConfigVeAuth,
  updateSetupConfigVeAuth,
  updateSetupConfigDataVeAuth,
  getImageConfigVeAuth,
  getCustomers,
  getCustomersForSecurityContext,
  getCustomer,
  getCustomerAppInfo,
  deleteCustomerAppInfo,
  getCustomerToken,
  getCustomerTokenForTokenId,
  deleteCustomerToken,
  addCustomerToken,
  getCustomerNameIds,
  getCustomerLedgerAccounts,
  getCustomerChangeLog,
  addCustomer,
  addCustomers,
  requestOnboardingPin,
  onboardCustomerDirect,
  getOnboardingStatus,
  deleteCustomer,
  deleteTestCustomers,
  deleteCPCCustomers,
  invokeContractExecuteAlgorithmVeAuth,
  invokeContractExecuteGlobalAlgorithmsVeAuth,
  invokeContractExecuteContractAlgorithmsVeAuth,
  invokeContractExecuteContractActionVeAuth,
  invokeContractExecuteContractActions,
  invokeContractCallIeeeAggClient,
  scheduleContractDef,
  listScheduledContractDefs,
  deleteScheduledContractDef,
  deleteHistoricContractDefSchedules,
  getNextStartForContractDefs,
  getContractDefCountByPricingProfile,
  getContractDefInfoByPricingProfileVeAuth,
  calculateCapacityPriceVeAuth,
  createContractsFromContractDef,
  getDeviceIdForDeviceSerialNumber,
  getDevicesForDeviceSerialNumber,
  getMeterIdsForDeviceId,
  getMetersForDeviceId,
  getEndPointsForDeviceSerialNumber,
  verifyDeviceSerialNumber,
  getMeter,
  getMeterData,
  getEndPointData,
  getEndPointDataVeAuth,
  getEndPointsForCustomer,
  getDevicesForCustomer,
  getServiceEntitiesForCustomer,
  getSupportRequestsForCustomer,
  getOrdersForCustomer,
  addSupportRequest,
  getMeters,
  // getEndPointsForSecurityContext,
  getEndPointsForSecurityContextWithParams,
  getOcppConnectionsForSecurityContext,
  getOcppNotificationsForSecurityContext,
  getOcppRequestsForSecurityContext,
  getOcppChargerStatus,
  invokeOcppAction,
  getEndPointsStatus,
  getControlEndPointLogs,
  getControlEndPointLogsForCustomer,
  getEndPointsOnline,
  getDevicesForSecurityContext,
  getEndPointsForContext,
  getDeviceIdsForMicrogridId,
  getDeviceData,
  updateEndPoint,
  controlEndPoint,
  createEndPoint,
  updateDevice,
  createEndPoints,
  updateEndPointAggregateByVeAuth,
  updateContractAggregateByVeAuth,
  deleteEndPoint,
  deleteDevice,
  removeServiceEntityIdFromEndPoint,
  diagnoseDevice,
  diagnoseDevices,
  setDeviceLog,
  getDeviceLogFromS3,
  getDeviceLogList,
  getDeviceStatusMsg,
  getDeviceStatusList,
  getDeviceStatusListWithFilter,
  getRecentDeviceStatusList,
  getRecentDeviceStatusListByAge,
  getRecentDeviceStatusListByAgeWithFilter,
  isDeviceOnline,
  pingDevice,
  setWifiDvi,
  setWifi,
  clearWifi,
  rebootDevice,
  rebootAndOtaDevice,
  otaDevice,
  factoryResetDevice,
  updateDeviceSoftware,
  setDeviceSourceMsg,
  setDeviceSerialNumberVeAuth,
  getDeviceProvisionInfo,
  getProvisionInfoForLocalEnvLoc,
  getProvisionInfoForProvEnvLoc,
  getProvisionInfoForEnvLoc,
  getDeviceSoftAPWifiCredentialsForSerialNumber,
  saveWifiCredentials,
  provisionDevice,
  moveDevice,
  deleteOldCertificates,
  getDeviceStatus,
  getNanoId,
  getSatVersionInfo,
  getVeXApiInfo,
  updateCurrentUserUserProfileSettings,
  updateCurrentUserProviderName,
  addUser,
  resetUserPassword,
  addUserApiKey,
  isMemberOfSatGroup,
  getGroupsForSecurityContext,
  getGroupForSecurityContextVeAuth,
  getCurrentUserGroupsForType,
  hasAssetManagementAccess,
  getRolesForOwner,
  getRolesFunctionsMappingsForOwner,
  updateCurrentUserUserProfile,
  updateUserVeAuth,
  updateGroupVeAuth,
  deleteGroup,
  deleteUser,
  deleteUserApiKey,
  getCurrentUserUserProfile,
  getCurrentUserAccessProfile,
  isCurrentUserSuperAdmin,
  isCurrentUserUserAdmin,
  getUsersForGroupVeAuth,
  getUserForSecurityContext,
  getUsersForSecurityContext,
  getUserDataVeAuth,
  getUserChangeLog,
  getNotificationsForSatUser,
  getNotificationsForCustomer,
  deleteNotificationForCustomer,
  getNotificationsForSecurityContext,
  useGMC,
  getDataSource,
  callLedgerApi,
  callIotApi,
  callIotAdminApi,
  testHmaApi,
  callHmaApi,
  callFbaApi,
  addFbaOwnerContextVeAuth,
  getSourceMsg,
  getEndPointSourceMsg,
  getEndPointsSourceMsg,
  exportEndPointSourceMsg,
  exportEndPointsSourceMsg,
  getAggregatedEnergyForAggregateByIdsVeAuth,
  getEnergyForAggregateByIdChartData,
  getEnergyForServiceEntityChartData,
  getEnergyForServiceEntity,
  getPowerForServiceEntity,
  getTxnForAggregateByIdsVeAuth,
  getDataForAggregateByIdsVeAuth,
  getAllSrcMsgAggregatedEnergyHashKeys,
  getAllEnergyMsgAggregatedEnergyHashKeys,
  getAllTxnContractAggregatedEnergyHashKeys,
  getAllAggregatedEnergyAggregateByIds,
  getAllAggregateByIds,
  getSrcMsgExpiryHashValue,
  getAllDynamicProfileIds,
  getDynamicProfileHashValue,
  getDynamicProfileHashFieldValue,
  getDeviceTxnHashValue,
  getEventWindowHashValue,
  getEventWindow,
  deleteEventWindow,
  updateServiceEntityInVeCache,
  deleteServiceEntityInVeCache,
  updateIacConfigInVeCache,
  deleteIacConfigInVeCache,
  updateSecretInVeCache,
  deleteSecretInVeCache,
  getVecacheInfo,
  getPermanentKeyNames,
  getSrcMsgEndPointIdsSetValue,
  getDynamicProfileIdsSetValue,
  getEndPointIdsForAggregateById,
  addUpdateDynamicProfile,
  deleteDynamicProfile,
  deleteAggregateById,
  setSrcMsgExpiry,
  completeContractEvent,
  addUpdateEventWindow,
  updateAggregateByIds,
  doAggregatedEnergyHashMaintenance,
  doSrcMsgEnergyHashMaintenance,
  doContractEventInProgressHashMaintenance,
  doContractEventCompletedHashMaintenance,
  doMostRecentContractTransactionsHashMaintenance,
  addPriceProfile,
  deletePriceProfileVeAuth,
  getPriceProfile,
  getPrice,
  getActivePriceProfileVeAuth,
  getAllActivePricingProfilesVeAuth,
  getPriceProfileInfo,
  getSrcMsgAggregatedEnergyHashValue,
  getEnergyMsgAggregatedEnergyHashValue,
  getTxnContractAggregatedEnergyHashValue,
  getSrcMsgEnergyHashValue,
  getContractEventHashValue,
  getContractEventsInProgressHashValue,
  getContractEventsCompletedHashValue,
  getMostRecentContractTransactionsHashValue,
  getContractTransactionHashValue,
  getContractTransactionsFromVeCache,
  getMostRecentServiceEntitiesHashValue,
  getMostRecentServiceEntity,
  getIacConfigsHashValue,
  getIacConfig,
  getSecretsHashValue,
  getSecret,
  publishPrice,
  getContextDataVeAuth,
  getEndPointEnergy,
  getEndPointsEnergyChartData,
  exportEndPointEnergy,
  getAggregatedEnergyVeAuth,
  getMeterDailyTotalEnergyVeAuth,
  updateMonthlyTxnForEndPointIds,
  regenTxnFromS3,
  getDeviceRawTxn,
  getEndPointRawTxn,
  getSignedUrl,
  getCsvFromS3VeAuth,
  getTxtFromS3,
  putTxtToS3VeAuth,
  putBinToS3VeAuth,
  getAllEventDefinitions,
  updateEventDefinitionParams,
  getEventDefinition,
  getEvents,
  getEventMetrics,
  getAssetsMetricsList,
  getMetricsListWithFilter,
  getRecentMetricsListByAgeWithFilter,
  getAllEventActions,
  addEventDefinition,
  downloadEventMetrics,
  submitMetric,
  submitDpChange,
  getDynamicPrice,
  getPostcodeCoords,
  sendTxtMessage,
  notifyCustomer,
  notifyCustomerWithTestPayload,
  invokeFcmAdmin,
  sendEmailToSatUser,
  getEndPointsStatusForCustomer,
  getEndPointsOnlineForCustomer,
  getDevicesOnline,
  getDevicesOnlineForCustomer,
  getDevicesVersionToOta,
  getDevicesVersionToOtaForCustomer,
  triggerInverterDiscovery,
  triggerDeviceDiscovery,
  getInverterDiscoveryResults,
  getDeviceDiscoveryResults,
  getInverterNameplate,
  getDeviceStatistics,
  getInverterFiveMinutesData,
  getDeviceDynamicProfile,
  publishDeviceDynamicProfile,
  getEndPointConfig,
  setEndPointConfig,
  sendEndPointConfig,
  clearEndPointConfig,
  getEndPointContract,
  setEndPointContract,
  sendEndPointContract,
  clearEndPointContracts,
  clearAllEndPointConfigsAndContracts,
  updateCustomer,
  updateCustomerParams,
  updateCustomerPassword,
  updateCustomerAppAccess,
  updateCustomerStatus,
  updateEventStatus,
  getVeEnvLocs,
  getDeploymentS3Buckets,
  getAllContractTransactions,
  getContractsForIdStatus,
  getRecentContractTransactionsForContractIdVeAuth,
  getContractTransactionsChartData,
  getContractTransactionsChartDataWithParams,
  getContractTransactionsForCustomerIdVeAuth,
  addContractOfferForCustomer,
  updateContract,
  invokeContractVeAuth,
  deleteContractDefinition,
  getAllDropdownSecurityContexts,
  getSecurityContexts,
  getAllContractGroupsVeAuth,
  getContractEvents,
  getRecentlyCompletedContractEvents,
  getContractOrchestrationEvent,
  getContractOrchestrationEventProgress,
  getContractOrchestrationEventChartData,
  getContractEnergyForAggregateByIdChartData,
  getContractEnergyForAggregateByIdsChartData,
  getContractChangeLog,
  getAllServiceEntitiesAccessList,
  addServiceEntityOwnerAccess,
  updateServiceEntityOwnerAccess,
  deleteServiceEntityOwnerAccess,
  getServiceEntityAccess,
  getServiceEntityAccessForCustomer,
  getServiceEntityDelegatedAccessForCustomer,
  addServiceEntityDelegatedAccessForCustomer,
  updateServiceEntityDelegatedAccess,
  updateServiceEntityDelegatedAccessParams,
  deleteServiceEntityDelegatedAccess,
  getRoute,
  getContractTransactionsMetrics,
  getContractsMetrics,
  getContractEventsMetrics,
  getContractsGrowth,
  getGoogleMapsApiKey,
  getExcludedContracts,
  updateEventEndPointAggregateBy,
  deleteEventDefinition,
  updateEventActionStatus,
  deleteEvent,
  deleteEventAction
}
